import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Checkbox,
  FormControlLabel,
  Radio,
  RadioGroup,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  Tabs,
  Tab,
  Divider
} from '@mui/material';
import {
  Add,
  Delete,
  PersonAdd,
  Warning,
  Info,
  CheckCircle,
  Sms,
  Visibility,
  Email,
  Phone,
  LocationOn,
  Notifications,
  Refresh,
  UploadFile,
  AttachMoney
} from '@mui/icons-material';
import { apiService } from '../services/api';
import { formatDateForDisplay, getTodayInUserTimezone } from '../utils/dateUtils';
import { getNextServiceDate as getNextServiceDateUtil, getServiceFrequency as getServiceFrequencyUtil, selectBestServiceRequestForCategory } from '../utils/serviceDateUtils';
import { getAccountStatus as getAccountStatusUtil, hasPaymentSetup as hasPaymentSetupUtil } from '../utils/customerStatusUtils';
import { ExistingCustomer } from '../types';
import PaymentVerificationStatus from './PaymentVerificationStatus';
import AddressAutocomplete, { AddressComponents } from './AddressAutocomplete';
import ProviderPriceBreakdown from './ProviderPriceBreakdown';
import InfoTip from './InfoTip';
import SetCustomerPricesDialog from './SetCustomerPricesDialog';
import { DEFAULT_PLATFORM_FEES } from '../utils/feeCalculation';
import { useAuth } from '../contexts/AuthContext';
import { salesTaxOptionsForJob } from '../utils/salesTax';

interface CustomerManagementProps {
  providerId: string;
  refreshTrigger?: number; // When this changes, refresh the customer list
}

// Helper function to format date as YYYY-MM-DD from ISO string, avoiding timezone issues

// Helper function to format date as MM/DD/YYYY
// Formats dates in user's timezone (browser timezone) for display
// Uses the date utility function that automatically detects user's timezone
const formatDateMMDDYYYY = (dateInput: string | Date): string => {
  return formatDateForDisplay(dateInput);
};

// Helper function to get day of week/month pattern for schedule display
// Returns: "Monday", "Tuesday", etc. for weekly/bi-weekly
//          "1st Monday", "2nd Tuesday", etc. for monthly
//          "Per Instance" for per-instance
const getScheduleDisplay = (
  frequency: string,
  dayOfWeek: number | undefined,
  referenceDate: Date | string | undefined
): string => {
  if (frequency === 'per-instance' || frequency === 'seasonal-contract') {
    return frequency === 'per-instance' ? 'Per Instance' : 'Seasonal Contract';
  }
  
  // CRITICAL: Check for monthly services FIRST, before checking dayOfWeek
  // Monthly services use dayOfMonth (from referenceDate), not dayOfWeek
  if (frequency === 'monthly' && referenceDate) {
    // For monthly services, show the day of the month (e.g., "2nd of each month")
    const refDate = typeof referenceDate === 'string' ? new Date(referenceDate) : referenceDate;
    
    // Get UTC date components to avoid timezone issues
    const dayOfMonth = refDate.getUTCDate();
    
    // Get ordinal suffix (1st, 2nd, 3rd, 4th, etc.)
    const getOrdinal = (n: number): string => {
      const s = ['th', 'st', 'nd', 'rd'];
      const v = n % 100;
      return n + (s[(v - 20) % 10] || s[v] || s[0]);
    };
    
    return `${getOrdinal(dayOfMonth)} of each month`;
  }
  
  // For weekly/bi-weekly services, dayOfWeek is required
  if (frequency === 'weekly' || frequency === 'bi-weekly') {
    if (dayOfWeek === undefined || dayOfWeek === null) {
      // If dayOfWeek is missing, return the frequency label
      return frequency.charAt(0).toUpperCase() + frequency.slice(1).replace('-', '-');
    }
    
    const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    const dayName = dayNames[dayOfWeek] || 'Unknown';
    
    // For bi-weekly services, prefix with "Every Other"
    if (frequency === 'bi-weekly') {
      return `Every Other ${dayName}`;
    }
    
    // For weekly services, just return the day name
    return dayName;
  }
  
  // Fallback for any other frequency
  if (dayOfWeek === undefined || dayOfWeek === null) {
    return frequency.charAt(0).toUpperCase() + frequency.slice(1).replace('-', '-');
  }
  
  const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  return dayNames[dayOfWeek] || 'Unknown';
};

const CustomerManagement: React.FC<CustomerManagementProps> = ({ providerId, refreshTrigger = 0 }) => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [addingToSchedule, setAddingToSchedule] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [pricingCustomer, setPricingCustomer] = useState<ExistingCustomer | null>(null);
  const [activeTab, setActiveTab] = useState(0);
  
  // Customer details dialog
  const [customerDetailsDialog, setCustomerDetailsDialog] = useState({
    open: false,
    customer: null as ExistingCustomer | null
  });
  const [customerServiceRequests, setCustomerServiceRequests] = useState<any[]>([]);
  const [loadingServiceRequests, setLoadingServiceRequests] = useState(false);
  
  // Editing next service date
  const [editingNextServiceDate, setEditingNextServiceDate] = useState<string | null>(null);
  const [nextServiceDateValue, setNextServiceDateValue] = useState<string>('');
  const [changeTypeDialog, setChangeTypeDialog] = useState<{ open: boolean; customerId: string | null }>({ open: false, customerId: null });
  
  // Mark season ended loading state
  const [markingSeasonEnded, setMarkingSeasonEnded] = useState<string | null>(null); // serviceRequestId
  const [seasonEndConfirmDialog, setSeasonEndConfirmDialog] = useState<{ open: boolean; serviceRequestId: string | null; serviceCategory: string | null }>({ 
    open: false, 
    serviceRequestId: null,
    serviceCategory: null 
  });
  
  // Filter for customer frequency
  const [filterFrequency, setFilterFrequency] = useState<'all' | 'weekly' | 'bi-weekly' | 'monthly' | 'per-instance' | 'seasonal-contract'>('all');
  // Filter for service type
  const [filterServiceType, setFilterServiceType] = useState<'all' | 'mowing' | 'fertilizing' | 'snow_removal'>('all');
  
  // Customer data
  const [customers, setCustomers] = useState<ExistingCustomer[]>([]);
  
  // Service requests for all customers (for All Customers table)
  const [allCustomersServiceRequests, setAllCustomersServiceRequests] = useState<Record<string, any[]>>({});
  const [loadingAllServiceRequests, setLoadingAllServiceRequests] = useState(false);
  
  // Add customer dialog
  const [addCustomerDialogOpen, setAddCustomerDialogOpen] = useState(false);
  const [csvImportDialogOpen, setCsvImportDialogOpen] = useState(false);
  const [csvPreviewRows, setCsvPreviewRows] = useState<Array<Record<string, string>>>([]);
  const [csvImportLoading, setCsvImportLoading] = useState(false);
  const [csvImportError, setCsvImportError] = useState<string | null>(null);
  const [newCustomer, setNewCustomer] = useState<Partial<ExistingCustomer>>({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    address: {
      street: '',
      city: '',
      state: '',
      zipCode: '',
      coordinates: { lat: 0, lng: 0 }
    },
    serviceSchedule: {
      frequency: 'weekly',
      dayOfWeek: 1,
      preferredTime: '',
      notes: ''
    },
    services: {
      lawnMowingTrimming: false,
      weedControlFertilizing: false,
      snowRemoval: false
    },
    price: 0
  });
  
  // Service pricing for each service type
  const [servicePricing, setServicePricing] = useState<{
    lawnMowingTrimming: { 
      frequency: string; 
      price: string | number; 
      serviceFrequency: 'weekly' | 'bi-weekly' | 'monthly' | 'per-instance';
      contractStartDate: string;
      contractEndDate: string;
      paymentSchedule: 'single-payment' | 'monthly';
    };
    weedControlFertilizing: { 
      frequency: string; 
      price: string | number; 
      serviceFrequency: 'weekly' | 'bi-weekly' | 'monthly' | 'per-instance';
      contractStartDate: string;
      contractEndDate: string;
      paymentSchedule: 'single-payment' | 'monthly';
    };
    snowRemoval: { 
      frequency: string; 
      price: string | number; 
      serviceFrequency: 'weekly' | 'bi-weekly' | 'monthly' | 'per-instance';
    };
  }>({
    lawnMowingTrimming: { 
      frequency: 'weekly', 
      price: '', 
      serviceFrequency: 'weekly' as 'weekly' | 'bi-weekly' | 'monthly' | 'per-instance',
      contractStartDate: '' as string,
      contractEndDate: '' as string,
      paymentSchedule: 'single-payment' as 'single-payment' | 'monthly'
    },
    weedControlFertilizing: { 
      frequency: 'monthly', 
      price: '', 
      serviceFrequency: 'monthly' as 'weekly' | 'bi-weekly' | 'monthly' | 'per-instance',
      contractStartDate: '' as string,
      contractEndDate: '' as string,
      paymentSchedule: 'single-payment' as 'single-payment' | 'monthly'
    },
    snowRemoval: { 
      frequency: 'per-instance', 
      price: '', 
      serviceFrequency: 'per-instance' as 'weekly' | 'bi-weekly' | 'monthly' | 'per-instance'
    }
  });

  useEffect(() => {
    fetchCustomers();

    // Refresh data when the page regains focus (e.g., user switches back from another tab or section)
    const handleFocus = () => {
      fetchCustomers();
    };

    // Listen for schedule update events from Dashboard
    const handleDashboardScheduleUpdate = () => {
      console.log('[CUSTOMER-MGMT] Dashboard schedule update detected, refreshing customers');
      fetchCustomers();
    };

    // Refresh data periodically (every 30 seconds) to catch real-time changes
    const refreshInterval = setInterval(() => {
      fetchCustomers();
    }, 30000);

    window.addEventListener('focus', handleFocus);
    window.addEventListener('refresh-customer-management', handleDashboardScheduleUpdate);
    
    return () => {
      window.removeEventListener('focus', handleFocus);
      window.removeEventListener('refresh-customer-management', handleDashboardScheduleUpdate);
      clearInterval(refreshInterval);
    };
  }, [providerId]);

  // Refresh when refreshTrigger changes (e.g., after job completion)
  useEffect(() => {
    if (refreshTrigger > 0) {
      fetchCustomers();
      // Service requests will be refreshed automatically when customers array updates
      // via the useEffect that depends on [activeTab, customers]
    }
  }, [refreshTrigger]);

  // Update dialog's customer data when customers array changes (e.g., after refresh)
  useEffect(() => {
    if (customerDetailsDialog.open && customerDetailsDialog.customer?.id) {
      const targetCustomerId = customerDetailsDialog.customer.id;
      const freshCustomer = customers.find(c => c.id === targetCustomerId);
      if (freshCustomer && freshCustomer.id === targetCustomerId) {
        console.log('[VIEW-DETAILS] Updating dialog customer data:', {
          targetCustomerId,
          foundCustomerId: freshCustomer.id,
          customerName: `${freshCustomer.firstName} ${freshCustomer.lastName}`,
          oldNextServiceDate: customerDetailsDialog.customer.nextServiceDate,
          newNextServiceDate: freshCustomer.nextServiceDate
        });
        setCustomerDetailsDialog(prev => {
          // Only update if the customer ID still matches (prevent race conditions)
          if (prev.customer?.id === targetCustomerId) {
            return { ...prev, customer: freshCustomer };
          }
          return prev;
        });
      }
    }
  }, [customers, customerDetailsDialog.open, customerDetailsDialog.customer?.id]);

  // Fetch ServiceRequests for customer when details dialog opens
  useEffect(() => {
    const fetchCustomerServiceRequests = async () => {
      if (customerDetailsDialog.open && customerDetailsDialog.customer?.linkedCustomerId) {
        setLoadingServiceRequests(true);
        try {
          const response = await apiService.get('/service-requests', {
            userType: 'provider',
            status: 'accepted,scheduled,completed,season_ended',
            limit: 1000, // Get all ServiceRequests for Customer Details dialog (backend skips pagination when limit >= 1000)
            page: 1
          });
          
          if (response.success && response.data) {
            const allRequests = (response.data as any).serviceRequests || [];
            const customer = customerDetailsDialog.customer!;
            const customerAddress = customer.address;
            
            // Filter to only this customer's requests for THIS SPECIFIC PROPERTY ADDRESS
            const requests = allRequests.filter((sr: any) => {
              // First match by customer ID
              const srCustomerId = sr.customerId?._id || sr.customerId?.id || sr.customerId;
              const matchesCustomer = srCustomerId?.toString() === customer.linkedCustomerId?.toString();
              if (!matchesCustomer) return false;
              
              // Then match by address to ensure we only show services for THIS property
              const srAddress = sr.address;
              if (srAddress && customerAddress) {
                const sameStreet = (srAddress.street || '').trim().toLowerCase() === (customerAddress.street || '').trim().toLowerCase();
                const sameZip = (srAddress.zipCode || '').trim() === (customerAddress.zipCode || '').trim();
                return sameStreet && sameZip;
              }
              
              // If no address match, don't include it (safety check)
              return false;
            });
            setCustomerServiceRequests(requests);
          }
        } catch (err: any) {
          console.error('Failed to fetch service requests for customer:', err);
          setCustomerServiceRequests([]);
        } finally {
          setLoadingServiceRequests(false);
        }
      } else {
        setCustomerServiceRequests([]);
      }
    };

    fetchCustomerServiceRequests();
  }, [customerDetailsDialog.open, customerDetailsDialog.customer?.linkedCustomerId]);

  // Listen for schedule update events to refresh service requests
  useEffect(() => {
    const handleScheduleUpdate = async () => {
      if (customerDetailsDialog.open && customerDetailsDialog.customer?.linkedCustomerId) {
        console.log('[CUSTOMER-DETAILS] Schedule update event received, refreshing service requests...');
        // Refresh service requests to get updated Bid data
        try {
          const response = await apiService.get('/service-requests', {
            userType: 'provider',
            status: 'accepted,scheduled,completed,season_ended',
            limit: 1000, // Get all ServiceRequests for schedule refresh (backend skips pagination when limit >= 1000)
            page: 1
          });
          
          if (response.success && response.data) {
            const allRequests = (response.data as any).serviceRequests || [];
            const customer = customerDetailsDialog.customer!;
            const customerAddress = customer.address;
            
            // Filter to only this customer's requests for THIS SPECIFIC PROPERTY ADDRESS
            const requests = allRequests.filter((sr: any) => {
              const srCustomerId = sr.customerId?._id || sr.customerId?.id || sr.customerId;
              const matchesCustomer = srCustomerId?.toString() === customer.linkedCustomerId?.toString();
              if (!matchesCustomer) return false;
              
              const srAddress = sr.address;
              if (srAddress && customerAddress) {
                const sameStreet = (srAddress.street || '').trim().toLowerCase() === (customerAddress.street || '').trim().toLowerCase();
                const sameZip = (srAddress.zipCode || '').trim() === (customerAddress.zipCode || '').trim();
                return sameStreet && sameZip;
              }
              
              return false;
            });
            setCustomerServiceRequests(requests);
            console.log('[CUSTOMER-DETAILS] ✅ Service requests refreshed after schedule update');
          }
        } catch (err: any) {
          console.error('[CUSTOMER-DETAILS] Failed to refresh service requests after schedule update:', err);
        }
      }
    };
    
    window.addEventListener('customer-schedule-updated', handleScheduleUpdate);
    window.addEventListener('refresh-todays-schedule', handleScheduleUpdate);
    
    return () => {
      window.removeEventListener('customer-schedule-updated', handleScheduleUpdate);
      window.removeEventListener('refresh-todays-schedule', handleScheduleUpdate);
    };
  }, [customerDetailsDialog.open, customerDetailsDialog.customer?.linkedCustomerId]);

  // Fetch ServiceRequests for all customers when All Customers tab is active
  useEffect(() => {
    const fetchAllServiceRequests = async () => {
      if (activeTab === 0 && customers.length > 0) {
        setLoadingAllServiceRequests(true);
        try {
          // If providerId is provided (e.g., from Admin), pass it as query parameter
          const params: any = {
            userType: 'provider',
            status: 'accepted,scheduled,completed,season_ended',
            limit: 1000, // Request high limit to get all ServiceRequests for All Customers view (backend defaults to 10)
            page: 1
          };
          if (providerId) {
            params.providerId = providerId;
          }
          const response = await apiService.get('/service-requests', params);
          
          if (response.success && response.data) {
            const allRequests = (response.data as any).serviceRequests || [];
            
            console.log('[FETCH-ALL-SERVICE-REQUESTS] Fetched service requests:', {
              totalRequests: allRequests.length,
              sampleRequests: allRequests.slice(0, 3).map((sr: any) => ({
                id: sr._id || sr.id,
                category: sr.serviceType?.category,
                scheduledDate: sr.scheduledDate,
                customerId: sr.customerId?._id || sr.customerId?.id || sr.customerId,
                status: sr.status,
                address: sr.address ? `${sr.address.street}, ${sr.address.zipCode}` : 'No address'
              }))
            });
            
            // Enhanced logging for Robert Romero
            const robertRequests = allRequests.filter((sr: any) => {
              const srCustomerId = sr.customerId?._id || sr.customerId?.id || sr.customerId;
              // Check if this is Robert Romero (we'll check by looking for his known addresses)
              const isRobertAddress = sr.address && (
                (sr.address.street || '').toLowerCase().includes('truman') ||
                (sr.address.street || '').toLowerCase().includes('michigan')
              );
              return isRobertAddress;
            });
            
            if (robertRequests.length > 0) {
              console.log('[FETCH-ALL-SERVICE-REQUESTS] Robert Romero ServiceRequests found:', {
                count: robertRequests.length,
                requests: robertRequests.map((sr: any) => ({
                  id: sr._id || sr.id,
                  category: sr.serviceType?.category,
                  status: sr.status,
                  address: sr.address ? `${sr.address.street}, ${sr.address.zipCode}` : 'No address',
                  customerId: sr.customerId?._id || sr.customerId?.id || sr.customerId
                }))
              });
            }
            
            // Group service requests by customer linkedCustomerId AND address
            // CRITICAL: Filter by address to ensure multi-property customers only show services for THIS property
            const requestsByCustomer: Record<string, any[]> = {};
            
            customers.forEach((customer) => {
              const customerId = customer.id || customer._id || '';
              const customerAddress = customer.address;
              
              // Enhanced logging for Robert Romero BEFORE filtering
              if (customer.firstName === 'Robert' && customer.lastName === 'Romero') {
                console.log('[FETCH-ALL-SERVICE-REQUESTS] BEFORE FILTER - Property check:', {
                  customerId: customerId,
                  customerName: `${customer.firstName} ${customer.lastName}`,
                  linkedCustomerId: customer.linkedCustomerId?.toString(),
                  customerAddress: customerAddress ? `${customerAddress.street}, ${customerAddress.zipCode}` : 'No address',
                  totalAllRequests: allRequests.length,
                  allRequestsSample: allRequests.slice(0, 5).map((sr: any) => ({
                    id: sr._id || sr.id,
                    category: sr.serviceType?.category,
                    customerId: sr.customerId?._id || sr.customerId?.id || sr.customerId,
                    address: sr.address ? `${sr.address.street}, ${sr.address.zipCode}` : 'No address',
                    providerId: sr.providerId?._id || sr.providerId?.id || sr.providerId
                  }))
                });
              }
              
              if (customer.linkedCustomerId && customerId && customerAddress) {
                // First, get all ServiceRequests that match the customerId (before address filtering)
                const requestsMatchingCustomerId = allRequests.filter((sr: any) => {
                  const srCustomerId = sr.customerId?._id || sr.customerId?.id || sr.customerId;
                  return srCustomerId?.toString() === customer.linkedCustomerId?.toString();
                });
                
                // Log for Robert Romero
                if (customer.firstName === 'Robert' && customer.lastName === 'Romero') {
                  console.log('[FETCH-ALL-SERVICE-REQUESTS] After customerId filter:', {
                      customerId: customerId,
                    customerName: `${customer.firstName} ${customer.lastName}`,
                    linkedCustomerId: customer.linkedCustomerId?.toString(),
                    customerAddress: `${customerAddress.street}, ${customerAddress.zipCode}`,
                    requestsMatchingCustomerId: requestsMatchingCustomerId.length,
                    requestsMatchingCustomerIdDetails: requestsMatchingCustomerId.map((sr: any) => ({
                      id: sr._id || sr.id,
                      category: sr.serviceType?.category,
                      status: sr.status,
                      address: sr.address ? `${sr.address.street}, ${sr.address.zipCode}` : 'No address',
                      customerId: sr.customerId?._id || sr.customerId?.id || sr.customerId
                    }))
                  });
                }
                
                const customerRequests = requestsMatchingCustomerId.filter((sr: any) => {
                  // Then match by address to ensure we only show services for THIS property
                  const srAddress = sr.address;
                  if (srAddress && customerAddress) {
                    const srStreet = (srAddress.street || '').trim().toLowerCase();
                    const srZip = (srAddress.zipCode || '').trim();
                    const customerStreet = (customerAddress.street || '').trim().toLowerCase();
                    const customerZip = (customerAddress.zipCode || '').trim();
                    const sameStreet = srStreet === customerStreet;
                    const sameZip = srZip === customerZip;
                    
                    // Log address matching for Robert Romero
                    if (customer.firstName === 'Robert' && customer.lastName === 'Romero') {
                      console.log('[FETCH-ALL-SERVICE-REQUESTS] Address match check:', {
                        customerId: customerId,
                        customerName: `${customer.firstName} ${customer.lastName}`,
                        srCategory: sr.serviceType?.category,
                        srId: sr._id || sr.id,
                        srStreet,
                        customerStreet,
                        sameStreet,
                        srZip,
                        customerZip,
                        sameZip,
                        matches: sameStreet && sameZip
                      });
                    }
                    
                    return sameStreet && sameZip;
                  }
                  
                  // If no address match, don't include it (safety check)
                  if (customer.firstName === 'Robert' && customer.lastName === 'Romero') {
                    console.log('[FETCH-ALL-SERVICE-REQUESTS] Missing address:', {
                      customerId: customerId,
                      customerName: `${customer.firstName} ${customer.lastName}`,
                      srCategory: sr.serviceType?.category,
                      srId: sr._id || sr.id,
                      hasSrAddress: !!srAddress,
                      hasCustomerAddress: !!customerAddress
                    });
                  }
                  return false;
                });
                requestsByCustomer[customerId] = customerRequests;
                
                // Enhanced debug logging for Robert Romero
                if (customer.firstName === 'Robert' && customer.lastName === 'Romero') {
                  // Check address matching details
                  const addressMatches = customerRequests.map((sr: any) => {
                    const srAddress = sr.address;
                    if (!srAddress || !customerAddress) return { match: false, reason: 'missing address' };
                    const srStreet = (srAddress.street || '').trim().toLowerCase();
                    const srZip = (srAddress.zipCode || '').trim();
                    const customerStreet = (customerAddress.street || '').trim().toLowerCase();
                    const customerZip = (customerAddress.zipCode || '').trim();
                    return {
                      match: srStreet === customerStreet && srZip === customerZip,
                      srStreet,
                      customerStreet,
                      srZip,
                      customerZip,
                      streetMatch: srStreet === customerStreet,
                      zipMatch: srZip === customerZip
                    };
                  });
                  
                  console.log('[FETCH-ALL-SERVICE-REQUESTS] Robert Romero - Matched requests:', {
                    customerId: customerId,
                    customerName: `${customer.firstName} ${customer.lastName}`,
                    linkedCustomerId: customer.linkedCustomerId?.toString(),
                    customerAddress: `${customerAddress.street}, ${customerAddress.zipCode}`,
                    totalRequests: allRequests.length,
                    matchedCount: customerRequests.length,
                    requests: customerRequests.map((sr: any, idx: number) => ({
                      id: sr._id || sr.id,
                      category: sr.serviceType?.category,
                      scheduledDate: sr.scheduledDate,
                      status: sr.status,
                      customerId: sr.customerId?._id || sr.customerId?.id || sr.customerId,
                      srAddress: sr.address ? `${sr.address.street}, ${sr.address.zipCode}` : 'No address',
                      addressMatch: addressMatches[idx]
                    })),
                    allRequestsForCustomer: allRequests.filter((sr: any) => {
                      const srCustomerId = sr.customerId?._id || sr.customerId?.id || sr.customerId;
                      return srCustomerId?.toString() === customer.linkedCustomerId?.toString();
                    }).map((sr: any) => {
                      const srAddress = sr.address;
                      const srStreet = srAddress ? (srAddress.street || '').trim().toLowerCase() : '';
                      const srZip = srAddress ? (srAddress.zipCode || '').trim() : '';
                      const customerStreet = customerAddress ? (customerAddress.street || '').trim().toLowerCase() : '';
                      const customerZip = customerAddress ? (customerAddress.zipCode || '').trim() : '';
                      const addressMatch = srStreet === customerStreet && srZip === customerZip;
                      
                      return {
                        id: sr._id || sr.id,
                        category: sr.serviceType?.category,
                        status: sr.status,
                        customerId: sr.customerId?._id || sr.customerId?.id || sr.customerId,
                        srAddress: sr.address ? `${sr.address.street}, ${sr.address.zipCode}` : 'No address',
                        addressMatch,
                        srStreet,
                        customerStreet,
                        srZip,
                        customerZip,
                        streetMatch: srStreet === customerStreet,
                        zipMatch: srZip === customerZip
                      };
                    })
                  });
                }
              } else if (customerId) {
                requestsByCustomer[customerId] = [];
              }
            });
            
            setAllCustomersServiceRequests(requestsByCustomer);
          }
        } catch (err: any) {
          console.error('Failed to fetch service requests for all customers:', err);
          setAllCustomersServiceRequests({});
        } finally {
          setLoadingAllServiceRequests(false);
        }
      }
    };

    fetchAllServiceRequests();
  }, [activeTab, customers]);



  const fetchCustomers = async () => {
    try {
      // If providerId is provided (e.g., from Admin), pass it as a query parameter
      const params = providerId ? { providerId } : {};
      const response = await apiService.get('/route-optimization/existing-customers', params);
      if (response.success) {
        const customersData = response.data as ExistingCustomer[];
        console.log('[FETCH-CUSTOMERS] Fetched customers:', customersData.length);
        // Log customers with same name to check for multi-property
        const nameGroups: Record<string, ExistingCustomer[]> = {};
        customersData.forEach(c => {
          const nameKey = `${c.firstName} ${c.lastName}`;
          if (!nameGroups[nameKey]) nameGroups[nameKey] = [];
          nameGroups[nameKey].push(c);
        });
        Object.keys(nameGroups).forEach(name => {
          if (nameGroups[name].length > 1) {
            console.log(`[FETCH-CUSTOMERS] Multi-property customer "${name}":`, nameGroups[name].map(c => ({
              id: c.id || c._id,
              address: `${c.address.street}, ${c.address.city}`,
              linkedCustomerId: c.linkedCustomerId
            })));
          }
        });
        setCustomers(customersData);
      } else {
        console.log('API returned success: false', response.error);
        setCustomers([]);
      }
    } catch (err: any) {
      console.error('Error fetching customers:', err);
      setCustomers([]);
    }
  };

  const handleAddressSelect = (address: string, addressComponents: AddressComponents) => {
    setNewCustomer({
      ...newCustomer,
      address: {
        street: addressComponents.street,
        city: addressComponents.city,
        state: addressComponents.state,
        zipCode: addressComponents.zipCode,
        coordinates: addressComponents.coordinates || { lat: 0, lng: 0 }
      }
    });
  };

  const handleAddCustomer = async () => {
    try {
      setLoading(true);

      // Require at least one contact method (email or phone)
      if (!newCustomer.email && !newCustomer.phone) {
        const errorMessage = '❌ Contact Information Required: Please provide either an email address or phone number so we can send invitations to this customer.';
        console.log('Setting error:', errorMessage); // Debug log
        console.log('Current error state before setting:', error); // Debug log
        setError(errorMessage);
        console.log('Error set, new error state should be:', errorMessage); // Debug log
        setLoading(false);
        return;
      }

      const unpricedServices = [
        newCustomer.services?.lawnMowingTrimming && !(Number(servicePricing.lawnMowingTrimming.price) > 0) && 'Lawn Mowing',
        newCustomer.services?.weedControlFertilizing && !(Number(servicePricing.weedControlFertilizing.price) > 0) && 'Fertilizing & Weed Control',
        newCustomer.services?.snowRemoval && !(Number(servicePricing.snowRemoval.price) > 0) && 'Snow Removal',
      ].filter(Boolean);
      if (unpricedServices.length > 0) {
        setError(`Enter a price greater than $0 for: ${unpricedServices.join(', ')}.`);
        setLoading(false);
        return;
      }

      // Clear any previous errors if validation passes
      setError(null);

      // Calculate total price based on selected services
      let totalPrice = 0;
      if (newCustomer.services?.lawnMowingTrimming) {
        totalPrice += Number(servicePricing.lawnMowingTrimming.price) || 0;
      }
      if (newCustomer.services?.weedControlFertilizing) {
        totalPrice += Number(servicePricing.weedControlFertilizing.price) || 0;
      }
      if (newCustomer.services?.snowRemoval) {
        totalPrice += Number(servicePricing.snowRemoval.price) || 0;
      }

      const customerData: any = {
        ...newCustomer,
        baggingRequired: newCustomer.services?.lawnMowingTrimming ? !!newCustomer.baggingRequired : false,
        price: totalPrice,
        servicePricing: {
          lawnMowingTrimming: newCustomer.services?.lawnMowingTrimming ? {
            price: servicePricing.lawnMowingTrimming.price,
            frequency: servicePricing.lawnMowingTrimming.frequency,
            // Include seasonalContractDetails if it's a seasonal contract (matching bid structure)
            ...(servicePricing.lawnMowingTrimming.frequency === 'seasonal-contract' ? {
              seasonalContractDetails: {
                serviceFrequency: servicePricing.lawnMowingTrimming.serviceFrequency,
                contractStartDate: servicePricing.lawnMowingTrimming.contractStartDate ? new Date(servicePricing.lawnMowingTrimming.contractStartDate) : undefined,
                contractEndDate: servicePricing.lawnMowingTrimming.contractEndDate ? new Date(servicePricing.lawnMowingTrimming.contractEndDate) : undefined,
                totalContractPrice: servicePricing.lawnMowingTrimming.price,
                paymentSchedule: servicePricing.lawnMowingTrimming.paymentSchedule || 'single-payment'
              }
            } : {})
          } : null,
          weedControlFertilizing: newCustomer.services?.weedControlFertilizing ? {
            price: servicePricing.weedControlFertilizing.price,
            frequency: servicePricing.weedControlFertilizing.frequency,
            // Include seasonalContractDetails if it's a seasonal contract (matching bid structure)
            ...(servicePricing.weedControlFertilizing.frequency === 'seasonal-contract' ? {
              seasonalContractDetails: {
                serviceFrequency: servicePricing.weedControlFertilizing.serviceFrequency,
                contractStartDate: servicePricing.weedControlFertilizing.contractStartDate ? new Date(servicePricing.weedControlFertilizing.contractStartDate) : undefined,
                contractEndDate: servicePricing.weedControlFertilizing.contractEndDate ? new Date(servicePricing.weedControlFertilizing.contractEndDate) : undefined,
                totalContractPrice: servicePricing.weedControlFertilizing.price,
                paymentSchedule: servicePricing.weedControlFertilizing.paymentSchedule || 'single-payment'
              }
            } : {})
          } : null,
          snowRemoval: newCustomer.services?.snowRemoval ? {
            price: servicePricing.snowRemoval.price,
            frequency: servicePricing.snowRemoval.frequency
          } : null
        },
        serviceSchedule: {
          ...newCustomer.serviceSchedule,
          frequency: newCustomer.services?.lawnMowingTrimming ? 
            servicePricing.lawnMowingTrimming.frequency : 'weekly'
        }
      };

      const response = await apiService.post('/route-optimization/existing-customers', customerData);

      if (response.success) {
        await fetchCustomers();
        setAddCustomerDialogOpen(false);
        clearCustomerForm();
        setSuccess('Customer added successfully!');
      } else {
        setError(response.error || 'Failed to add customer');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to add customer');
    } finally {
      setLoading(false);
    }
  };

  // Parse CSV text into array of objects (first row = headers)
  const parseCSV = (text: string): Array<Record<string, string>> => {
    const lines = text.split(/\r?\n/).filter((line) => line.trim());
    if (lines.length < 2) return [];
    const rawHeaders = lines[0].split(',').map((h) => h.trim().toLowerCase().replace(/\s+/g, ''));
    const rows: Array<Record<string, string>> = [];
    for (let i = 1; i < lines.length; i++) {
      const values: string[] = [];
      let current = '';
      let inQuotes = false;
      for (let j = 0; j < lines[i].length; j++) {
        const c = lines[i][j];
        if (c === '"') {
          inQuotes = !inQuotes;
        } else if ((c === ',' && !inQuotes) || c === '\n' || c === '\r') {
          values.push(current.trim());
          current = '';
          if (c !== ',') break;
        } else {
          current += c;
        }
      }
      values.push(current.trim());
      const row: Record<string, string> = {};
      rawHeaders.forEach((h, idx) => {
        row[h] = values[idx] ?? '';
      });
      if (row.firstname || row.lastname || row.email || row.phone || row.street) {
        rows.push(row);
      }
    }
    return rows;
  };

  const normalizeKey = (key: string): string => {
    const k = key.toLowerCase().replace(/\s+/g, '');
    if (k === 'firstname' || k === 'first_name') return 'firstname';
    if (k === 'lastname' || k === 'last_name') return 'lastname';
    if (k === 'zipcode' || k === 'zip') return 'zipcode';
    if (k === 'mowingbagged' || k === 'mowing_bagged' || k === 'bagged') return 'mowingbagged';
    return k;
  };

  const csvRowToBulkCustomer = (row: Record<string, string>): any => {
    const get = (...keys: string[]) => {
      for (const k of keys) {
        const v = row[k] ?? row[normalizeKey(k)];
        if (v !== undefined && v !== '') return String(v).trim();
      }
      return '';
    };
    const servicesStr = (get('services') || '').toLowerCase();
    const mowing = /mowing|lawn/.test(servicesStr) || servicesStr === '' || servicesStr === 'mowing';
    const fertilizing = /fertiliz|weed/.test(servicesStr);
    const snow = /snow/.test(servicesStr);
    const bagged = /^(yes|y|1|true)$/i.test((get('mowingbagged') || '').trim());
    const street = get('street', 'address');
    const city = get('city');
    const state = get('state');
    const zipCode = (get('zipcode', 'zip') || '').replace(/\D/g, '').slice(0, 10);
    return {
      firstName: get('firstname') || 'Customer',
      lastName: get('lastname') || 'Unknown',
      email: get('email') || undefined,
      phone: get('phone') || undefined,
      address: { street: street || 'TBD', city: city || 'TBD', state: state || 'IL', zipCode: zipCode || '00000' },
      services: { lawnMowingTrimming: mowing, weedControlFertilizing: fertilizing, snowRemoval: snow },
      baggingRequired: mowing && bagged,
      serviceSchedule: { frequency: 'weekly', dayOfWeek: 1 },
      price: 0,
      servicePricing: {
        lawnMowingTrimming: mowing ? { price: 0, frequency: 'weekly' } : null,
        weedControlFertilizing: fertilizing ? { price: 0, frequency: 'monthly' } : null,
        snowRemoval: snow ? { price: 0, frequency: 'per-instance' } : null,
      },
      paymentStructure: { type: 'one-time' as const, amount: 0, isPaymentSetup: false, firstPaymentProcessed: false },
      customerSource: 'onboarded' as const,
    };
  };

  const handleCsvFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setCsvImportError(null);
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const text = String(reader.result ?? '');
        const rows = parseCSV(text);
        setCsvPreviewRows(rows);
        if (rows.length === 0) setCsvImportError('No valid rows found. Use headers: FirstName, LastName, Email, Phone, Street, City, State, ZipCode, Services, Mowing Bagged');
      } catch (e) {
        setCsvImportError('Failed to parse CSV.');
      }
    };
    reader.readAsText(file);
    event.target.value = '';
  };

  const handleCsvConfirmImport = async () => {
    if (csvPreviewRows.length === 0) {
      setCsvImportError('Add at least one row to import.');
      return;
    }
    setCsvImportLoading(true);
    setCsvImportError(null);
    try {
      const customers = csvPreviewRows.map((row) => csvRowToBulkCustomer(row));
      const res = await apiService.post('/existing-customers/bulk', { customers });
      const imported = Array.isArray(res?.data) ? res.data : [];
      if (res.success && imported.length > 0) {
        await fetchCustomers();
        setSuccess(
          `Imported ${imported.length} customer(s): ${imported.map((c: any) => `${c.firstName} ${c.lastName}`).join(', ')}. ` +
          'Use the $ button on each customer to set prices before sending invitations.'
        );
        setCsvImportDialogOpen(false);
        setCsvPreviewRows([]);
      } else {
        setCsvImportError(res.error || 'Import failed');
      }
    } catch (err: any) {
      setCsvImportError(err.message || 'Import failed');
    } finally {
      setCsvImportLoading(false);
    }
  };

  const handleRemoveCustomer = async (customerId: string) => {
    if (!window.confirm('Are you sure you want to remove this customer? This action cannot be undone.')) {
      return;
    }

    // Validate customerId before making the request
    if (!customerId || customerId === 'undefined' || customerId === 'null') {
      setError('Invalid customer ID. Please refresh the page and try again.');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const response = await apiService.delete(`/route-optimization/existing-customers/${customerId}`);

      if (response.success) {
        await fetchCustomers();
        setSuccess('Customer removed successfully');
      } else {
        setError(response.error || 'Failed to remove customer');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to remove customer');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteInvitation = async (invitationId: string, customerName: string) => {
    if (!window.confirm(`Are you sure you want to delete the invitation for ${customerName}?`)) {
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const response = await apiService.delete(`/invitations/${invitationId}`);

      if (response.success) {
        await fetchCustomers();
        setSuccess('Invitation deleted successfully');
      } else {
        setError(response.error || 'Failed to delete invitation');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to delete invitation');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateNextServiceDate = async (customerId: string, changeType: 'one-time' | 'permanent') => {
    if (!nextServiceDateValue) {
      setError('Please select a date');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const response = await apiService.put(`/existing-customers/${customerId}/next-service-date`, {
        nextServiceDate: nextServiceDateValue,
        changeType: changeType
      });

      if (response.success) {
        // Fetch updated customers list
        const updatedCustomersResponse = await apiService.get('/route-optimization/existing-customers');
        if (updatedCustomersResponse.success && Array.isArray(updatedCustomersResponse.data)) {
          setCustomers(updatedCustomersResponse.data as ExistingCustomer[]);
          // Update the customer in the dialog with fresh data if dialog is open
          if (customerDetailsDialog.open) {
            const updatedCustomer = updatedCustomersResponse.data.find((c: ExistingCustomer) => c.id === customerId);
            if (updatedCustomer) {
              setCustomerDetailsDialog({ ...customerDetailsDialog, customer: updatedCustomer });
            }
          }
        }
        setEditingNextServiceDate(null);
        setNextServiceDateValue('');
        setChangeTypeDialog({ open: false, customerId: null });
        setSuccess('Next service date updated successfully');
      } else {
        setError(response.error || 'Failed to update next service date');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to update next service date');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveNextServiceDate = (customerId: string) => {
    if (!nextServiceDateValue) {
      setError('Please select a date');
      return;
    }
    // Show dialog to ask if this is one-time or permanent
    setChangeTypeDialog({ open: true, customerId });
  };

  const handleAddServiceToToday = async (customerId: string, serviceType: 'lawnMowingTrimming' | 'weedControlFertilizing' | 'snowRemoval') => {
    try {
      setAddingToSchedule(`${customerId}-${serviceType}`);
      setError(null);
      setSuccess(null);

      // Find the customer (check both id and _id)
      const customer = customers.find(c => {
        const cId = (c.id || c._id || '').toString();
        const searchId = customerId.toString();
        return cId === searchId;
      });
      if (!customer) {
        console.error('[ADD-TO-TODAY] Customer not found:', { 
          customerId, 
          availableIds: customers.map(c => ({ id: c.id, _id: c._id, name: `${c.firstName} ${c.lastName}` }))
        });
        setError('Customer not found');
        return;
      }

      if (!customer.linkedCustomerId) {
        setError('Customer must be onboarded first');
        return;
      }

      if (customer.isOnHold) {
        setError('Cannot add service to today - customer is on payment hold');
        return;
      }

      // Map service type to ServiceRequest category
      const serviceCategoryMap: Record<string, string> = {
        'lawnMowingTrimming': 'mowing',
        'weedControlFertilizing': 'fertilizing',
        'snowRemoval': 'snow_removal'
      };
      const category = serviceCategoryMap[serviceType];

      // Get today's date in YYYY-MM-DD format (in user's timezone to match ProviderDashboard)
      const todayStr = getTodayInUserTimezone();

      // Find the ServiceRequest for this customer and service type
      const serviceRequestsResponse = await apiService.get('/service-requests', {
        userType: 'provider',
        status: 'accepted,scheduled'
      });

      if (!serviceRequestsResponse.success || !serviceRequestsResponse.data) {
        console.error('[ADD-TO-TODAY] Failed to fetch service requests:', serviceRequestsResponse);
        setError('Failed to fetch service requests');
        return;
      }

      const allRequests = (serviceRequestsResponse.data as any).serviceRequests || [];

      const customerRequests = allRequests.filter((sr: any) => {
        const srCustomerId = sr.customerId?._id || sr.customerId?.id || sr.customerId;
        const matches = srCustomerId?.toString() === customer.linkedCustomerId?.toString();
        return matches;
      });

      // Find the ServiceRequest for this specific service type
      // If multiple exist, prefer one that's scheduled (not completed) or the most recent one
      const serviceRequestsForCategory = customerRequests.filter((sr: any) => {
        const srCategory = sr.serviceType?.category;
        return srCategory === category;
      });
      
      // Prefer a scheduled (not completed) service request, or the most recent one
      let serviceRequest = serviceRequestsForCategory.find((sr: any) => 
        sr.status === 'scheduled' || sr.status === 'accepted'
      );
      
      // If no scheduled/accepted found, use the first one
      if (!serviceRequest && serviceRequestsForCategory.length > 0) {
        serviceRequest = serviceRequestsForCategory[0];
      }

      if (!serviceRequest) {
        console.error('[ADD-TO-TODAY] Service request not found:', {
          customerName: `${customer.firstName} ${customer.lastName}`,
          linkedCustomerId: customer.linkedCustomerId,
          requestedCategory: category,
          availableCategories: customerRequests.map((sr: any) => sr.serviceType?.category)
        });
        setError(`${serviceType === 'lawnMowingTrimming' ? 'Mowing' : serviceType === 'weedControlFertilizing' ? 'Fertilizer' : 'Snow'} service not found. Please schedule the service first.`);
        return;
      }

      // Schedule the service for today
      const scheduleResponse = await apiService.post(`/service-requests/${serviceRequest._id || serviceRequest.id}/schedule`, {
        scheduledDate: todayStr,
        resetRecurringSchedule: false
      });

      if (scheduleResponse.success) {
        const serviceName = serviceType === 'lawnMowingTrimming' ? 'Mowing' : serviceType === 'weedControlFertilizing' ? 'Fertilizer' : 'Snow';
        
        setSuccess(`${serviceName} service added to today's schedule successfully!`);
        
        // Get the updated service request from the response
        const updatedServiceRequest = scheduleResponse.data as any;
        
        // Immediately update the service request in allCustomersServiceRequests state
        // This ensures the Next Service Date updates immediately without waiting for a full refresh
        const customerIdForUpdate = customer.id || customer._id || '';
        if (updatedServiceRequest && customer.linkedCustomerId && customerIdForUpdate) {
          setAllCustomersServiceRequests((prev: Record<string, any[]>) => {
            const newState = { ...prev };
            const existingRequests = newState[customerIdForUpdate] || [];
            
            // Find and replace the updated service request
            const requestIndex = existingRequests.findIndex((sr: any) => {
              const srId = sr._id || sr.id;
              const updatedId = updatedServiceRequest._id || updatedServiceRequest.id;
              return srId?.toString() === updatedId?.toString();
            });
            
            if (requestIndex >= 0) {
              // Replace existing service request with updated one (deep clone to ensure React detects change)
              const updatedArray = [...existingRequests];
              updatedArray[requestIndex] = { ...updatedServiceRequest };
              newState[customerIdForUpdate] = updatedArray;
            } else {
              // Add new service request if not found
              newState[customerIdForUpdate] = [...existingRequests, { ...updatedServiceRequest }];
            }
            
            return newState;
          });
        }
        
        // Wait a moment to let React process the state update and re-render
        await new Promise(resolve => setTimeout(resolve, 100));
        
        // Refresh the customer list
        await fetchCustomers();
        
        // Wait longer for database to update and avoid race conditions
        await new Promise(resolve => setTimeout(resolve, 1000));
        
        // Also do a full refresh to ensure everything is in sync
        // But preserve the updated service request from the response
        if (activeTab === 0) {
          try {
            const serviceRequestsResponse = await apiService.get('/service-requests', {
              userType: 'provider',
              status: 'accepted,scheduled,completed,season_ended'
            });
            
            if (serviceRequestsResponse.success && serviceRequestsResponse.data) {
              const allRequests = (serviceRequestsResponse.data as any).serviceRequests || [];
              
              // Get fresh customers list (after fetchCustomers updates state)
              const freshCustomersResponse = await apiService.get('/route-optimization/existing-customers');
              const freshCustomers = freshCustomersResponse.success ? (freshCustomersResponse.data as ExistingCustomer[]) : customers;
              
              // Group service requests by customer, but merge in the updated service request if present
              const requestsByCustomer: Record<string, any[]> = {};
              
              freshCustomers.forEach((customer) => {
                const customerId = customer.id || customer._id || '';
                if (customer.linkedCustomerId && customerId) {
                  const customerRequests = allRequests.filter((sr: any) => {
                    const srCustomerId = sr.customerId?._id || sr.customerId?.id || sr.customerId;
                    return srCustomerId?.toString() === customer.linkedCustomerId?.toString();
                  });
                  
                  // If this is the customer we just updated, ensure we use the updated service request
                  if (customerId === customerIdForUpdate && updatedServiceRequest) {
                    const updatedRequestId = (updatedServiceRequest._id || updatedServiceRequest.id)?.toString();
                    const requestIndex = customerRequests.findIndex((sr: any) => {
                      const srId = (sr._id || sr.id)?.toString();
                      return srId === updatedRequestId;
                    });
                    
                    if (requestIndex >= 0) {
                      // Replace with the updated version from the response
                      customerRequests[requestIndex] = { ...updatedServiceRequest };
                    }
                  }
                  
                  requestsByCustomer[customerId] = customerRequests;
                } else if (customerId) {
                  requestsByCustomer[customerId] = [];
                }
              });
              
              setAllCustomersServiceRequests(requestsByCustomer);
            }
          } catch (err) {
            console.error('[ADD-TO-TODAY] Error refreshing service requests:', err);
          }
        }
        
        // Trigger a refresh on the provider dashboard
        window.dispatchEvent(new Event('customer-schedule-updated'));
        window.dispatchEvent(new CustomEvent('refresh-todays-schedule'));
      } else {
        console.error('[ADD-TO-TODAY] Schedule failed:', scheduleResponse);
        setError(scheduleResponse.error || 'Failed to schedule service for today');
      }
    } catch (err: any) {
      console.error('[ADD-TO-TODAY] Error adding service to today\'s schedule:', {
        error: err,
        message: err.message,
        response: err.response?.data,
        stack: err.stack
      });
      setError(err.response?.data?.error || err.message || 'Failed to add service to today\'s schedule');
    } finally {
      setAddingToSchedule(null);
    }
  };

  const handleResendInvitation = async (invitationId: string, customerName: string, customer?: ExistingCustomer) => {
    if (customer && needsPricing(customer)) {
      setPricingCustomer(customer);
      return;
    }
    try {
      setLoading(true);
      setError(null);

      const response = await apiService.post(`/invitations/${invitationId}/resend`);

      if (response.success) {
        setSuccess(`Invitation resent to ${customerName} successfully`);
      } else {
        setError(response.error || 'Failed to resend invitation');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to resend invitation');
    } finally {
      setLoading(false);
    }
  };

  const handleSendPaymentReminder = async (customer: ExistingCustomer) => {
    if (needsPricing(customer)) {
      setPricingCustomer(customer);
      return;
    }
    try {
      setLoading(true);
      setError(null);

      const response = await apiService.post(`/route-optimization/existing-customers/${customer.id}/send-payment-reminder`, {});

      if (response.success) {
        setSuccess(`Payment setup reminder sent to ${customer.firstName} ${customer.lastName} successfully`);
      } else {
        setError(response.error || 'Failed to send payment reminder');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to send payment reminder');
    } finally {
      setLoading(false);
    }
  };

  const handleSendSMSInvitation = async (customer: ExistingCustomer) => {
    if (!customer.phone) {
      setError('Customer phone number is required for SMS invitation');
      return;
    }
    if (needsPricing(customer)) {
      setPricingCustomer(customer);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const response = await apiService.post('/route-optimization/existing-customers/send-sms-invitation', {
        customerId: customer.id,
        customerPhone: customer.phone,
        customerName: `${customer.firstName} ${customer.lastName}`,
        services: customer.services,
        frequency: customer.serviceSchedule?.frequency || 'per-instance'
      });
      
      if (response.success) {
        setSuccess(`SMS invitation sent to ${customer.firstName} ${customer.lastName}`);
      } else {
        setError(response.error || 'Failed to send SMS invitation');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to send SMS invitation');
    } finally {
      setLoading(false);
    }
  };

  const handleMarkSeasonEnded = async (serviceRequestId: string, serviceCategory: string) => {
    // Close confirmation dialog
    setSeasonEndConfirmDialog({ open: false, serviceRequestId: null, serviceCategory: null });
    
    try {
      setMarkingSeasonEnded(serviceRequestId);
      setError(null);
      setSuccess(null);

      const response = await apiService.patch(`/service-requests/${serviceRequestId}/mark-season-ended`);

      if (response.success) {
        const serviceName = serviceCategory === 'mowing' ? 'Lawn Mowing' : 
                          serviceCategory === 'fertilizing' ? 'Weed Control' : 
                          'Snow Removal';
        setSuccess(`${serviceName} service marked as season ended. It will appear on your Renewals page.`);
        
        // Refresh service requests and customers
        if (customerDetailsDialog.customer?.linkedCustomerId) {
          try {
            const serviceRequestsResponse = await apiService.get('/service-requests', {
              userType: 'provider',
              status: 'accepted,scheduled,completed,season_ended'
            });
            
            if (serviceRequestsResponse.success && serviceRequestsResponse.data) {
              const allRequests = (serviceRequestsResponse.data as any).serviceRequests || [];
              const requests = allRequests.filter((sr: any) => {
                const srCustomerId = sr.customerId?._id || sr.customerId?.id || sr.customerId;
                const matchesCustomer = srCustomerId?.toString() === customerDetailsDialog.customer?.linkedCustomerId?.toString();
                if (!matchesCustomer) return false;
                
                // Filter by address
                const srAddress = sr.address;
                const customerAddress = customerDetailsDialog.customer?.address;
                if (srAddress && customerAddress) {
                  const sameStreet = (srAddress.street || '').trim().toLowerCase() === (customerAddress.street || '').trim().toLowerCase();
                  const sameZip = (srAddress.zipCode || '').trim() === (customerAddress.zipCode || '').trim();
                  return sameStreet && sameZip;
                }
                return false;
              });
              
              setCustomerServiceRequests(requests);
            }
          } catch (err: any) {
            console.error('Failed to refresh service requests:', err);
          }
        }
        
        // Refresh customers list
        fetchCustomers();
      } else {
        setError(response.error || 'Failed to mark service as season ended');
      }
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Failed to mark service as season ended');
    } finally {
      setMarkingSeasonEnded(null);
    }
  };

  const handleSendInvitation = async (customer: ExistingCustomer) => {
    if (needsPricing(customer)) {
      setPricingCustomer(customer);
      return;
    }
    try {
      setLoading(true);
      setError(null);

      const response = await apiService.post('/invitations/create', {
        existingCustomerId: customer.id
      });
      
      if (response.success) {
        setSuccess(`Invitation sent to ${customer.firstName} ${customer.lastName}`);
        await fetchCustomers(); // Refresh to show updated status
      } else {
        setError(response.error || 'Failed to send invitation');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to send invitation');
    } finally {
      setLoading(false);
    }
  };

  const clearCustomerForm = () => {
    setNewCustomer({
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
        address: {
          street: '',
          city: '',
          state: '',
          zipCode: '',
          coordinates: { lat: 0, lng: 0 }
        },
      serviceSchedule: {
        frequency: 'weekly',
        dayOfWeek: 1,
        preferredTime: '',
        notes: ''
      },
      services: {
        lawnMowingTrimming: false,
        weedControlFertilizing: false,
        snowRemoval: false
      },
      baggingRequired: false,
      price: 0
    });
    setServicePricing({
      lawnMowingTrimming: { 
        frequency: 'weekly', 
        price: 0, 
        serviceFrequency: 'weekly' as 'weekly' | 'bi-weekly' | 'monthly' | 'per-instance',
        contractStartDate: '',
        contractEndDate: '',
        paymentSchedule: 'single-payment' as 'single-payment' | 'monthly'
      },
      weedControlFertilizing: { 
        frequency: 'monthly', 
        price: 0, 
        serviceFrequency: 'monthly' as 'weekly' | 'bi-weekly' | 'monthly' | 'per-instance',
        contractStartDate: '',
        contractEndDate: '',
        paymentSchedule: 'single-payment' as 'single-payment' | 'monthly'
      },
      snowRemoval: { 
        frequency: 'per-instance', 
        price: 0, 
        serviceFrequency: 'per-instance' as 'weekly' | 'bi-weekly' | 'monthly' | 'per-instance'
      }
    });
  };

  const hasPaymentSetup = (customer: ExistingCustomer) => hasPaymentSetupUtil(customer, customers);
  const needsPricing = (customer: ExistingCustomer) => (customer.unpricedServices?.length ?? 0) > 0;

  const renderPricingButton = (customer: ExistingCustomer) => {
    if (!customer.canEditPricing) return null;
    const missing = needsPricing(customer);
    return (
      <Tooltip
        title={
          missing
            ? `Set prices before inviting (missing: ${customer.unpricedServices!.join(', ')})`
            : 'Edit prices (allowed until the customer accepts)'
        }
      >
        <IconButton
          size="small"
          color={missing ? 'warning' : 'default'}
          onClick={() => setPricingCustomer(customer)}
        >
          <AttachMoney fontSize="small" />
        </IconButton>
      </Tooltip>
    );
  };
  const getAccountStatus = (customer: ExistingCustomer) => getAccountStatusUtil(customer);

  // Helper function to get customer status information (combined account + payment for legacy use)
  const getCustomerStatus = (customer: ExistingCustomer) => {
    const isBidCustomer = customer.customerSource === 'bid';
    const paid = hasPaymentSetup(customer);

    if (customer.linkedCustomerId && paid) {
      if (isBidCustomer) {
        return {
          type: 'bid_active',
          label: 'New Customer',
          color: 'success' as const,
          icon: <CheckCircle />,
          fee: '5%',
          description: 'Customer from marketplace with active payment',
          tooltip: 'This customer came from the marketplace and has active payment. You receive 95% of the service price (5% marketplace fee, first season).'
        };
      }
      return {
        type: 'onboarded',
        label: 'Onboarded',
        color: 'success' as const,
        icon: <CheckCircle />,
        fee: '1%',
        description: 'Customer has completed account setup and payment',
        tooltip: 'This customer has completed account setup and payment registration. You receive 99% of the service price (1% platform fee).'
      };
    }
    if (customer.linkedCustomerId && !paid) {
      return {
        type: 'account_created',
        label: 'Payment Setup Needed',
        color: 'warning' as const,
        icon: <Warning />,
        fee: 'N/A',
        description: 'Customer has account but no payment setup',
        tooltip: 'Customer has created an account but needs to set up payment. They will be charged 5% marketplace fees until payment is set up.'
      };
    }
    if (customer.invitationId) {
      return {
        type: 'invited',
        label: 'Invited',
        color: 'info' as const,
        icon: <Email />,
        fee: 'N/A',
        description: 'Invitation sent, waiting for signup',
        tooltip: 'Invitation has been sent. Waiting for customer to create account.'
      };
    } else {
      return {
        type: 'pending',
        label: 'Not Invited',
        color: 'error' as const,
        icon: <Warning />,
        fee: 'N/A',
        description: 'Customer needs invitation',
        tooltip: 'Customer needs to be invited to create an account.'
      };
    }
  };

  // Helper function to generate service pricing breakdown for existing customers
  const generateServiceBreakdown = (customer: ExistingCustomer) => {
    // First, try to use the stored individual service pricing
    if (customer.servicePricing) {
      return customer.servicePricing;
    }
    
    // Fallback: if no individual pricing is stored, return null
    // This prevents showing incorrect calculated pricing
    return null;
  };

  // Helper function to get payment status information. Uses hasPaymentSetup as single source of truth.
  const getPaymentStatus = (customer: ExistingCustomer) => {
    if (!customer.linkedCustomerId) {
      return {
        type: 'not_setup',
        label: 'Not Setup',
        color: 'error' as const,
        icon: <Warning />,
        tooltip: 'Customer has not created a platform account yet. No payment setup possible until account is created.'
      };
    }
    if (hasPaymentSetup(customer)) {
      return {
        type: 'setup',
        label: 'Setup',
        color: 'success' as const,
        icon: <CheckCircle />,
        tooltip: `Customer has active payment method${customer.paymentMethodDetails?.cardCount && customer.paymentMethodDetails.cardCount > 1 ? 's' : ''} on file. Ready for automated payments.`
      };
    }
    return {
      type: 'not_setup',
      label: 'Not Setup',
      color: 'warning' as const,
      icon: <Warning />,
      tooltip: 'Customer account exists but payment structure not configured. Set up payment structure to enable automated billing.'
    };
  };

  // Helper to determine if a customer originated from the marketplace (bid) or was manually onboarded/imported
  const isMarketplaceCustomer = (customer: ExistingCustomer): boolean =>
    (customer.customerSource || '').toLowerCase() === 'bid';

  // Separate customers by type:
  // - Onboarded customers: Manually onboarded existing customers who have completed payment setup (1% fee)
  // - Pending customers: Manually onboarded customers (or legacy records without a source) who haven't created accounts or completed payment setup
  const onboardedCustomers = customers.filter(customer => 
    !customer.isDeleted &&
    !isMarketplaceCustomer(customer) &&
    customer.linkedCustomerId &&
    hasPaymentSetup(customer)
  );

  const pendingCustomers = customers.filter(customer => 
    !isMarketplaceCustomer(customer) &&
    (!customer.linkedCustomerId || !hasPaymentSetup(customer))
  );
  
  // Helper function to get service frequency for a customer
  // SINGLE SOURCE OF TRUTH: ServiceRequest.frequency
  // This is updated from bid when accepted (bid-accepted services) or from ExistingCustomer.servicePricing when created (onboarded customers)
  // No fallbacks - ServiceRequest.frequency should always be set correctly
  // Use shared utility function for getting service frequency (SINGLE SOURCE OF TRUTH)
  const getServiceFrequency = (
    customer: ExistingCustomer,
    category: 'mowing' | 'fertilizing' | 'snow_removal',
    serviceRequest?: any
  ): string | null => {
    return getServiceFrequencyUtil(customer, category, serviceRequest);
  };
  
  // Helper function to check if a customer has ANY recurring service (not just per-instance)
  const customerHasRecurringService = (customer: ExistingCustomer): boolean => {
    const customerId = customer.id || customer._id || '';
    const customerRequests = allCustomersServiceRequests[customerId] || [];
    
    // Helper to get service request for a category
    const getServiceRequest = (category: string) => {
      return customerRequests.find((sr: any) => {
        const srCategory = sr.serviceType?.category;
        return srCategory === category;
      });
    };
    
    // CRITICAL: Use ServiceRequest as single source of truth, not servicePricing
    // ServiceRequest is already filtered by providerId (backend) and address (frontend)
    const services = [
      { category: 'mowing' as const, hasService: getServiceRequest('mowing') !== undefined },
      { category: 'fertilizing' as const, hasService: getServiceRequest('fertilizing') !== undefined },
      { category: 'snow_removal' as const, hasService: getServiceRequest('snow_removal') !== undefined }
    ];
    
    // Check if any service is recurring (not per-instance)
    for (const service of services) {
      if (service.hasService) {
        const serviceRequest = getServiceRequest(service.category);
        const serviceFreq = getServiceFrequency(customer, service.category, serviceRequest);
        
        // If service frequency is recurring (weekly, bi-weekly, monthly) or seasonal-contract (which might have recurring serviceFrequency)
        if (serviceFreq && serviceFreq !== 'per-instance') {
          // For seasonal contracts, check if the actual serviceFrequency is per-instance
          if (serviceFreq === 'seasonal-contract' && serviceRequest?.acceptedBid) {
            const bid = serviceRequest.acceptedBid;
            if (bid.servicePricing && Array.isArray(bid.servicePricing)) {
              const matchingServicePricing = bid.servicePricing.find((sp: any) => {
                if (service.category === 'mowing') return sp.service === 'lawnMowingTrimming' || sp.serviceName?.toLowerCase().includes('mowing');
                if (service.category === 'fertilizing') return sp.service === 'weedControlFertilizing' || sp.serviceName?.toLowerCase().includes('fertiliz') || sp.serviceName?.toLowerCase().includes('weed');
                if (service.category === 'snow_removal') return sp.service === 'snowRemoval' || sp.serviceName?.toLowerCase().includes('snow');
                return false;
              });
              
              const actualFreq = matchingServicePricing?.seasonalContractDetails?.serviceFrequency;
              // If it's per-instance, skip this service
              if (actualFreq === 'per-instance') {
                continue;
              }
            }
          }
          // Found a recurring service
          return true;
        }
      }
    }
    
    // All services are per-instance or no services
    return false;
  };

  // For Schedule tab: Show customers who have payment setup (fully onboarded) but don't have a nextServiceDate yet
  // EXCLUDE customers who ONLY have per-instance services (they don't need initial scheduling)
  // This includes:
  // 1. Bid-accepted customers (customerSource: 'bid') with payment setup - they need scheduling even if they haven't signed up yet
  // 2. Manually onboarded customers with linked accounts and payment setup
  const customersNeedingSchedule = customers.filter(customer => {
    if (!hasPaymentSetup(customer)) {
      return false;
    }
    
    // Must not have a nextServiceDate yet (needs scheduling)
    if (customer.nextServiceDate) {
      return false;
    }
    
    // EXCLUDE customers who ONLY have per-instance services
    // Per-instance services are scheduled on-demand, not initially
    if (!customerHasRecurringService(customer)) {
      return false;
    }
    
    return true;
  });
  
  // Helper function to get effective frequency for a customer
  // Checks both serviceSchedule.frequency and paymentStructure.type for seasonal contracts
  const getCustomerFrequency = (customer: ExistingCustomer): string => {
    // Check if it's a seasonal contract (either in serviceSchedule or paymentStructure)
    if (customer.serviceSchedule?.frequency === 'seasonal-contract' || 
        customer.paymentStructure?.type === 'seasonal-contract') {
      return 'seasonal-contract';
    }
    // Otherwise use serviceSchedule frequency
    return customer.serviceSchedule?.frequency || 'per-instance';
  };

  // Helper function to get the actual service frequency for a specific service
  // This handles seasonal contracts by checking the accepted bid's serviceFrequency
  // Helper function to check if a customer has ANY service with the given frequency
  const customerHasServiceWithFrequency = (
    customer: ExistingCustomer,
    frequency: string,
    serviceType?: 'mowing' | 'fertilizing' | 'snow_removal'
  ): boolean => {
    const customerId = customer.id || customer._id || '';
    const customerRequests = allCustomersServiceRequests[customerId] || [];
    
    // Helper to get service request for a category
    const getServiceRequest = (category: string) => {
      return customerRequests.find((sr: any) => {
        const srCategory = sr.serviceType?.category;
        return srCategory === category;
      });
    };
    
    // Check each service type
    const servicesToCheck: Array<{ category: 'mowing' | 'fertilizing' | 'snow_removal'; hasService: boolean }> = [];
    
    // CRITICAL: Use ServiceRequest as single source of truth, not servicePricing
    // ServiceRequest is already filtered by providerId (backend) and address (frontend)
    if (!serviceType || serviceType === 'mowing') {
        const serviceRequest = getServiceRequest('mowing');
      if (serviceRequest) {
        const serviceFreq = getServiceFrequency(customer, 'mowing', serviceRequest);
        if (serviceFreq === frequency) {
          return true;
        }
      }
    }
    
    if (!serviceType || serviceType === 'fertilizing') {
        const serviceRequest = getServiceRequest('fertilizing');
      if (serviceRequest) {
        const serviceFreq = getServiceFrequency(customer, 'fertilizing', serviceRequest);
        if (serviceFreq === frequency) {
          return true;
        }
      }
    }
    
    if (!serviceType || serviceType === 'snow_removal') {
        const serviceRequest = getServiceRequest('snow_removal');
      if (serviceRequest) {
        const serviceFreq = getServiceFrequency(customer, 'snow_removal', serviceRequest);
        if (serviceFreq === frequency) {
          return true;
        }
      }
    }
    
    return false;
  };

  // Helper function to check if a customer has a service
  // CRITICAL: Use ServiceRequest as single source of truth, not servicePricing
  // ServiceRequest is already filtered by providerId (backend) and address (frontend)
  const customerHasService = (customer: ExistingCustomer, serviceType: 'mowing' | 'fertilizing' | 'snow_removal'): boolean => {
    const customerId = customer.id || customer._id || '';
    const customerRequests = allCustomersServiceRequests[customerId] || [];
    
    // Use ServiceRequest as the single source of truth
    // This ensures providers only see services they're providing
    const hasServiceRequest = customerRequests.some((sr: any) => {
      const srCategory = sr.serviceType?.category;
      if (serviceType === 'mowing') return srCategory === 'mowing';
      if (serviceType === 'fertilizing') return srCategory === 'fertilizing';
      if (serviceType === 'snow_removal') return srCategory === 'snow_removal';
      return false;
    });
    
    return hasServiceRequest;
  };

  // Filter customers for display in All Customers tab
  const displayedCustomers = activeTab === 0
    ? customers.filter(c => {
        // Filter by frequency if not 'all'
        if (filterFrequency !== 'all') {
          if (!customerHasServiceWithFrequency(c, filterFrequency, filterServiceType !== 'all' ? filterServiceType : undefined)) {
            return false;
          }
        }
        
        // Filter by service type if not 'all'
        if (filterServiceType !== 'all') {
          const hasService = customerHasService(c, filterServiceType as 'mowing' | 'fertilizing' | 'snow_removal');
          
          if (!hasService) {
            return false;
          }
        }
        
        return true;
      })
    : customers;
  
  // Guard against missing providerId (after hooks)
  // Only show error if providerId is explicitly empty string (not just falsy during initial load)
  if (providerId === '') {
    return (
      <Box>
        <Alert severity="error">
          Provider ID is missing. Please refresh the page or contact support.
        </Alert>
      </Box>
    );
  }
  
  // If providerId is still not set, show loading state
  if (!providerId) {
    return (
      <Box>
        <CircularProgress />
        <Typography variant="body1" sx={{ mt: 2 }}>
          Loading customer data...
        </Typography>
      </Box>
    );
  }
  
  return (
    <Box>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h5" component="h2" sx={{ display: 'inline-flex', alignItems: 'center' }}>
          Customer Management
          <InfoTip title="Onboard existing customers, manage properties and services, and send invitations so they can set up payment." />
        </Typography>
        <Box display="flex" gap={1} flexWrap="wrap" alignItems="center">
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => {
              setAddCustomerDialogOpen(true);
            }}
            data-tour="demo-customers-import"
          >
            Import Current Customer
          </Button>
          <InfoTip title="Add one customer with address, services, and price, then send an invite." />
          <Button
            variant="outlined"
            startIcon={<UploadFile />}
            onClick={() => {
              setCsvImportDialogOpen(true);
              setCsvPreviewRows([]);
              setCsvImportError(null);
            }}
            data-tour="demo-customers-csv"
          >
            Import from CSV
          </Button>
          <InfoTip title="Bulk-load many customers at once, then use the $ button on each customer to set prices before sending invitations." />
        </Box>
      </Box>

      {/* Alerts - Only show when modal is not open */}
      {!addCustomerDialogOpen && error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {!addCustomerDialogOpen && success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}



      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={activeTab} onChange={(_, newValue) => setActiveTab(newValue)}>
          <Tab 
            label={
              <Box display="flex" alignItems="center" gap={1}>
                <PersonAdd />
                All Customers ({(() => {
                  // Count unique customers, not properties
                  const customerGroups: Record<string, ExistingCustomer[]> = {};
                  displayedCustomers.forEach((customer) => {
                    const linkedCustomerId = customer.linkedCustomerId as any;
                    const linkedId = linkedCustomerId
                      ? (typeof linkedCustomerId === 'object' 
                          ? (linkedCustomerId._id || linkedCustomerId.id)?.toString()
                          : linkedCustomerId?.toString())
                      : null;
                    
                    const groupKey = linkedId || 
                      `${customer.firstName}|${customer.lastName}|${customer.email || ''}|${customer.phone || ''}`;
                    
                    if (!customerGroups[groupKey]) {
                      customerGroups[groupKey] = [];
                    }
                    customerGroups[groupKey].push(customer);
                  });
                  return Object.keys(customerGroups).length;
                })()})
                <InfoTip title="Everyone you’ve imported or linked — onboarded and still pending setup." />
              </Box>
            }
          />
          <Tab 
            label={
              <Box display="flex" alignItems="center" gap={1}>
                <CheckCircle />
                Onboarded ({onboardedCustomers.length})
                <InfoTip title="Customers with platform accounts — services and payments can be managed fully." />
              </Box>
            }
          />
          <Tab 
            label={
              <Box display="flex" alignItems="center" gap={1}>
                <Warning />
                Pending Setup ({pendingCustomers.length})
                <InfoTip title="Imported but not finished signup/payment yet. Invite them so services can bill normally." />
              </Box>
            }
          />
        </Tabs>
      </Box>

      {/* Customer Lists */}
      {activeTab === 0 && (
        <Card>
          <CardContent>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="h6" sx={{ display: 'inline-flex', alignItems: 'center' }}>
                All Customers
                <InfoTip title="Full list of properties/customers. Open a row for details, services, and invites." />
              </Typography>
              <Box display="flex" gap={2} alignItems="center">
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<Refresh />}
                  onClick={() => {
                    console.log('[CUSTOMER-MGMT] Manual refresh triggered');
                    fetchCustomers();
                  }}
                >
                  Refresh
                </Button>
                <InfoTip title="Reload customers from the server after imports or schedule changes." />
                <FormControl size="small" sx={{ minWidth: 220 }}>
                  <InputLabel>Filter by Frequency</InputLabel>
                  <Select
                    value={filterFrequency}
                    onChange={(e) => setFilterFrequency(e.target.value as 'all' | 'weekly' | 'bi-weekly' | 'monthly' | 'per-instance' | 'seasonal-contract')}
                    label="Filter by Frequency"
                  >
                    <MenuItem value="all">All Frequencies</MenuItem>
                    <MenuItem value="weekly">Weekly</MenuItem>
                    <MenuItem value="bi-weekly">Bi-Weekly</MenuItem>
                    <MenuItem value="monthly">Monthly</MenuItem>
                    <MenuItem value="per-instance">Per Instance</MenuItem>
                    <MenuItem value="seasonal-contract">Seasonal Contract</MenuItem>
                  </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: 200 }}>
                  <InputLabel>Filter by Service</InputLabel>
                  <Select
                    value={filterServiceType}
                    onChange={(e) => setFilterServiceType(e.target.value as 'all' | 'mowing' | 'fertilizing' | 'snow_removal')}
                    label="Filter by Service"
                  >
                    <MenuItem value="all">All Services</MenuItem>
                    <MenuItem value="mowing">Mowing</MenuItem>
                    <MenuItem value="fertilizing">Fertilizing</MenuItem>
                    <MenuItem value="snow_removal">Snow Removal</MenuItem>
                  </Select>
                </FormControl>
              </Box>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              View and manage all your customers, including imported and onboarded customers.
            </Typography>
            
            {displayedCustomers.length === 0 ? (
              <Box textAlign="center" py={4}>
                <PersonAdd sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                <Typography variant="h6" gutterBottom>
                  No Customers Yet
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Import your current customers to get started.
                </Typography>
              </Box>
            ) : (
              <TableContainer component={Paper} data-tour="demo-customers-table">
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>
                        Customer Name
                        <InfoTip title="Customer identity. Multiple properties for the same person are grouped together." />
                      </TableCell>
                      <TableCell>
                        Address
                        <InfoTip title="Service property address used for scheduling and routing." />
                      </TableCell>
                      <TableCell>
                        Services
                        <InfoTip title="Active service types and pricing for this property." />
                      </TableCell>
                      <TableCell>
                        Last Service
                        <InfoTip title="Most recent completed service date for this property." />
                      </TableCell>
                      <TableCell>
                        Next Service
                        <InfoTip title="Next scheduled or recurring date. Edit via Scheduling or the row actions." />
                      </TableCell>
                      <TableCell>
                        Actions
                        <InfoTip title="View details, edit services/pricing, send invites, or manage the customer." />
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(() => {
                      // Group customers by customer identity (linkedCustomerId, or name/email/phone for unlinked)
                      const customerGroups: Record<string, ExistingCustomer[]> = {};
                      
                      console.log('[CUSTOMER-TABLE] Total displayedCustomers:', displayedCustomers.length);
                      displayedCustomers.forEach((customer) => {
                        // Create a unique key for customer grouping
                        const linkedCustomerId = customer.linkedCustomerId as any;
                        const linkedId = linkedCustomerId
                          ? (typeof linkedCustomerId === 'object' 
                              ? (linkedCustomerId._id || linkedCustomerId.id)?.toString()
                              : linkedCustomerId?.toString())
                          : null;
                        
                        // Group by linkedCustomerId if available, otherwise by name+email+phone
                        const groupKey = linkedId || 
                          `${customer.firstName}|${customer.lastName}|${customer.email || ''}|${customer.phone || ''}`;
                        
                        if (!customerGroups[groupKey]) {
                          customerGroups[groupKey] = [];
                        }
                        customerGroups[groupKey].push(customer);
                        
                        console.log('[CUSTOMER-TABLE] Customer:', {
                          name: `${customer.firstName} ${customer.lastName}`,
                          address: `${customer.address.street}, ${customer.address.city}`,
                          linkedId: linkedId,
                          groupKey: groupKey,
                          customerId: customer.id || customer._id
                        });
                      });
                      
                      console.log('[CUSTOMER-TABLE] Customer groups:', Object.keys(customerGroups).map(key => ({
                        groupKey: key,
                        count: customerGroups[key].length,
                        addresses: customerGroups[key].map(c => `${c.address.street}, ${c.address.city}`)
                      })));
                      
                      // Flatten groups into rows, with spacing between customer groups
                      const rows: Array<{ customer: ExistingCustomer; isFirstInGroup: boolean; isLastInGroup: boolean; groupKey: string }> = [];
                      const groupKeys = Object.keys(customerGroups);
                      
                      groupKeys.forEach((groupKey, groupIndex) => {
                        const groupCustomers = customerGroups[groupKey];
                        groupCustomers.forEach((customer, customerIndex) => {
                          rows.push({
                            customer,
                            isFirstInGroup: customerIndex === 0,
                            isLastInGroup: customerIndex === groupCustomers.length - 1,
                            groupKey
                          });
                        });
                      });
                      
                      return rows.map((row, rowIndex) => {
                        const { customer, isFirstInGroup, isLastInGroup, groupKey } = row;
                        const customerFreq = getCustomerFrequency(customer);
                        const frequencyLabels: Record<string, string> = {
                          'weekly': 'Weekly',
                          'bi-weekly': 'Bi-Weekly',
                          'monthly': 'Monthly',
                          'per-instance': 'Per Instance',
                          'seasonal-contract': 'Seasonal Contract'
                        };
                        const frequencyColors: Record<string, 'primary' | 'info' | 'success' | 'warning' | 'secondary' | 'default'> = {
                          'weekly': 'primary',
                          'bi-weekly': 'info',
                          'monthly': 'success',
                          'per-instance': 'warning',
                          'seasonal-contract': 'secondary'
                        };
                        
                        // Check if this is the last row of a customer group (add spacing after)
                        const isLastRowOfGroup = isLastInGroup;
                        const nextRow = rows[rowIndex + 1];
                        const shouldAddSpacing = isLastRowOfGroup && nextRow && nextRow.groupKey !== groupKey;
                        
                        return (
                          <React.Fragment key={`${customer.id}-${rowIndex}`}>
                            <TableRow 
                              sx={{ 
                                ...(shouldAddSpacing && { borderBottom: '2px solid', borderBottomColor: 'divider', mb: 2 }),
                                // Add spacing between customer groups
                                ...(isFirstInGroup && rowIndex > 0 ? { 
                                  borderTop: '2px solid', 
                                  borderTopColor: 'divider',
                                  '& td': { pt: 2 }
                                } : {}),
                                // Add slight spacing for additional addresses
                                ...(!isFirstInGroup ? { 
                                  '& td': { pt: 1, pb: 1 }
                                } : {})
                              }}
                            >
                              <TableCell>
                                <Box display="flex" alignItems="center" gap={1}>
                                  {isFirstInGroup ? (
                                    <>
                                      <Typography variant="body2" fontWeight="medium">
                                        {customer.firstName} {customer.lastName}
                                      </Typography>
                                      {customer.isDeleted && (
                                        <Chip 
                                          label="Deleted" 
                                          size="small" 
                                          color="error" 
                                          sx={{ fontSize: '0.7rem', height: '20px' }}
                                        />
                                      )}
                                      {customer.isOnHold && !customer.isDeleted && (
                                        <Tooltip title={customer.onHoldReason || "No payment method on file"}>
                                          <Warning color="error" sx={{ fontSize: 18 }} />
                                        </Tooltip>
                                      )}
                                    </>
                                  ) : (
                                    <Box sx={{ pl: 3 }}>
                                      {/* Additional address - indented under customer name */}
                                    </Box>
                                  )}
                                </Box>
                              </TableCell>
                              <TableCell>
                                {customer.address.street}, {customer.address.city}, {customer.address.state} {customer.address.zipCode}
                              </TableCell>
                        <TableCell>
                          <Box display="flex" flexDirection="column" gap={0.5}>
                            {(() => {
                              const customerId = customer.id || customer._id || '';
                              // For multi-property customers, we need to match service requests by address, not just customer ID
                              // Get all service requests for this customer (may include multiple properties)
                              const allCustomerRequests = allCustomersServiceRequests[customerId] || [];
                              
                              // Enhanced logging for Robert Romero
                              if (customer.firstName === 'Robert' && customer.lastName === 'Romero') {
                                console.log('[CUSTOMER-TABLE] Property A - allCustomerRequests:', {
                                  customerId: customerId,
                                  customerAddress: customer.address ? `${customer.address.street}, ${customer.address.zipCode}` : 'No address',
                                  totalRequests: allCustomerRequests.length,
                                  requests: allCustomerRequests.map((sr: any) => ({
                                    id: sr._id || sr.id,
                                    category: sr.serviceType?.category,
                                    status: sr.status,
                                    srAddress: sr.address ? `${sr.address.street}, ${sr.address.zipCode}` : 'No address',
                                    customerId: sr.customerId?._id || sr.customerId?.id || sr.customerId
                                  }))
                                });
                              }
                              
                              // Filter to only service requests for THIS specific address/property
                              const customerRequestsForThisProperty = allCustomerRequests.filter((sr: any) => {
                                const srAddress = sr.address;
                                const customerAddress = customer.address;
                                if (!srAddress || !customerAddress) {
                                  if (customer.firstName === 'Robert' && customer.lastName === 'Romero') {
                                    console.log('[CUSTOMER-TABLE] Address filter - missing address:', {
                                      customerId: customerId,
                                      hasSrAddress: !!srAddress,
                                      hasCustomerAddress: !!customerAddress,
                                      srCategory: sr.serviceType?.category
                                    });
                                  }
                                  return false;
                                }
                                
                                // Match by street and zipCode (most reliable)
                                const srStreet = (srAddress.street || '').trim().toLowerCase();
                                const srZip = (srAddress.zipCode || '').trim();
                                const customerStreet = (customerAddress.street || '').trim().toLowerCase();
                                const customerZip = (customerAddress.zipCode || '').trim();
                                const sameStreet = srStreet === customerStreet;
                                const sameZip = srZip === customerZip;
                                
                                // Enhanced logging for Robert Romero
                                if (customer.firstName === 'Robert' && customer.lastName === 'Romero') {
                                  console.log('[CUSTOMER-TABLE] Address filter check:', {
                                    customerId: customerId,
                                    customerAddress: `${customerStreet}, ${customerZip}`,
                                    srAddress: `${srStreet}, ${srZip}`,
                                    srCategory: sr.serviceType?.category,
                                    sameStreet,
                                    sameZip,
                                    matches: sameStreet && sameZip
                                  });
                                }
                                
                                return sameStreet && sameZip;
                              });
                              
                              // CRITICAL: Only use property-specific requests - NEVER fallback to allCustomerRequests
                              // This ensures multi-property customers only show services for THIS property
                              const customerRequests = customerRequestsForThisProperty;
                              
                              // Debug logging for all customers with service requests
                              if (customerRequests.length > 0) {
                                console.log('[CUSTOMER-TABLE] Service requests for customer:', {
                                  customerId: customerId,
                                  customerName: `${customer.firstName} ${customer.lastName}`,
                                  linkedCustomerId: customer.linkedCustomerId,
                                  requestCount: customerRequests.length,
                                  requests: customerRequests.map((sr: any) => ({
                                    id: sr._id || sr.id,
                                    category: sr.serviceType?.category,
                                    scheduledDate: sr.scheduledDate,
                                    completedDate: sr.completedDate,
                                    status: sr.status,
                                    customerId: sr.customerId?._id || sr.customerId?.id || sr.customerId,
                                    serviceTypeFull: sr.serviceType
                                  }))
                                });
                              } else if (customer.linkedCustomerId) {
                                console.log('[CUSTOMER-TABLE] No service requests found for customer:', {
                                  customerId: customerId,
                                  customerName: `${customer.firstName} ${customer.lastName}`,
                                  linkedCustomerId: customer.linkedCustomerId,
                                  allCustomersServiceRequestsKeys: Object.keys(allCustomersServiceRequests),
                                  totalRequestsInState: Object.values(allCustomersServiceRequests).flat().length
                                });
                              }
                              
                              // Helper to get service request for a specific service type
                              // Use customerRequestsForThisProperty to ensure we only match services for this address
                              const getServiceRequest = (category: string) => {
                                // Log the actual category values as strings for easy debugging
                                const actualCategories = customerRequestsForThisProperty.map((sr: any) => {
                                  const cat = sr.serviceType?.category;
                                  return cat ? String(cat) : 'undefined';
                                });
                                console.log(`[CUSTOMER-TABLE] Searching for category "${category}". Available categories:`, actualCategories.join(', '));
                                
                                // First try to find exact match
                                let found = customerRequestsForThisProperty.find((sr: any) => {
                                  const srCategory = sr.serviceType?.category;
                                  const matches = srCategory === category;
                                  
                                  // Log each comparison
                                  if (customerRequests.length > 0) {
                                    console.log(`[CUSTOMER-TABLE] Comparing: "${srCategory}" === "${category}" = ${matches}`);
                                  }
                                  
                                  return matches;
                                });
                                
                                // If no exact match found and we have a service request with category "other",
                                // use it as a fallback (this handles incorrectly categorized service requests)
                                if (!found && customerRequests.length > 0) {
                                  const otherCategoryRequest = customerRequests.find((sr: any) => {
                                    return sr.serviceType?.category === 'other';
                                  });
                                  
                                  if (otherCategoryRequest) {
                                    console.log(`[CUSTOMER-TABLE] No exact match for "${category}", using fallback "other" category service request`);
                                    found = otherCategoryRequest;
                                  }
                                }
                                
                                // Debug logging for all lookups - show full service request details
                                console.log('[CUSTOMER-TABLE] Looking for category:', category, {
                                  customerId: customerId,
                                  customerName: `${customer.firstName} ${customer.lastName}`,
                                  found: !!found,
                                  requestCount: customerRequests.length,
                                  actualCategoryValues: actualCategories,
                                  allCategories: customerRequests.map((sr: any) => ({
                                    category: sr.serviceType?.category,
                                    categoryString: String(sr.serviceType?.category || 'undefined'),
                                    categoryType: typeof sr.serviceType?.category,
                                    id: sr._id || sr.id,
                                    scheduledDate: sr.scheduledDate,
                                    serviceTypeFull: sr.serviceType
                                  })),
                                  allServiceRequests: customerRequests.map((sr: any) => ({
                                    id: sr._id || sr.id,
                                    category: sr.serviceType?.category,
                                    categoryString: String(sr.serviceType?.category || 'undefined'),
                                    serviceType: sr.serviceType,
                                    scheduledDate: sr.scheduledDate,
                                    status: sr.status,
                                    fullServiceRequest: sr
                                  })),
                                  foundRequest: found ? {
                                    id: found._id || found.id,
                                    category: found.serviceType?.category,
                                    scheduledDate: found.scheduledDate,
                                    completedDate: found.completedDate,
                                    status: found.status,
                                    serviceType: found.serviceType
                                  } : null,
                                  searchCategory: category,
                                  searchCategoryType: typeof category
                                });
                                
                                return found;
                              };
                              
                              // Helper to get last completed date for a service type
                              // Priority: 1) Most recent serviceRequest.completedDate for this specific service, 2) customer.lastServiceDate (fallback)
                              const getLastServiceDate = (category: string) => {
                                console.log(`[LAST-SERVICE-DATE] Getting last service date for ${category}`, {
                                  customerName: `${customer.firstName} ${customer.lastName}`,
                                  totalRequests: customerRequests.length,
                                  allStatuses: customerRequests.map((sr: any) => ({
                                    category: sr.serviceType?.category,
                                    status: sr.status,
                                    completedDate: sr.completedDate
                                  }))
                                });
                                
                                // First, look for ALL completed service requests for this specific service category
                                // CRITICAL: Include both 'completed' status AND 'scheduled' status with completedDate
                                // After auto-rescheduling, status becomes 'scheduled' but completedDate is preserved
                                const completedRequests = customerRequests.filter((sr: any) => {
                                  const srCategory = sr.serviceType?.category;
                                  const matches = srCategory === category;
                                  const hasCompletedDate = !!sr.completedDate;
                                  const isCompleted = sr.status === 'completed' && hasCompletedDate;
                                  const isScheduledWithCompletedDate = sr.status === 'scheduled' && hasCompletedDate;
                                  const isCompletedOrScheduled = isCompleted || isScheduledWithCompletedDate;
                                  console.log(`[LAST-SERVICE-DATE] Checking request:`, {
                                    category: srCategory,
                                    matches,
                                    status: sr.status,
                                    hasCompletedDate,
                                    isCompleted,
                                    isScheduledWithCompletedDate,
                                    isCompletedOrScheduled
                                  });
                                  return matches && isCompletedOrScheduled;
                                });
                                
                                console.log(`[LAST-SERVICE-DATE] Found ${completedRequests.length} completed requests for ${category}`);
                                
                                // If we have completed requests, get the most recent one (by completedDate)
                                if (completedRequests.length > 0) {
                                  const mostRecent = completedRequests.reduce((latest, current) => {
                                    if (!latest.completedDate) return current;
                                    if (!current.completedDate) return latest;
                                    return new Date(current.completedDate) > new Date(latest.completedDate) ? current : latest;
                                  });
                                  
                                  console.log(`[LAST-SERVICE-DATE] Most recent completed date:`, mostRecent.completedDate);
                                  
                                  if (mostRecent?.completedDate) {
                                    // Normalize completedDate to UTC midnight (date-only) to avoid timezone display issues
                                    // IMPORTANT: completedDate should already be stored as UTC midnight representing a calendar date
                                    // Extract UTC date components directly (don't convert to Central Time first)
                                    const completedDate = new Date(mostRecent.completedDate);
                                    
                                    // Extract UTC date components directly - this preserves the calendar date
                                    const year = completedDate.getUTCFullYear();
                                    const month = completedDate.getUTCMonth();
                                    const day = completedDate.getUTCDate();
                                    
                                    // Create a UTC date at midnight for this calendar date
                                    const normalizedDate = new Date(Date.UTC(year, month, day, 0, 0, 0, 0));
                                    
                                    console.log(`[LAST-SERVICE-DATE] Normalized date:`, {
                                      original: mostRecent.completedDate,
                                      originalISO: new Date(mostRecent.completedDate).toISOString(),
                                      utcYear: year,
                                      utcMonth: month,
                                      utcDay: day,
                                      normalized: normalizedDate.toISOString()
                                    });
                                    return normalizedDate;
                                  }
                                }
                                
                                // Fallback: check any service request (not just completed) for this category
                                const serviceRequest = getServiceRequest(category);
                                if (serviceRequest?.completedDate) {
                                  // Normalize completedDate to UTC midnight (date-only) to avoid timezone display issues
                                  const completedDate = new Date(serviceRequest.completedDate);
                                  const normalizedDate = new Date(Date.UTC(
                                    completedDate.getUTCFullYear(),
                                    completedDate.getUTCMonth(),
                                    completedDate.getUTCDate(),
                                    0, 0, 0, 0
                                  ));
                                  console.log(`[LAST-SERVICE-DATE] Using fallback completedDate from serviceRequest (normalized):`, {
                                    original: serviceRequest.completedDate,
                                    normalized: normalizedDate.toISOString()
                                  });
                                  return normalizedDate;
                                }
                                
                                // IMPORTANT: Don't use customer.lastServiceDate as fallback
                                // It's a single field that can't represent multiple services correctly
                                // For customers with multiple services, customer.lastServiceDate might be for a different service
                                // Return null if no completed service request found for this specific category
                                console.log(`[LAST-SERVICE-DATE] No completed service request found for ${category}`);
                                return null;
                              };
                              
                              // Use shared utility function for calculating next service dates (SINGLE SOURCE OF TRUTH)
                              const getNextServiceDate = (category: string) => {
                                const serviceRequest = getServiceRequest(category);
                                return getNextServiceDateUtil(
                                  customer,
                                  category as 'mowing' | 'fertilizing' | 'snow_removal',
                                  serviceRequest,
                                  customerRequests
                                );
                              };
                              
                              const services: Array<{
                                name: string;
                                key: string;
                                category: string;
                                lastDate: Date | string | null;
                                nextDate: Date | string | null;
                              }> = [];
                              
                              // SINGLE SOURCE OF TRUTH: Only show service if there's a ServiceRequest for THIS property address
                              // ServiceRequest is filtered by providerId (backend) and address (frontend), so it's the ultimate source of truth
                              // CRITICAL: Do NOT check servicePricing - it may contain services from other providers
                              
                              // Check mowing service - use ServiceRequest ONLY
                              if (customerRequests.some((sr: any) => sr.serviceType?.category === 'mowing')) {
                                services.push({
                                  name: 'Mowing',
                                  key: 'lawnMowingTrimming',
                                  category: 'mowing' as const,
                                  lastDate: getLastServiceDate('mowing'),
                                  nextDate: getNextServiceDate('mowing')
                                });
                              }
                              
                              // Check fertilizing service - use ServiceRequest ONLY
                              if (customerRequests.some((sr: any) => sr.serviceType?.category === 'fertilizing')) {
                                services.push({
                                  name: 'Fertilizer',
                                  key: 'weedControlFertilizing',
                                  category: 'fertilizing' as const,
                                  lastDate: getLastServiceDate('fertilizing'),
                                  nextDate: getNextServiceDate('fertilizing')
                                });
                              }
                              
                              // Check snow removal service - use ServiceRequest ONLY
                              if (customerRequests.some((sr: any) => sr.serviceType?.category === 'snow_removal')) {
                                services.push({
                                  name: 'Snow',
                                  key: 'snowRemoval',
                                  category: 'snow_removal' as const,
                                  lastDate: getLastServiceDate('snow_removal'),
                                  nextDate: getNextServiceDate('snow_removal')
                                });
                              }
                              
                              if (services.length === 0) {
                                return (
                                  <Typography variant="body2" color="text.secondary">
                                    No services
                                  </Typography>
                                );
                              }
                              
                              return services.map((service) => {
                                // SINGLE SOURCE OF TRUTH: ServiceRequest.frequency
                                // Match service request by address for multi-property customers
                                const customerAddress = customer.address;
                                const serviceRequest = customerRequests.find((sr: any) => {
                                  const matchesCategory = sr.serviceType?.category === service.category;
                                  if (!matchesCategory) return false;
                                  
                                  // Match by address for multi-property customers
                                  // CRITICAL: Normalize addresses (trim, lowercase) to handle format differences
                                  const srAddress = sr.address;
                                  if (srAddress && customerAddress) {
                                    const srStreet = (srAddress.street || '').trim().toLowerCase();
                                    const srZip = (srAddress.zipCode || '').trim();
                                    const customerStreet = (customerAddress.street || '').trim().toLowerCase();
                                    const customerZip = (customerAddress.zipCode || '').trim();
                                    const sameStreet = srStreet === customerStreet;
                                    const sameZip = srZip === customerZip;
                                    return sameStreet && sameZip;
                                  }
                                  
                                  // If no address match, use first one found (fallback)
                                  return true;
                                });
                                
                                // SINGLE SOURCE OF TRUTH: Use getServiceFrequency helper function
                                // This function handles all the logic for extracting frequency from:
                                // - serviceRequest.frequency
                                // - serviceRequest.acceptedBid.servicePricing (for seasonal contracts)
                                // - customer.servicePricing (for onboarded customers)
                                // This ensures consistent behavior across the entire platform
                                // Type assertion: service.category is guaranteed to be one of the valid categories
                                // Using 'as const' on category properties ensures TypeScript infers literal types
                                const serviceCategory = service.category as 'mowing' | 'fertilizing' | 'snow_removal';
                                const displayFrequency = getServiceFrequency(customer, serviceCategory, serviceRequest);
                                
                                const frequencyLabels: Record<string, string> = {
                                  'weekly': 'Weekly',
                                  'bi-weekly': 'Bi-Weekly',
                                  'monthly': 'Monthly',
                                  'per-instance': 'Per Instance',
                                  'seasonal-contract': 'Seasonal Contract'
                                };
                                
                                // CRITICAL: Always show the actual frequency badge (weekly/bi-weekly/monthly/per-instance)
                                // For seasonal contracts, displayFrequency should already be the actual serviceFrequency
                                // Don't show "Seasonal Contract" as the badge - show the actual frequency like other services
                                // Show badge for all valid frequencies including per-instance
                                const shouldShowBadge = displayFrequency && 
                                  displayFrequency !== 'seasonal-contract' && 
                                  (displayFrequency === 'weekly' || 
                                   displayFrequency === 'bi-weekly' || 
                                   displayFrequency === 'monthly' || 
                                   displayFrequency === 'per-instance');
                                
                                return (
                                  <Box key={service.key} display="flex" alignItems="center" gap={0.5} sx={{ mb: 0.5 }}>
                                    <Chip label={service.name} size="small" sx={{ mr: 0.5 }} />
                                    {shouldShowBadge && (
                                      <Chip 
                                        label={frequencyLabels[displayFrequency] || displayFrequency}
                                        size="small"
                                        color="default"
                                        variant="outlined"
                                        sx={{ 
                                          height: 20, 
                                          fontSize: '0.65rem'
                                        }}
                                      />
                                    )}
                                  </Box>
                                );
                              });
                            })()}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box display="flex" flexDirection="column" gap={0.5}>
                            {(() => {
                              const customerId = customer.id || customer._id || '';
                              const allCustomerRequests = allCustomersServiceRequests[customerId] || [];
                              
                              // IMPORTANT: Filter service requests by address for multi-property customers
                              // Only include service requests that match this property's address
                              const customerAddress = customer.address;
                              const customerRequests = allCustomerRequests.filter((sr: any) => {
                                if (!customerAddress || !sr.address) return true; // If no address, include it (fallback)
                                
                                const srStreet = (sr.address.street || '').trim().toLowerCase();
                                const srZipCode = (sr.address.zipCode || '').trim();
                                const customerStreet = (customerAddress.street || '').trim().toLowerCase();
                                const customerZipCode = (customerAddress.zipCode || '').trim();
                                
                                return srStreet === customerStreet && srZipCode === customerZipCode;
                              });
                              
                              const getServiceRequest = (category: string) => {
                                return customerRequests.find((sr: any) => sr.serviceType?.category === category);
                              };
                              
                              const getLastServiceDate = (category: string) => {
                                // Use the filtered customerRequests (already filtered by address above)
                                // First, look for ALL completed service requests for this specific service category
                                // CRITICAL: Include both 'completed' status AND 'scheduled' status with completedDate
                                // After auto-rescheduling, status becomes 'scheduled' but completedDate is preserved
                                const completedRequests = customerRequests.filter((sr: any) => {
                                  const srCategory = sr.serviceType?.category;
                                  const matches = srCategory === category;
                                  const hasCompletedDate = !!sr.completedDate;
                                  return matches && hasCompletedDate && (sr.status === 'completed' || sr.status === 'scheduled');
                                });
                                
                                // If we have completed requests, get the most recent one (by completedDate)
                                if (completedRequests.length > 0) {
                                  const mostRecent = completedRequests.reduce((latest: any, current: any) => {
                                    if (!latest.completedDate) return current;
                                    if (!current.completedDate) return latest;
                                    return new Date(current.completedDate) > new Date(latest.completedDate) ? current : latest;
                                  });
                                  
                                  if (mostRecent?.completedDate) {
                                    // Normalize completedDate to UTC midnight (date-only) to avoid timezone display issues
                                    // IMPORTANT: completedDate should already be stored as UTC midnight representing a calendar date
                                    // Extract UTC date components directly (don't convert to Central Time first)
                                    const completedDate = new Date(mostRecent.completedDate);
                                    
                                    // Extract UTC date components directly - this preserves the calendar date
                                    const year = completedDate.getUTCFullYear();
                                    const month = completedDate.getUTCMonth();
                                    const day = completedDate.getUTCDate();
                                    
                                    // Create a UTC date at midnight for this calendar date
                                    const normalizedDate = new Date(Date.UTC(year, month, day, 0, 0, 0, 0));
                                    return normalizedDate;
                                  }
                                }
                                
                                // Fallback: check any service request (not just completed) for this category
                                const serviceRequest = getServiceRequest(category);
                                if (serviceRequest?.completedDate) {
                                  // Normalize completedDate to UTC midnight (date-only) to avoid timezone display issues
                                  // Extract UTC date components directly
                                  const completedDate = new Date(serviceRequest.completedDate);
                                  const normalizedDate = new Date(Date.UTC(
                                    completedDate.getUTCFullYear(),
                                    completedDate.getUTCMonth(),
                                    completedDate.getUTCDate(),
                                    0, 0, 0, 0
                                  ));
                                  return normalizedDate;
                                }
                                
                                // IMPORTANT: Don't use customer.lastServiceDate as fallback
                                // It's a single field that can't represent multiple services correctly
                                // For customers with multiple services, customer.lastServiceDate might be for a different service
                                // Return null if no completed service request found for this specific category
                                return null;
                              };
                              
                              const services: Array<{ category: string; lastDate: Date | string | null }> = [];
                              
                              // CRITICAL: Use ServiceRequest as single source of truth, not servicePricing
                              // ServiceRequest is already filtered by providerId (backend) and address (frontend)
                              
                              // Check mowing service - use ServiceRequest ONLY
                              if (customerRequests.some((sr: any) => sr.serviceType?.category === 'mowing')) {
                                services.push({
                                  category: 'mowing',
                                  lastDate: getLastServiceDate('mowing')
                                });
                              }
                              
                              // Check fertilizing service - use ServiceRequest ONLY
                              if (customerRequests.some((sr: any) => sr.serviceType?.category === 'fertilizing')) {
                                services.push({
                                  category: 'fertilizing',
                                  lastDate: getLastServiceDate('fertilizing')
                                });
                              }
                              
                              // Check snow removal service - use ServiceRequest ONLY
                              if (customerRequests.some((sr: any) => sr.serviceType?.category === 'snow_removal')) {
                                services.push({
                                  category: 'snow_removal',
                                  lastDate: getLastServiceDate('snow_removal')
                                });
                              }
                              
                              if (services.length === 0) {
                                return <Typography variant="body2" color="text.secondary">N/A</Typography>;
                              }
                              
                              return services.map((service, index) => (
                                <Typography key={index} variant="body2" sx={{ minHeight: '24px', display: 'flex', alignItems: 'center' }}>
                                  {service.lastDate ? formatDateMMDDYYYY(service.lastDate.toString()) : 'N/A'}
                                </Typography>
                              ));
                            })()}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box display="flex" flexDirection="column" gap={0.5}>
                            {(() => {
                              const customerId = customer.id || customer._id || '';
                              const allCustomerRequests = allCustomersServiceRequests[customerId] || [];
                              
                              // IMPORTANT: Filter service requests by address for multi-property customers
                              // Only include service requests that match this property's address
                              const customerAddress = customer.address;
                              const customerRequests = allCustomerRequests.filter((sr: any) => {
                                if (!customerAddress || !sr.address) return true; // If no address, include it (fallback)
                                
                                const srStreet = (sr.address.street || '').trim().toLowerCase();
                                const srZipCode = (sr.address.zipCode || '').trim();
                                const customerStreet = (customerAddress.street || '').trim().toLowerCase();
                                const customerZipCode = (customerAddress.zipCode || '').trim();
                                
                                return srStreet === customerStreet && srZipCode === customerZipCode;
                              });
                              
                              const getServiceRequest = (category: string) => {
                                const best = selectBestServiceRequestForCategory(
                                  customerRequests,
                                  category as 'mowing' | 'fertilizing' | 'snow_removal'
                                );
                                if (best) return best;
                                // Legacy fallback for mis-categorized "other" SRs
                                return customerRequests.find((sr: any) => sr.serviceType?.category === 'other') || null;
                              };
                              
                              // Use shared utility function for calculating next service dates (SINGLE SOURCE OF TRUTH)
                              const getNextServiceDate = (category: string) => {
                                const serviceRequest = getServiceRequest(category);
                                return getNextServiceDateUtil(
                                  customer,
                                  category as 'mowing' | 'fertilizing' | 'snow_removal',
                                  serviceRequest,
                                  customerRequests
                                );
                              };
                              
                              const services: Array<{ category: string; nextDate: Date | string | null }> = [];
                              
                              // CRITICAL: Use ServiceRequest as single source of truth, not servicePricing
                              // ServiceRequest is already filtered by providerId (backend) and address (frontend)
                              // This prevents duplicate entries and ensures provider isolation
                              
                              // Check mowing service - use ServiceRequest ONLY
                              if (customerRequests.some((sr: any) => sr.serviceType?.category === 'mowing')) {
                                services.push({
                                  category: 'mowing',
                                  nextDate: getNextServiceDate('mowing')
                                });
                              }
                              
                              // Check fertilizing service - use ServiceRequest ONLY
                              if (customerRequests.some((sr: any) => sr.serviceType?.category === 'fertilizing')) {
                                services.push({
                                  category: 'fertilizing',
                                  nextDate: getNextServiceDate('fertilizing')
                                });
                              }
                              
                              // Check snow removal service - use ServiceRequest ONLY
                              if (customerRequests.some((sr: any) => sr.serviceType?.category === 'snow_removal')) {
                                services.push({
                                  category: 'snow_removal',
                                  nextDate: getNextServiceDate('snow_removal')
                                });
                              }
                              
                              if (services.length === 0) {
                                return <Typography variant="body2" color="text.secondary">N/A</Typography>;
                              }
                              
                              return services.map((service, index) => {
                                // Get service frequency to determine display text
                                let serviceFrequency: string | null = null;
                                if (service.category === 'mowing' && customer.servicePricing?.lawnMowingTrimming?.frequency) {
                                  serviceFrequency = customer.servicePricing.lawnMowingTrimming.frequency;
                                } else if (service.category === 'fertilizing' && customer.servicePricing?.weedControlFertilizing?.frequency) {
                                  serviceFrequency = customer.servicePricing.weedControlFertilizing.frequency;
                                } else if (service.category === 'snow_removal' && customer.servicePricing?.snowRemoval?.frequency) {
                                  serviceFrequency = customer.servicePricing.snowRemoval.frequency;
                                }
                                
                                // Also check serviceRequest for frequency
                                const serviceRequest = getServiceRequest(service.category);
                                if (serviceRequest?.frequency) {
                                  serviceFrequency = serviceRequest.frequency;
                                }
                                
                                // For seasonal contracts, check the accepted bid's serviceFrequency
                                if (serviceFrequency === 'seasonal-contract' && serviceRequest?.acceptedBid) {
                                  const bid = serviceRequest.acceptedBid;
                                  if (bid.servicePricing && Array.isArray(bid.servicePricing)) {
                                    // Find the service pricing that matches this service type
                                    const matchingServicePricing = bid.servicePricing.find((sp: any) => {
                                      if (service.category === 'mowing') return sp.service === 'lawnMowingTrimming' || sp.serviceName?.toLowerCase().includes('mowing');
                                      if (service.category === 'fertilizing') return sp.service === 'weedControlFertilizing' || sp.serviceName?.toLowerCase().includes('fertiliz') || sp.serviceName?.toLowerCase().includes('weed');
                                      if (service.category === 'snow_removal') return sp.service === 'snowRemoval' || sp.serviceName?.toLowerCase().includes('snow');
                                      return false;
                                    });
                                    
                                    if (matchingServicePricing?.seasonalContractDetails?.serviceFrequency) {
                                      serviceFrequency = matchingServicePricing.seasonalContractDetails.serviceFrequency;
                                    }
                                  }
                                }
                                
                                let dateDisplay = 'N/A';
                                
                                // For per-instance services with no date, show "Per Instance"
                                if (!service.nextDate && serviceFrequency === 'per-instance') {
                                  dateDisplay = 'Per Instance';
                                } else if (service.nextDate) {
                                  try {
                                    // Handle different date formats
                                    const date = service.nextDate instanceof Date 
                                      ? service.nextDate 
                                      : new Date(service.nextDate);
                                    
                                    if (!isNaN(date.getTime())) {
                                      // Pass the Date object directly to formatDateMMDDYYYY
                                      // It will extract UTC date components correctly
                                      dateDisplay = formatDateMMDDYYYY(date);
                                    } else {
                                      // Try direct string formatting if ISO parsing fails
                                      dateDisplay = formatDateMMDDYYYY(service.nextDate.toString());
                                    }
                                  } catch (e) {
                                    console.error('[CUSTOMER-TABLE] Error formatting next service date:', e, service.nextDate);
                                    dateDisplay = serviceFrequency === 'per-instance' ? 'Per Instance' : 'N/A';
                                  }
                                } else {
                                  if (serviceFrequency === 'per-instance') {
                                    dateDisplay = 'Per Instance';
                                  }
                                }
                                
                                return (
                                  <Typography key={index} variant="body2" sx={{ minHeight: '24px', display: 'flex', alignItems: 'center' }}>
                                    {dateDisplay}
                                  </Typography>
                                );
                              });
                            })()}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box display="flex" gap={0.5} alignItems="center">
                            <Tooltip title="View Details">
                              <IconButton 
                                size="small"
                                onClick={async () => {
                                  // Capture customer ID immediately to avoid closure issues
                                  const clickedCustomerId = customer.id || customer._id || '';
                                  const clickedCustomerName = `${customer.firstName} ${customer.lastName}`;
                                  
                                  console.log('[VIEW-DETAILS] Button clicked for customer:', {
                                    customerId: clickedCustomerId,
                                    customerName: clickedCustomerName,
                                    tableNextServiceDate: customer.nextServiceDate,
                                    tableNextServiceDateType: typeof customer.nextServiceDate
                                  });
                                  
                                  // Create a copy of the customer object to avoid reference issues
                                  const customerCopy = { ...customer };
                                  
                                  // Use the customer from the table directly (it should already be fresh)
                                  // But also refresh to ensure we have the absolute latest
                                  try {
                                    const response = await apiService.get('/route-optimization/existing-customers');
                                    if (response.success && Array.isArray(response.data)) {
                                      // Use clickedCustomerId consistently to find the right customer
                                      const freshCustomer = response.data.find((c: ExistingCustomer) => 
                                        (c.id || c._id || '').toString() === clickedCustomerId.toString()
                                      );
                                      if (freshCustomer && (freshCustomer.id || freshCustomer._id || '').toString() === clickedCustomerId.toString()) {
                                        console.log('[VIEW-DETAILS] Fresh customer data from API:', {
                                          clickedCustomerId,
                                          foundCustomerId: freshCustomer.id || freshCustomer._id,
                                          customerName: `${freshCustomer.firstName} ${freshCustomer.lastName}`,
                                          nextServiceDate: freshCustomer.nextServiceDate,
                                          nextServiceDateType: typeof freshCustomer.nextServiceDate,
                                          nextServiceDateFormatted: freshCustomer.nextServiceDate ? formatDateMMDDYYYY(freshCustomer.nextServiceDate.toString()) : 'N/A'
                                        });
                                        setCustomerDetailsDialog({ 
                                          open: true, 
                                          customer: freshCustomer 
                                        });
                                      } else {
                                        console.warn('[VIEW-DETAILS] Customer not found in API response, using table data', {
                                          clickedCustomerId,
                                          searchedIn: response.data.length,
                                          foundCustomerId: freshCustomer?.id || freshCustomer?._id
                                        });
                                        setCustomerDetailsDialog({ 
                                          open: true, 
                                          customer: customerCopy 
                                        });
                                      }
                                    } else {
                                      console.warn('[VIEW-DETAILS] API response invalid, using table data');
                                      setCustomerDetailsDialog({ 
                                        open: true, 
                                        customer: customerCopy 
                                      });
                                    }
                                  } catch (err) {
                                    console.error('[VIEW-DETAILS] Error fetching fresh customer data:', err);
                                    setCustomerDetailsDialog({ 
                                      open: true, 
                                      customer: customerCopy 
                                    });
                                  }
                                }}
                              >
                                <Visibility fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            {customer.invitationId && (
                              <Tooltip title="Delete Invitation">
                                <IconButton 
                                  onClick={() => handleDeleteInvitation(customer.invitationId!, `${customer.firstName} ${customer.lastName}`)}
                                  color="error"
                                  size="small"
                                >
                                  <Delete fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                          </Box>
                        </TableCell>
                      </TableRow>
                      {shouldAddSpacing && (
                        <TableRow>
                          <TableCell colSpan={6} sx={{ height: '16px', border: 'none', padding: 0 }} />
                        </TableRow>
                      )}
                    </React.Fragment>
                  );
                      })
                    })()}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>
      )}

      {activeTab === 1 && (
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Onboarded Customers
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Your existing customers who have created platform accounts and completed payment setup. You receive 99% of the service price (1% platform fee).
            </Typography>
            
            {onboardedCustomers.length === 0 ? (
              <Box textAlign="center" py={4}>
                <PersonAdd sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                <Typography variant="h6" gutterBottom>
                  No Onboarded Customers Yet
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Customers will appear here once they create accounts and link to your services.
                </Typography>
              </Box>
            ) : (
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>
                        Customer Name
                        <InfoTip title="Linked platform customer for this property." />
                      </TableCell>
                      <TableCell>
                        Property Address
                        <InfoTip title="Service address for this property." />
                      </TableCell>
                      <TableCell>
                        Services & Pricing
                        <InfoTip title="Service types and amounts you charge; platform fees apply per your plan." />
                      </TableCell>
                      <TableCell>
                        Account Status
                        <InfoTip title="Whether the customer finished signup / invitation flow." />
                      </TableCell>
                      <TableCell>
                        Payment Verification
                        <InfoTip title="Stripe payment method status — needed before recurring charges run." />
                      </TableCell>
                      <TableCell>
                        Actions
                        <InfoTip title="Manage services, resend invite, or open customer details." />
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {onboardedCustomers.map((customer) => (
                      <TableRow key={customer.id}>
                        <TableCell>{customer.firstName} {customer.lastName}</TableCell>
                        <TableCell>
                          {customer.address.street}, {customer.address.city}, {customer.address.state} {customer.address.zipCode}
                        </TableCell>
                        <TableCell>
                          {(() => {
                            const customerId = customer.id || customer._id || '';
                            const customerRequests = allCustomersServiceRequests[customerId] || [];
                            const servicePricing = customer.servicePricing || generateServiceBreakdown(customer);
                            
                            const services = [];
                            
                            // Helper to get price and frequency for a service
                            const getServiceInfo = (category: 'mowing' | 'fertilizing' | 'snow_removal') => {
                              const serviceKeyMap: Record<'mowing' | 'fertilizing' | 'snow_removal', 'lawnMowingTrimming' | 'weedControlFertilizing' | 'snowRemoval'> = {
                                'mowing': 'lawnMowingTrimming',
                                'fertilizing': 'weedControlFertilizing',
                                'snow_removal': 'snowRemoval'
                              };
                              const serviceKey = serviceKeyMap[category];
                              
                              // First check servicePricing
                              // Use type assertion since we know serviceKey is a valid key
                              const pricing = servicePricing?.[serviceKey as keyof typeof servicePricing];
                              if (pricing && typeof pricing === 'object' && pricing !== null && 'price' in pricing && pricing.price && Number(pricing.price) > 0) {
                                return {
                                  price: Number(pricing.price),
                                  frequency: (pricing as { price: number; frequency: string }).frequency || 'N/A'
                                };
                              }
                              
                              // If no price in servicePricing, check service requests
                              const serviceRequest = customerRequests.find((sr: any) => 
                                sr.serviceType?.category === category
                              );
                              
                              if (serviceRequest) {
                                // Try to get price from accepted bid
                                const acceptedBid = serviceRequest.acceptedBid || serviceRequest.acceptedBidId;
                                if (acceptedBid) {
                                  // Check bid.servicePricing array for matching service
                                  if (Array.isArray(acceptedBid.servicePricing)) {
                                    const bidServicePricing = acceptedBid.servicePricing.find((sp: any) => {
                                      if (category === 'mowing') {
                                        return sp.service === 'lawnMowingTrimming' || 
                                               sp.serviceName?.toLowerCase().includes('mowing') ||
                                               sp.serviceName?.toLowerCase().includes('trim');
                                      } else if (category === 'fertilizing') {
                                        return sp.service === 'weedControlFertilizing' ||
                                               sp.serviceName?.toLowerCase().includes('fertiliz') ||
                                               sp.serviceName?.toLowerCase().includes('weed');
                                      } else if (category === 'snow_removal') {
                                        return sp.service === 'snowRemoval' ||
                                               sp.serviceName?.toLowerCase().includes('snow');
                                      }
                                      return false;
                                    });
                                    
                                    if (bidServicePricing?.price) {
                                      return {
                                        price: Number(bidServicePricing.price),
                                        frequency: bidServicePricing.frequency || serviceRequest.frequency || 'N/A'
                                      };
                                    }
                                  }
                                  
                                  // Fallback to bid.price if no service-specific pricing
                                  if (acceptedBid.price && Number(acceptedBid.price) > 0) {
                                    return {
                                      price: Number(acceptedBid.price),
                                      frequency: serviceRequest.frequency || 'N/A'
                                    };
                                  }
                                }
                                
                                // If service request exists but no price found, still show it
                                return {
                                  price: null,
                                  frequency: serviceRequest.frequency || getServiceFrequency(customer, category, serviceRequest) || 'N/A'
                                };
                              }
                              
                              return null;
                            };
                            
                            // Check each service type
                            const mowingInfo = getServiceInfo('mowing');
                            const fertilizingInfo = getServiceInfo('fertilizing');
                            const snowRemovalInfo = getServiceInfo('snow_removal');
                            
                            // Also check if service exists via servicePricing or service request
                            const hasMowing = (servicePricing?.lawnMowingTrimming?.price && Number(servicePricing.lawnMowingTrimming.price) > 0) ||
                                             customerRequests.some((sr: any) => sr.serviceType?.category === 'mowing');
                            const hasFertilizing = (servicePricing?.weedControlFertilizing?.price && Number(servicePricing.weedControlFertilizing.price) > 0) ||
                                                  customerRequests.some((sr: any) => sr.serviceType?.category === 'fertilizing');
                            const hasSnowRemoval = (servicePricing?.snowRemoval?.price && Number(servicePricing.snowRemoval.price) > 0) ||
                                                  customerRequests.some((sr: any) => sr.serviceType?.category === 'snow_removal');
                            
                            if (hasMowing && mowingInfo) {
                              services.push(
                                <Typography key="mowing" variant="body2" sx={{ mb: 0.5 }}>
                                  <strong>Lawn Mowing/Trimming:</strong> {mowingInfo.price ? `$${mowingInfo.price.toFixed(2)}` : 'Price N/A'} ({mowingInfo.frequency})
                                  {(customer as any).baggingRequired && (
                                    <Chip label="Bagged" size="small" sx={{ ml: 1 }} variant="outlined" />
                                  )}
                                </Typography>
                              );
                            }
                            
                            if (hasFertilizing && fertilizingInfo) {
                              services.push(
                                <Typography key="weed" variant="body2" sx={{ mb: 0.5 }}>
                                  <strong>Weed Control/Fertilizer:</strong> {fertilizingInfo.price ? `$${fertilizingInfo.price.toFixed(2)}` : 'Price N/A'} ({fertilizingInfo.frequency})
                                </Typography>
                              );
                            }
                            
                            if (hasSnowRemoval && snowRemovalInfo) {
                              services.push(
                                <Typography key="snow" variant="body2" sx={{ mb: 0.5 }}>
                                  <strong>Snow Removal:</strong> {snowRemovalInfo.price ? `$${snowRemovalInfo.price.toFixed(2)}` : 'Price N/A'} ({snowRemovalInfo.frequency})
                                </Typography>
                              );
                            }
                            
                            if (services.length === 0) {
                              return (
                                <Typography variant="body2" color="text.secondary">
                                  Pricing not available
                                </Typography>
                              );
                            }
                            
                            return <Box>{services}</Box>;
                          })()}
                        </TableCell>
                        <TableCell>
                          <Box display="flex" alignItems="center" gap={1}>
                            <Tooltip title={getAccountStatus(customer).tooltip}>
                              <Chip 
                                label={getAccountStatus(customer).label}
                                color={getAccountStatus(customer).color}
                                size="small" 
                                icon={getAccountStatus(customer).icon}
                              />
                            </Tooltip>
                            {customer.invitationId && getCustomerStatus(customer).type !== 'setup' && getCustomerStatus(customer).type !== 'recurring_active' && getCustomerStatus(customer).type !== 'seasonal_paid' && (
                              <Tooltip title="Resend Invitation">
                                <IconButton
                                  size="small"
                                  onClick={() => handleResendInvitation(customer.invitationId!, `${customer.firstName} ${customer.lastName}`, customer)}
                                  color="primary"
                                >
                                  <Notifications fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box display="flex" alignItems="center" gap={0.5}>
                            <PaymentVerificationStatus
                              verificationStatus={customer.paymentMethodDetails?.verificationStatus}
                              verificationDate={customer.paymentMethodDetails?.verificationDate}
                              verificationAmount={customer.paymentMethodDetails?.verificationAmount}
                              cardLast4={customer.paymentMethodDetails?.cardLast4}
                              cardBrand={customer.paymentMethodDetails?.cardBrand}
                            />
                            {/* Show notification button for customers who need payment setup */}
                            {customer.linkedCustomerId && !hasPaymentSetup(customer) && (
                              <Tooltip title="Send Payment Setup Reminder">
                                <IconButton
                                  size="small"
                                  onClick={() => handleSendPaymentReminder(customer)}
                                  color="warning"
                                >
                                  <Notifications fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box display="flex" gap={0.5}>
                            {renderPricingButton(customer)}
                            <Tooltip title="Remove Customer">
                              <IconButton 
                                onClick={() => handleRemoveCustomer(customer.id || customer._id || '')}
                                color="error"
                                size="small"
                                disabled={!customer.id && !customer._id}
                              >
                                <Delete />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>
      )}

      {activeTab === 2 && (
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Pending Setup
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Customers you've imported who haven't created platform accounts yet. Services cannot be managed until they complete account setup and payment registration. The Payment Status column shows whether customers have set up payment methods through Stripe.
            </Typography>
            
            {pendingCustomers.length === 0 ? (
              <Box textAlign="center" py={4}>
                <CheckCircle sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                <Typography variant="h6" gutterBottom>
                  All Customers Set Up
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  All your imported customers have completed account setup.
                </Typography>
              </Box>
            ) : (
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>
                        Customer Name
                        <InfoTip title="Imported customer who has not finished creating a platform account yet." />
                      </TableCell>
                      <TableCell>
                        Email
                        <InfoTip title="Used for invitation and account setup." />
                      </TableCell>
                      <TableCell>
                        Services
                        <InfoTip title="Service types you set when importing this customer." />
                      </TableCell>
                      <TableCell>
                        Service Pricing
                        <InfoTip title="Your prices per service. Set or edit before inviting if needed." />
                      </TableCell>
                      <TableCell>
                        Account Status
                        <InfoTip title="Invitation / setup progress until they create an account." />
                      </TableCell>
                      <TableCell>
                        Payment Status
                        <InfoTip title="Whether a payment method is on file after they complete setup." />
                      </TableCell>
                      <TableCell>
                        Actions
                        <InfoTip title="Send or resend invitation, edit pricing, or remove the import." />
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {pendingCustomers.map((customer) => (
                      <TableRow key={customer.id}>
                        <TableCell>{customer.firstName} {customer.lastName}</TableCell>
                        <TableCell>{customer.email || 'No email'}</TableCell>
                        <TableCell>
                          {customer.services && (
                            <Box>
                              {customer.services.lawnMowingTrimming && (
                                <>
                                  <Chip label="Lawn Mowing/Trimming" size="small" sx={{ mr: 0.5, mb: 0.5 }} />
                                  {(customer as any).baggingRequired && (
                                    <Chip label="Bagged" size="small" color="default" variant="outlined" sx={{ mr: 0.5, mb: 0.5 }} />
                                  )}
                                </>
                              )}
                              {customer.services.weedControlFertilizing && (
                                <Chip label="Weed Control/Fertilizer" size="small" sx={{ mr: 0.5, mb: 0.5 }} />
                              )}
                              {customer.services.snowRemoval && (
                                <Chip label="Snow Removal" size="small" sx={{ mr: 0.5, mb: 0.5 }} />
                              )}
                            </Box>
                          )}
                        </TableCell>
                        <TableCell>
                          {(() => {
                            const servicePricing = customer.servicePricing || generateServiceBreakdown(customer);
                            if (!servicePricing) {
                              return (
                                <Typography variant="body2" color="text.secondary">
                                  Pricing not available
                                    </Typography>
                              );
                            }
                            
                            // Only show services that have valid pricing
                            const servicesToShow = [];
                            
                            if (servicePricing.lawnMowingTrimming && 
                                servicePricing.lawnMowingTrimming.price && 
                                !isNaN(Number(servicePricing.lawnMowingTrimming.price)) && 
                                Number(servicePricing.lawnMowingTrimming.price) > 0) {
                              servicesToShow.push({
                                name: 'Lawn Mowing/Trimming',
                                price: Number(servicePricing.lawnMowingTrimming.price),
                                frequency: servicePricing.lawnMowingTrimming.frequency || 'N/A'
                              });
                            }
                            
                            if (servicePricing.weedControlFertilizing && 
                                servicePricing.weedControlFertilizing.price && 
                                !isNaN(Number(servicePricing.weedControlFertilizing.price)) && 
                                Number(servicePricing.weedControlFertilizing.price) > 0) {
                              servicesToShow.push({
                                name: 'Weed Control/Fertilizer',
                                price: Number(servicePricing.weedControlFertilizing.price),
                                frequency: servicePricing.weedControlFertilizing.frequency || 'N/A'
                              });
                            }
                            
                            if (servicePricing.snowRemoval && 
                                servicePricing.snowRemoval.price && 
                                !isNaN(Number(servicePricing.snowRemoval.price)) && 
                                Number(servicePricing.snowRemoval.price) > 0) {
                              servicesToShow.push({
                                name: 'Snow Removal',
                                price: Number(servicePricing.snowRemoval.price),
                                frequency: servicePricing.snowRemoval.frequency || 'N/A'
                              });
                            }
                            
                            if (servicesToShow.length === 0) {
                              return (
                                <Typography variant="body2" color="text.secondary">
                                  Pricing not available
                                    </Typography>
                              );
                            }
                            
                            return (
                              <Box>
                                {servicesToShow.map((service, index) => (
                                  <Box key={index} sx={{ mb: 0.5 }}>
                                    <Typography variant="caption" color="text.secondary">
                                      {service.name}
                                    </Typography>
                                    <Typography variant="body2" fontWeight="medium">
                                      ${service.price.toFixed(2)} ({service.frequency})
                                    </Typography>
                                  </Box>
                                ))}
                                  </Box>
                            );
                          })()}
                        </TableCell>
                        <TableCell>
                          <Tooltip title={getAccountStatus(customer).tooltip}>
                            <Chip 
                              label={getAccountStatus(customer).label}
                              color={getAccountStatus(customer).color}
                              size="small" 
                              icon={getAccountStatus(customer).icon}
                            />
                          </Tooltip>
                        </TableCell>
                        <TableCell>
                          <Tooltip title={getPaymentStatus(customer).tooltip}>
                            <Chip 
                              label={getPaymentStatus(customer).label}
                              color={getPaymentStatus(customer).color}
                              size="small" 
                              icon={getPaymentStatus(customer).icon}
                            />
                          </Tooltip>
                        </TableCell>
                        <TableCell>
                          <Box display="flex" gap={1}>
                            {renderPricingButton(customer)}
                            {customer.invitationId ? (
                              <Tooltip title="Resend Invitation Email">
                                <IconButton 
                                  onClick={() => handleResendInvitation(customer.invitationId!, `${customer.firstName} ${customer.lastName}`, customer)}
                                  color="primary"
                                  size="small"
                                  disabled={loading}
                                >
                                  <Email />
                                </IconButton>
                              </Tooltip>
                            ) : (
                              <Tooltip title="Send Invitation Email">
                                <IconButton 
                                  onClick={() => handleSendInvitation(customer)}
                                  color="primary"
                                  size="small"
                                  disabled={loading}
                                >
                                  <Email />
                                </IconButton>
                              </Tooltip>
                            )}
                            {customer.phone && (
                              <Tooltip title="Send SMS Invitation">
                                <IconButton 
                                  onClick={() => handleSendSMSInvitation(customer)}
                                  color="primary"
                                  size="small"
                                  disabled={loading}
                                >
                                  <Sms />
                                </IconButton>
                              </Tooltip>
                            )}
                            <Tooltip title="Remove Customer">
                              <IconButton 
                                onClick={() => handleRemoveCustomer(customer.id || customer._id || '')}
                                color="error"
                                size="small"
                                disabled={!customer.id && !customer._id}
                              >
                                <Delete />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>
      )}


      {/* Add Customer Dialog */}
      <SetCustomerPricesDialog
        customer={pricingCustomer}
        onClose={() => setPricingCustomer(null)}
        onSaved={(saved) => {
          setPricingCustomer(null);
          setError(null);
          setSuccess(`Prices saved for ${saved.firstName} ${saved.lastName}. You can now send their invitation.`);
          fetchCustomers();
        }}
      />

      <Dialog open={addCustomerDialogOpen} onClose={() => setAddCustomerDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Import Current Customer</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            {/* Error Alert inside modal */}
            {error && (
              <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
                {error}
              </Alert>
            )}
            {/* Debug: Always show error state */}
            {success && (
              <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess(null)}>
                {success}
              </Alert>
            )}
            
            <Grid container spacing={3}>
              {/* Customer Information */}
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>Customer Information</Typography>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="First Name"
                  value={newCustomer.firstName || ''}
                  onChange={(e) => setNewCustomer({ ...newCustomer, firstName: e.target.value })}
                  required
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Last Name"
                  value={newCustomer.lastName || ''}
                  onChange={(e) => setNewCustomer({ ...newCustomer, lastName: e.target.value })}
                  required
                />
              </Grid>
              
              <Grid item xs={12}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Contact Information (at least one required for invitations)
                </Typography>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Email (required if no phone)"
                  type="email"
                  value={newCustomer.email || ''}
                  onChange={(e) => {
                    setNewCustomer({ ...newCustomer, email: e.target.value });
                    // Clear contact error if user starts typing
                    if (error?.includes('Contact Information Required')) {
                      setError(null);
                    }
                  }}
                  error={!newCustomer.email && !newCustomer.phone && error?.includes('Contact Information Required')}
                  helperText={!newCustomer.email && !newCustomer.phone && error?.includes('Contact Information Required') ? 'At least one contact method required' : ''}
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Phone (required if no email)"
                  value={newCustomer.phone || ''}
                  onChange={(e) => {
                    setNewCustomer({ ...newCustomer, phone: e.target.value });
                    // Clear contact error if user starts typing
                    if (error?.includes('Contact Information Required')) {
                      setError(null);
                    }
                  }}
                  error={!newCustomer.email && !newCustomer.phone && error?.includes('Contact Information Required')}
                  helperText={!newCustomer.email && !newCustomer.phone && error?.includes('Contact Information Required') ? 'At least one contact method required' : ''}
                />
              </Grid>
              
              <Grid item xs={12}>
                <AddressAutocomplete
                  value={newCustomer.address?.street || ''}
                  onChange={handleAddressSelect}
                  label="Street Address"
                  fullWidth
                  required
                />
              </Grid>
              
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="City"
                  value={newCustomer.address?.city || ''}
                  onChange={(e) => setNewCustomer({ 
                    ...newCustomer, 
                      address: {
                        ...newCustomer.address,
                        street: newCustomer.address?.street || '',
                        city: e.target.value,
                        state: newCustomer.address?.state || '',
                        zipCode: newCustomer.address?.zipCode || '',
                        coordinates: newCustomer.address?.coordinates || { lat: 0, lng: 0 }
                      }
                  })}
                  required
                />
              </Grid>
              
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="State"
                  value={newCustomer.address?.state || ''}
                  onChange={(e) => setNewCustomer({ 
                    ...newCustomer, 
                      address: {
                        ...newCustomer.address,
                        street: newCustomer.address?.street || '',
                        city: newCustomer.address?.city || '',
                        state: e.target.value,
                        zipCode: newCustomer.address?.zipCode || '',
                        coordinates: newCustomer.address?.coordinates || { lat: 0, lng: 0 }
                      }
                  })}
                  required
                />
              </Grid>
              
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="ZIP Code"
                  value={newCustomer.address?.zipCode || ''}
                  onChange={(e) => setNewCustomer({ 
                    ...newCustomer, 
                      address: {
                        ...newCustomer.address,
                        street: newCustomer.address?.street || '',
                        city: newCustomer.address?.city || '',
                        state: newCustomer.address?.state || '',
                        zipCode: e.target.value,
                        coordinates: newCustomer.address?.coordinates || { lat: 0, lng: 0 }
                      }
                  })}
                  required
                />
              </Grid>

              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
                <Typography variant="h6" gutterBottom>Services & Pricing</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Select the services this customer receives and set the pricing for each.
                </Typography>
              </Grid>

              {/* Lawn Mowing/Trimming Service */}
              <Grid item xs={12}>
                <Card variant="outlined">
                  <CardContent>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={newCustomer.services?.lawnMowingTrimming || false}
                          onChange={(e) => setNewCustomer({
                            ...newCustomer,
                            services: {
                              lawnMowingTrimming: e.target.checked,
                              weedControlFertilizing: newCustomer.services?.weedControlFertilizing || false,
                              snowRemoval: newCustomer.services?.snowRemoval || false
                            }
                          })}
                        />
                      }
                      label="Lawn Mowing/Trimming"
                    />
                    {newCustomer.services?.lawnMowingTrimming && (
                      <Grid container spacing={2} sx={{ mt: 1 }}>
                        <Grid item xs={12}>
                          <FormControl component="fieldset" sx={{ mb: 1 }}>
                            <Typography component="span" variant="body2" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>Require grass clippings to be bagged?</Typography>
                            <RadioGroup
                              row
                              value={newCustomer.baggingRequired === true ? 'yes' : 'no'}
                              onChange={(e) => setNewCustomer({ ...newCustomer, baggingRequired: e.target.value === 'yes' })}
                            >
                              <FormControlLabel value="no" control={<Radio size="small" />} label="No" />
                              <FormControlLabel value="yes" control={<Radio size="small" />} label="Yes" />
                            </RadioGroup>
                            {newCustomer.baggingRequired && (
                              <Typography variant="caption" color="text.secondary">Bagging may affect pricing.</Typography>
                            )}
                          </FormControl>
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <FormControl fullWidth>
                            <InputLabel>Frequency</InputLabel>
                            <Select
                              value={servicePricing.lawnMowingTrimming.frequency}
                              onChange={(e) => setServicePricing({
                                ...servicePricing,
                                lawnMowingTrimming: {
                                  ...servicePricing.lawnMowingTrimming,
                                  frequency: e.target.value,
                                  // Reset serviceFrequency when changing from seasonal-contract
                                  serviceFrequency: e.target.value === 'seasonal-contract' ? servicePricing.lawnMowingTrimming.serviceFrequency : (e.target.value as any)
                                }
                              })}
                            >
                              <MenuItem value="weekly">Weekly</MenuItem>
                              <MenuItem value="bi-weekly">Bi-weekly</MenuItem>
                              <MenuItem value="monthly">Monthly</MenuItem>
                              <MenuItem value="per-instance">Per Instance</MenuItem>
                              <MenuItem value="seasonal-contract">Seasonal Contract</MenuItem>
                            </Select>
                          </FormControl>
                        </Grid>
                        {servicePricing.lawnMowingTrimming.frequency === 'seasonal-contract' && (
                          <>
                            <Grid item xs={12} md={6}>
                              <FormControl fullWidth>
                                <InputLabel>Service Frequency</InputLabel>
                                <Select
                                  value={servicePricing.lawnMowingTrimming.serviceFrequency}
                                  onChange={(e) => setServicePricing({
                                    ...servicePricing,
                                    lawnMowingTrimming: {
                                      ...servicePricing.lawnMowingTrimming,
                                      serviceFrequency: e.target.value as 'weekly' | 'bi-weekly' | 'monthly' | 'per-instance'
                                    }
                                  })}
                                >
                                  <MenuItem value="weekly">Weekly</MenuItem>
                                  <MenuItem value="bi-weekly">Bi-Weekly</MenuItem>
                                  <MenuItem value="monthly">Monthly</MenuItem>
                                  <MenuItem value="per-instance">Per Instance</MenuItem>
                            </Select>
                          </FormControl>
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <TextField
                            fullWidth
                                label="Contract Start Date"
                                type="date"
                                value={servicePricing.lawnMowingTrimming.contractStartDate}
                            onChange={(e) => setServicePricing({
                              ...servicePricing,
                              lawnMowingTrimming: {
                                ...servicePricing.lawnMowingTrimming,
                                    contractStartDate: e.target.value
                                  }
                                })}
                                InputLabelProps={{ shrink: true }}
                                required
                              />
                            </Grid>
                            <Grid item xs={12} md={6}>
                              <TextField
                                fullWidth
                                label="Contract End Date"
                                type="date"
                                value={servicePricing.lawnMowingTrimming.contractEndDate}
                                onChange={(e) => setServicePricing({
                                  ...servicePricing,
                                  lawnMowingTrimming: {
                                    ...servicePricing.lawnMowingTrimming,
                                    contractEndDate: e.target.value
                                  }
                                })}
                                InputLabelProps={{ shrink: true }}
                                required
                              />
                            </Grid>
                            <Grid item xs={12} md={6}>
                              <FormControl fullWidth>
                                <InputLabel>Payment Schedule</InputLabel>
                                <Select
                                  value={servicePricing.lawnMowingTrimming.paymentSchedule || 'single-payment'}
                                  label="Payment Schedule"
                                  onChange={(e) => setServicePricing({
                                    ...servicePricing,
                                    lawnMowingTrimming: {
                                      ...servicePricing.lawnMowingTrimming,
                                      paymentSchedule: e.target.value as 'single-payment' | 'monthly'
                                    }
                                  })}
                                >
                                  <MenuItem value="single-payment">One-Time Payment (Full payment after first service)</MenuItem>
                                  <MenuItem value="monthly">Monthly Payments (Charged on 1st of each month)</MenuItem>
                                </Select>
                              </FormControl>
                            </Grid>
                          </>
                        )}
                        <Grid item xs={12} md={servicePricing.lawnMowingTrimming.frequency === 'seasonal-contract' ? 12 : 6}>
                          <TextField
                            fullWidth
                            label={servicePricing.lawnMowingTrimming.frequency === 'seasonal-contract' ? "Total Contract Price" : "Price per Service"}
                            type="text"
                            value={String(servicePricing.lawnMowingTrimming.price || '')}
                            onChange={(e) => {
                              // Allow only numbers and decimal point
                              const value = e.target.value.replace(/[^0-9.]/g, '');
                              // Prevent multiple decimal points
                              const parts = value.split('.');
                              const sanitizedValue = parts.length > 2 ? parts[0] + '.' + parts.slice(1).join('') : value;
                              setServicePricing({
                                ...servicePricing,
                                lawnMowingTrimming: {
                                  ...servicePricing.lawnMowingTrimming,
                                  price: sanitizedValue
                                }
                              });
                            }}
                            InputProps={{
                              startAdornment: <Typography>$</Typography>
                            }}
                            inputProps={{ 
                              inputMode: 'decimal',
                              pattern: '[0-9]*\\.?[0-9]*',
                              style: { 
                                MozAppearance: 'textfield',
                                WebkitAppearance: 'none',
                              }
                            }}
                            sx={{
                              '& input[type=text]::-webkit-outer-spin-button': {
                                WebkitAppearance: 'none',
                                margin: 0,
                              },
                              '& input[type=text]::-webkit-inner-spin-button': {
                                WebkitAppearance: 'none',
                                margin: 0,
                              },
                              '& input[type=text]': {
                                MozAppearance: 'textfield',
                              },
                            }}
                            helperText={servicePricing.lawnMowingTrimming.frequency === 'seasonal-contract' 
                              ? (servicePricing.lawnMowingTrimming.paymentSchedule === 'single-payment'
                                  ? "Customer will be charged the full amount after the first service. All subsequent services are included at no additional charge."
                                  : "Customer will be charged monthly installments. Payments will be automatically processed on the 1st of each month.")
                              : "Price per individual service"}
                          />
                        </Grid>
                      </Grid>
                    )}
                  </CardContent>
                </Card>
              </Grid>

              {/* Weed Control/Fertilizing Service */}
              <Grid item xs={12}>
                <Card variant="outlined">
                  <CardContent>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={newCustomer.services?.weedControlFertilizing || false}
                          onChange={(e) => setNewCustomer({
                            ...newCustomer,
                            services: {
                              lawnMowingTrimming: newCustomer.services?.lawnMowingTrimming || false,
                              weedControlFertilizing: e.target.checked,
                              snowRemoval: newCustomer.services?.snowRemoval || false
                            }
                          })}
                        />
                      }
                      label="Weed Control/Fertilizing"
                    />
                    {newCustomer.services?.weedControlFertilizing && (
                      <Grid container spacing={2} sx={{ mt: 1 }}>
                        <Grid item xs={12} md={6}>
                          <FormControl fullWidth>
                            <InputLabel>Frequency</InputLabel>
                            <Select
                              value={servicePricing.weedControlFertilizing.frequency}
                              onChange={(e) => setServicePricing({
                                ...servicePricing,
                                weedControlFertilizing: {
                                  ...servicePricing.weedControlFertilizing,
                                  frequency: e.target.value,
                                  // Reset serviceFrequency when changing from seasonal-contract
                                  serviceFrequency: e.target.value === 'seasonal-contract' ? servicePricing.weedControlFertilizing.serviceFrequency : (e.target.value as any)
                                }
                              })}
                            >
                              <MenuItem value="monthly">Monthly</MenuItem>
                              <MenuItem value="bi-monthly">Bi-monthly</MenuItem>
                              <MenuItem value="quarterly">Quarterly</MenuItem>
                              <MenuItem value="per-instance">Per Instance</MenuItem>
                              <MenuItem value="seasonal-contract">Seasonal Contract</MenuItem>
                            </Select>
                          </FormControl>
                        </Grid>
                        {servicePricing.weedControlFertilizing.frequency === 'seasonal-contract' && (
                          <>
                            <Grid item xs={12} md={6}>
                              <FormControl fullWidth>
                                <InputLabel>Service Frequency</InputLabel>
                                <Select
                                  value={servicePricing.weedControlFertilizing.serviceFrequency}
                                  onChange={(e) => setServicePricing({
                                    ...servicePricing,
                                    weedControlFertilizing: {
                                      ...servicePricing.weedControlFertilizing,
                                      serviceFrequency: e.target.value as 'weekly' | 'bi-weekly' | 'monthly' | 'per-instance'
                                    }
                                  })}
                                >
                                  <MenuItem value="weekly">Weekly</MenuItem>
                                  <MenuItem value="bi-weekly">Bi-Weekly</MenuItem>
                                  <MenuItem value="monthly">Monthly</MenuItem>
                                  <MenuItem value="per-instance">Per Instance</MenuItem>
                            </Select>
                          </FormControl>
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <TextField
                            fullWidth
                                label="Contract Start Date"
                                type="date"
                                value={servicePricing.weedControlFertilizing.contractStartDate}
                            onChange={(e) => setServicePricing({
                              ...servicePricing,
                              weedControlFertilizing: {
                                ...servicePricing.weedControlFertilizing,
                                    contractStartDate: e.target.value
                                  }
                                })}
                                InputLabelProps={{ shrink: true }}
                                required
                              />
                            </Grid>
                            <Grid item xs={12} md={6}>
                              <TextField
                                fullWidth
                                label="Contract End Date"
                                type="date"
                                value={servicePricing.weedControlFertilizing.contractEndDate}
                                onChange={(e) => setServicePricing({
                                  ...servicePricing,
                                  weedControlFertilizing: {
                                    ...servicePricing.weedControlFertilizing,
                                    contractEndDate: e.target.value
                                  }
                                })}
                                InputLabelProps={{ shrink: true }}
                                required
                              />
                            </Grid>
                            <Grid item xs={12} md={6}>
                              <FormControl fullWidth>
                                <InputLabel>Payment Schedule</InputLabel>
                                <Select
                                  value={servicePricing.weedControlFertilizing.paymentSchedule || 'single-payment'}
                                  label="Payment Schedule"
                                  onChange={(e) => setServicePricing({
                                    ...servicePricing,
                                    weedControlFertilizing: {
                                      ...servicePricing.weedControlFertilizing,
                                      paymentSchedule: e.target.value as 'single-payment' | 'monthly'
                                    }
                                  })}
                                >
                                  <MenuItem value="single-payment">One-Time Payment (Full payment after first service)</MenuItem>
                                  <MenuItem value="monthly">Monthly Payments (Charged on 1st of each month)</MenuItem>
                                </Select>
                              </FormControl>
                            </Grid>
                          </>
                        )}
                        <Grid item xs={12} md={servicePricing.weedControlFertilizing.frequency === 'seasonal-contract' ? 12 : 6}>
                          <TextField
                            fullWidth
                            label={servicePricing.weedControlFertilizing.frequency === 'seasonal-contract' ? "Total Contract Price" : "Price per Application"}
                            type="text"
                            value={String(servicePricing.weedControlFertilizing.price || '')}
                            onChange={(e) => {
                              // Allow only numbers and decimal point
                              const value = e.target.value.replace(/[^0-9.]/g, '');
                              // Prevent multiple decimal points
                              const parts = value.split('.');
                              const sanitizedValue = parts.length > 2 ? parts[0] + '.' + parts.slice(1).join('') : value;
                              setServicePricing({
                                ...servicePricing,
                                weedControlFertilizing: {
                                  ...servicePricing.weedControlFertilizing,
                                  price: sanitizedValue
                                }
                              });
                            }}
                            InputProps={{
                              startAdornment: <Typography>$</Typography>
                            }}
                            inputProps={{ 
                              inputMode: 'decimal',
                              pattern: '[0-9]*\\.?[0-9]*',
                              style: { 
                                MozAppearance: 'textfield',
                                WebkitAppearance: 'none',
                              }
                            }}
                            sx={{
                              '& input[type=text]::-webkit-outer-spin-button': {
                                WebkitAppearance: 'none',
                                margin: 0,
                              },
                              '& input[type=text]::-webkit-inner-spin-button': {
                                WebkitAppearance: 'none',
                                margin: 0,
                              },
                              '& input[type=text]': {
                                MozAppearance: 'textfield',
                              },
                            }}
                            helperText={servicePricing.weedControlFertilizing.frequency === 'seasonal-contract' 
                              ? (servicePricing.weedControlFertilizing.paymentSchedule === 'single-payment'
                                  ? "Customer will be charged the full amount after the first service. All subsequent services are included at no additional charge."
                                  : "Customer will be charged monthly installments. Payments will be automatically processed on the 1st of each month.")
                              : "Price per individual application"}
                          />
                        </Grid>
                      </Grid>
                    )}
                  </CardContent>
                </Card>
              </Grid>

              {/* Snow Removal Service */}
              <Grid item xs={12}>
                <Card variant="outlined">
                  <CardContent>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={newCustomer.services?.snowRemoval || false}
                          onChange={(e) => setNewCustomer({
                            ...newCustomer,
                            services: {
                              lawnMowingTrimming: newCustomer.services?.lawnMowingTrimming || false,
                              weedControlFertilizing: newCustomer.services?.weedControlFertilizing || false,
                              snowRemoval: e.target.checked
                            }
                          })}
                        />
                      }
                      label="Snow Removal"
                    />
                    {newCustomer.services?.snowRemoval && (
                      <Grid container spacing={2} sx={{ mt: 1 }}>
                        <Grid item xs={12} md={6}>
                          <FormControl fullWidth>
                            <InputLabel>Frequency</InputLabel>
                            <Select
                              value={servicePricing.snowRemoval.frequency}
                              onChange={(e) => setServicePricing({
                                ...servicePricing,
                                snowRemoval: {
                                  ...servicePricing.snowRemoval,
                                  frequency: e.target.value
                                }
                              })}
                            >
                              <MenuItem value="per-instance">Per Instance</MenuItem>
                              <MenuItem value="monthly">Monthly</MenuItem>
                              <MenuItem value="seasonal-contract">Seasonal Contract</MenuItem>
                            </Select>
                          </FormControl>
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <TextField
                            fullWidth
                            label="Price per Frequency"
                            type="text"
                            value={String(servicePricing.snowRemoval.price || '')}
                            onChange={(e) => {
                              // Allow only numbers and decimal point
                              const value = e.target.value.replace(/[^0-9.]/g, '');
                              // Prevent multiple decimal points
                              const parts = value.split('.');
                              const sanitizedValue = parts.length > 2 ? parts[0] + '.' + parts.slice(1).join('') : value;
                              setServicePricing({
                              ...servicePricing,
                              snowRemoval: {
                                ...servicePricing.snowRemoval,
                                  price: sanitizedValue
                                }
                              });
                            }}
                            inputProps={{ 
                              inputMode: 'decimal',
                              pattern: '[0-9]*\\.?[0-9]*',
                              style: { 
                                MozAppearance: 'textfield',
                                WebkitAppearance: 'none',
                              }
                            }}
                            sx={{
                              '& input[type=text]::-webkit-outer-spin-button': {
                                WebkitAppearance: 'none',
                                margin: 0,
                              },
                              '& input[type=text]::-webkit-inner-spin-button': {
                                WebkitAppearance: 'none',
                                margin: 0,
                              },
                              '& input[type=text]': {
                                MozAppearance: 'textfield',
                              },
                            }}
                            InputProps={{
                              startAdornment: <Typography>$</Typography>
                            }}
                          />
                        </Grid>
                      </Grid>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            {(() => {
              const lines: Array<{ label: string; price: number }> = [];
              if (newCustomer.services?.lawnMowingTrimming) {
                const p = Number(servicePricing.lawnMowingTrimming.price) || 0;
                if (p > 0) lines.push({ label: 'Lawn Mowing/Trimming', price: p });
              }
              if (newCustomer.services?.weedControlFertilizing) {
                const p = Number(servicePricing.weedControlFertilizing.price) || 0;
                if (p > 0) lines.push({ label: 'Weed Control/Fertilizing', price: p });
              }
              if (newCustomer.services?.snowRemoval) {
                const p = Number(servicePricing.snowRemoval.price) || 0;
                if (p > 0) lines.push({ label: 'Snow Removal', price: p });
              }
              if (lines.length === 0) return null;
              return (
                <Box sx={{ mt: 3, p: 2, backgroundColor: 'primary.50', borderRadius: 1 }}>
                  <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold' }}>
                    Price Summary (1% onboarded fee)
                  </Typography>
                  {lines.map((line) => (
                    <ProviderPriceBreakdown
                      key={line.label}
                      label={line.label}
                      servicePrice={line.price}
                      platformFeePercent={DEFAULT_PLATFORM_FEES.importedCustomerFeePercentage}
                      compact
                      {...salesTaxOptionsForJob((user as any)?.salesTaxRates, newCustomer.address)}
                    />
                  ))}
                </Box>
              );
            })()}
          </Box>
        </DialogContent>
        <DialogActions>
    <Button onClick={() => { 
      setAddCustomerDialogOpen(false); 
      clearCustomerForm(); 
      setError(null); // Clear any errors when closing
      setSuccess(null); // Clear any success messages when closing
    }}>
      Cancel
    </Button>
          <Button 
            onClick={handleAddCustomer} 
            variant="contained" 
            disabled={loading || !newCustomer.firstName || !newCustomer.lastName || !newCustomer.address?.street}
          >
            {loading ? <CircularProgress size={20} /> : 'Import Customer'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* CSV Import Dialog */}
      <Dialog open={csvImportDialogOpen} onClose={() => { setCsvImportDialogOpen(false); setCsvPreviewRows([]); setCsvImportError(null); }} maxWidth="md" fullWidth>
        <DialogTitle>Import customers from CSV</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Upload a CSV with columns: <strong>FirstName</strong>, <strong>LastName</strong>, <strong>Email</strong>, <strong>Phone</strong>, <strong>Street</strong>, <strong>City</strong>, <strong>State</strong>, <strong>ZipCode</strong>, <strong>Services</strong> (e.g. Mowing or Mowing;Fertilizing), <strong>Mowing Bagged</strong> (Yes/No). At least one of Email or Phone is required per row.
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Price and frequency are set to $0/defaults. After import, set real prices and frequency per customer in Customer Management.
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
            <Button variant="outlined" component="label" startIcon={<UploadFile />}>
              Choose CSV file
              <input type="file" accept=".csv" hidden onChange={handleCsvFileSelect} />
            </Button>
            <Button
              variant="outlined"
              startIcon={<Visibility />}
              onClick={() => {
                const header = 'FirstName,LastName,Email,Phone,Street,City,State,ZipCode,Services,Mowing Bagged';
                const example = 'Jane,Doe,jane@example.com,555-123-4567,123 Main St,Springfield,IL,62701,Mowing;Fertilizing,Yes';
                const csv = [header, example].join('\n');
                const blob = new Blob([csv], { type: 'text/csv' });
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = 'customer-import-template.csv';
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                URL.revokeObjectURL(url);
              }}
            >
              Download template
            </Button>
          </Box>
          {csvImportError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setCsvImportError(null)}>{csvImportError}</Alert>
          )}
          {csvPreviewRows.length > 0 && (
            <>
              <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
                Preview ({csvPreviewRows.length} row{csvPreviewRows.length !== 1 ? 's' : ''}) — remove any row you don&apos;t want to import, then confirm.
              </Typography>
              <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 360, mb: 2 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell>First Name</TableCell>
                      <TableCell>Last Name</TableCell>
                      <TableCell>Email</TableCell>
                      <TableCell>Phone</TableCell>
                      <TableCell>Address</TableCell>
                      <TableCell>Services</TableCell>
                      <TableCell>Bagged</TableCell>
                      <TableCell align="right">Remove</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {csvPreviewRows.map((row, idx) => (
                      <TableRow key={idx}>
                        <TableCell>{(row.firstname ?? row.first_name ?? '').slice(0, 20)}</TableCell>
                        <TableCell>{(row.lastname ?? row.last_name ?? '').slice(0, 20)}</TableCell>
                        <TableCell>{(row.email ?? '').slice(0, 25)}</TableCell>
                        <TableCell>{(row.phone ?? '').slice(0, 15)}</TableCell>
                        <TableCell>{(row.street ?? '').slice(0, 25)}…</TableCell>
                        <TableCell>{row.services ?? '—'}</TableCell>
                        <TableCell>{/^(yes|y|1|true)$/i.test((row.mowingbagged ?? row.mowing_bagged ?? row.bagged ?? '').trim()) ? 'Yes' : 'No'}</TableCell>
                        <TableCell align="right">
                          <IconButton size="small" onClick={() => setCsvPreviewRows((prev) => prev.filter((_, i) => i !== idx))}>
                            <Delete fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <DialogActions>
                <Button onClick={() => { setCsvImportDialogOpen(false); setCsvPreviewRows([]); }}>Cancel</Button>
                <Button variant="contained" onClick={handleCsvConfirmImport} disabled={csvImportLoading}>
                  {csvImportLoading ? <CircularProgress size={20} /> : `Confirm import (${csvPreviewRows.length})`}
                </Button>
              </DialogActions>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Customer Details Dialog */}
      <Dialog 
        open={customerDetailsDialog.open} 
        onClose={() => setCustomerDetailsDialog({ open: false, customer: null })} 
        maxWidth="md" 
        fullWidth
      >
        <DialogTitle>
          Customer Details
        </DialogTitle>
        <DialogContent>
          {customerDetailsDialog.customer && (
            <Box sx={{ pt: 2 }}>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Full Name
                  </Typography>
                  <Typography variant="body1">
                    {customerDetailsDialog.customer.firstName} {customerDetailsDialog.customer.lastName}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Contact Information
                  </Typography>
                  {customerDetailsDialog.customer.email && (
                    <Box display="flex" alignItems="center" gap={1} mb={1}>
                      <Email fontSize="small" />
                      <Typography variant="body2">{customerDetailsDialog.customer.email}</Typography>
                    </Box>
                  )}
                  {customerDetailsDialog.customer.phone && (
                    <Box display="flex" alignItems="center" gap={1}>
                      <Phone fontSize="small" />
                      <Typography variant="body2">{customerDetailsDialog.customer.phone}</Typography>
                    </Box>
                  )}
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Address
                  </Typography>
                  <Box display="flex" alignItems="center" gap={1}>
                    <LocationOn fontSize="small" />
                    <Typography variant="body2">
                      {customerDetailsDialog.customer.address.street}, {customerDetailsDialog.customer.address.city}, {customerDetailsDialog.customer.address.state} {customerDetailsDialog.customer.address.zipCode}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Services & Schedules
                  </Typography>
                  {loadingServiceRequests ? (
                    <Box display="flex" justifyContent="center" py={2}>
                      <CircularProgress size={24} />
                    </Box>
                  ) : (
                    <Box display="flex" flexDirection="column" gap={2}>
                      {/* Lawn Mowing Service */}
                      {/* SINGLE SOURCE OF TRUTH: Only show service if there's a ServiceRequest for THIS property address */}
                      {/* ServiceRequest is filtered by providerId (backend) and address (frontend), so it's the ultimate source of truth */}
                      {/* CRITICAL: Do NOT check servicePricing - it may contain services from other providers */}
                      {customerServiceRequests.some((sr: any) => sr.serviceType?.category === 'mowing') && (
                        <Card variant="outlined">
                          <CardContent>
                            <Box display="flex" justifyContent="space-between" alignItems="start" mb={1}>
                              <Typography variant="h6" fontSize="1rem">
                                Lawn Mowing/Trimming
                              </Typography>
                              <Chip label="Active" size="small" color="success" />
                            </Box>
                            <Grid container spacing={2}>
                              <Grid item xs={12} sm={6}>
                                <Typography variant="caption" color="text.secondary">Price</Typography>
                                <Box display="flex" alignItems="center" gap={1}>
                                  <Typography variant="body2" fontWeight="medium">
                                    {(() => {
                                      // Check servicePricing first
                                      let price = customerDetailsDialog.customer.servicePricing?.lawnMowingTrimming?.price;
                                      let isSeasonalContract = false;
                                      
                                      // Check if this is a seasonal contract
                                      const serviceFrequency = customerDetailsDialog.customer.servicePricing?.lawnMowingTrimming?.frequency;
                                      if (serviceFrequency === 'seasonal-contract' || customerDetailsDialog.customer.paymentStructure?.type === 'seasonal-contract') {
                                        isSeasonalContract = true;
                                      }
                                      
                                      // If price is missing or 0, check service request's accepted bid
                                      if (!price || Number(price) === 0) {
                                        const serviceRequest = customerServiceRequests.find((sr: any) => sr.serviceType?.category === 'mowing');
                                        if (serviceRequest?.acceptedBid) {
                                          // Check if bid has servicePricing array
                                          if (serviceRequest.acceptedBid.servicePricing && Array.isArray(serviceRequest.acceptedBid.servicePricing)) {
                                            const matchingServicePricing = serviceRequest.acceptedBid.servicePricing.find((sp: any) => 
                                              sp.service === 'lawnMowingTrimming' || sp.serviceName?.toLowerCase().includes('mowing')
                                            );
                                            if (matchingServicePricing?.price) {
                                              price = matchingServicePricing.price;
                                            }
                                            if (matchingServicePricing?.isSeasonalContract || matchingServicePricing?.frequency === 'seasonal-contract') {
                                              isSeasonalContract = true;
                                            }
                                          }
                                          // Fallback to bid.price
                                          if (!price && serviceRequest.acceptedBid.price) {
                                            price = serviceRequest.acceptedBid.price;
                                          }
                                        }
                                      }
                                      
                                      return price && Number(price) > 0 
                                        ? `$${Number(price).toFixed(2)}`
                                        : 'N/A';
                                    })()}
                                  </Typography>
                                  {(() => {
                                    const serviceFrequency = customerDetailsDialog.customer.servicePricing?.lawnMowingTrimming?.frequency;
                                    const serviceRequest = customerServiceRequests.find((sr: any) => sr.serviceType?.category === 'mowing');
                                    // CRITICAL: Only check service-specific seasonal contract status, NOT customer-level paymentStructure
                                    // paymentStructure.type is customer-level and may be 'seasonal-contract' if ANY service is seasonal
                                    // We need to check each service individually
                                    let isSeasonal = serviceFrequency === 'seasonal-contract';
                                    
                                    // Also check service request's accepted bid for service-specific seasonal contract status
                                    if (!isSeasonal && serviceRequest?.acceptedBid?.servicePricing) {
                                      const matchingServicePricing = serviceRequest.acceptedBid.servicePricing.find((sp: any) => 
                                        sp.service === 'lawnMowingTrimming' || sp.serviceName?.toLowerCase().includes('mowing')
                                      );
                                      if (matchingServicePricing?.isSeasonalContract || matchingServicePricing?.frequency === 'seasonal-contract') {
                                        isSeasonal = true;
                                      }
                                    }
                                    
                                    return isSeasonal ? (
                                      <Chip 
                                        label="Seasonal Contract" 
                                        size="small" 
                                        color="secondary"
                                        variant="outlined"
                                        sx={{ height: 20, fontSize: '0.65rem' }}
                                      />
                                    ) : null;
                                  })()}
                                </Box>
                              </Grid>
                              <Grid item xs={12} sm={6}>
                                <Typography variant="caption" color="text.secondary">Frequency</Typography>
                                <Typography variant="body2" fontWeight="medium">
                                  {(() => {
                                    const serviceRequest = customerServiceRequests.find((sr: any) => sr.serviceType?.category === 'mowing');
                                    const frequency = getServiceFrequency(customerDetailsDialog.customer, 'mowing', serviceRequest);
                                    
                                    if (!frequency) return 'N/A';
                                    
                                    // Format frequency for display
                                    if (frequency === 'per-instance') return 'Per Instance';
                                    if (frequency === 'weekly') return 'Weekly';
                                    if (frequency === 'bi-weekly') return 'Bi-Weekly';
                                    if (frequency === 'monthly') return 'Monthly';
                                    if (frequency === 'seasonal-contract') return 'Seasonal Contract';
                                    return frequency.charAt(0).toUpperCase() + frequency.slice(1).replace('-', '-');
                                  })()}
                                </Typography>
                              </Grid>
                              <Grid item xs={12} sm={6}>
                                <Typography variant="caption" color="text.secondary">Schedule</Typography>
                                {(() => {
                                  // Use the SAME logic as All Customers chart - use getServiceFrequency helper
                                  const serviceRequest = customerServiceRequests.find((sr: any) => sr.serviceType?.category === 'mowing');
                                  const serviceFrequency = getServiceFrequency(customerDetailsDialog.customer, 'mowing', serviceRequest);
                                  
                                  // For seasonal contracts, check the actual service frequency
                                  // getServiceFrequency should already return the actual frequency (weekly/bi-weekly/monthly)
                                  // but if it returns 'seasonal-contract', we need to extract the actual frequency
                                  let actualServiceFrequency = serviceFrequency;
                                  if (serviceFrequency === 'seasonal-contract') {
                                    // First check acceptedBid (for bid-accepted customers)
                                    if (serviceRequest?.acceptedBid?.servicePricing) {
                                    const matchingServicePricing = serviceRequest.acceptedBid.servicePricing.find((sp: any) => 
                                      sp.service === 'lawnMowingTrimming' || sp.serviceName?.toLowerCase().includes('mowing')
                                    );
                                    if (matchingServicePricing?.seasonalContractDetails?.serviceFrequency) {
                                      actualServiceFrequency = matchingServicePricing.seasonalContractDetails.serviceFrequency;
                                    }
                                  }
                                  
                                    // CRITICAL: Check customer.servicePricing (for onboarded customers)
                                    // This is the PRIMARY source for onboarded seasonal contracts
                                    // Type assertion: servicePricing may have seasonalContractDetails even if TypeScript doesn't know it
                                    const lawnMowingPricing = customerDetailsDialog.customer.servicePricing?.lawnMowingTrimming as any;
                                    if (actualServiceFrequency === 'seasonal-contract' && lawnMowingPricing?.seasonalContractDetails?.serviceFrequency) {
                                      actualServiceFrequency = lawnMowingPricing.seasonalContractDetails.serviceFrequency;
                                      console.log('[CUSTOMER-DETAILS] ✅ Extracted serviceFrequency from customer.servicePricing for onboarded seasonal contract:', {
                                        service: 'mowing',
                                        serviceFrequency: actualServiceFrequency,
                                        note: 'Onboarded customer - using customer.servicePricing.seasonalContractDetails.serviceFrequency'
                                      });
                                    }
                                  }
                                  
                                  // For per-instance services (including seasonal contracts with per-instance service frequency), show "Per Instance"
                                  if (actualServiceFrequency === 'per-instance') {
                                    return <Typography variant="body2" fontWeight="medium">Per Instance</Typography>;
                                  }
                                  
                                  // For weekly, bi-weekly, monthly (and seasonal contracts with recurring serviceFrequency), show the schedule
                                  // SINGLE SOURCE OF TRUTH: Use scheduledDate from ServiceRequest when available (for today or upcoming)
                                  // This ensures the display matches what's actually scheduled
                                  let dayOfWeek: number | undefined = undefined;
                                  let dayOfMonth: number | undefined = undefined;
                                  
                                  // Get the mowing service request first (needed for fallback)
                                  const mowingServiceRequest = customerServiceRequests.find((sr: any) => sr.serviceType?.category === 'mowing');
                                  
                                  // CRITICAL: Schedule display shows the RECURRING schedule pattern, NOT one-time changes
                                  // PRIORITY 1: Use Bid's recurringDayOfWeek as PRIMARY source (represents the recurring pattern)
                                  // This ensures Schedule shows the permanent recurring day, not temporary one-time changes
                                  if ((actualServiceFrequency === 'weekly' || actualServiceFrequency === 'bi-weekly') && serviceRequest?.acceptedBid?.servicePricing && Array.isArray(serviceRequest.acceptedBid.servicePricing)) {
                                    const matchingServicePricing = serviceRequest.acceptedBid.servicePricing.find((sp: any) => 
                                      sp.service === 'lawnMowingTrimming' || sp.serviceName?.toLowerCase().includes('mowing')
                                    );
                                    
                                    // Check if recurringDayOfWeek is set (including 0 for Sunday)
                                    if (matchingServicePricing && typeof matchingServicePricing.recurringDayOfWeek === 'number') {
                                        dayOfWeek = matchingServicePricing.recurringDayOfWeek;
                                        const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
                                      const bidDayOfWeek = matchingServicePricing.recurringDayOfWeek;
                                      console.log('[CUSTOMER-DETAILS] ✅ Using Bid recurringDayOfWeek as PRIMARY source (recurring pattern):', {
                                          service: 'mowing',
                                          dayOfWeek,
                                        dayName: dayNames[bidDayOfWeek],
                                        frequency: actualServiceFrequency,
                                        bidRecurringDayOfWeek: matchingServicePricing.recurringDayOfWeek,
                                        note: 'Bid recurringDayOfWeek represents the permanent recurring schedule - not affected by one-time changes'
                                        });
                                      } else if (actualServiceFrequency === 'bi-weekly' && matchingServicePricing?.biWeeklyStartDate) {
                                        const biWeeklyStartDate = new Date(matchingServicePricing.biWeeklyStartDate);
                                        dayOfWeek = biWeeklyStartDate.getUTCDay();
                                        const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
                                      const calculatedDayOfWeek = biWeeklyStartDate.getUTCDay();
                                      console.log('[CUSTOMER-DETAILS] ✅ Using Bid biWeeklyStartDate as PRIMARY source (recurring pattern):', {
                                          service: 'mowing',
                                          biWeeklyStartDate: matchingServicePricing.biWeeklyStartDate,
                                          dayOfWeek,
                                        dayName: dayNames[calculatedDayOfWeek],
                                        note: 'Bid biWeeklyStartDate represents the permanent recurring schedule - not affected by one-time changes'
                                      });
                                    } else {
                                      console.log('[CUSTOMER-DETAILS] ⚠️ Bid recurringDayOfWeek not available for mowing service:', {
                                        service: 'mowing',
                                        hasMatchingServicePricing: !!matchingServicePricing,
                                        recurringDayOfWeek: matchingServicePricing?.recurringDayOfWeek,
                                        biWeeklyStartDate: matchingServicePricing?.biWeeklyStartDate,
                                        frequency: actualServiceFrequency,
                                        note: 'Will fallback to scheduledDate or other sources'
                                        });
                                      }
                                    }
                                    
                                  // PRIORITY 2: For ServiceRequests without acceptedBid, use service-specific recurringDayOfWeek from servicePricing
                                  // CRITICAL: Each service has its own recurringDayOfWeek, not shared dayOfWeek
                                  // This is the proper source of truth for onboarded customers - it represents the permanent recurring schedule
                                  // and is NOT affected by one-time changes (only updated on first-time scheduling and permanent changes)
                                  if (dayOfWeek === undefined && (actualServiceFrequency === 'weekly' || actualServiceFrequency === 'bi-weekly') && !mowingServiceRequest?.acceptedBid) {
                                    // PRIORITY 2A: Try to get day of week from service-specific pricing (most accurate)
                                    const mowingPricing = customerDetailsDialog.customer.servicePricing?.lawnMowingTrimming;
                                    if (mowingPricing && typeof mowingPricing === 'object' && 'recurringDayOfWeek' in mowingPricing) {
                                      dayOfWeek = (mowingPricing as any).recurringDayOfWeek;
                                      const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
                                      console.log('[CUSTOMER-DETAILS] ✅ Using service-specific recurringDayOfWeek from servicePricing.lawnMowingTrimming (ServiceRequest has no acceptedBid):', {
                                        service: 'mowing',
                                        recurringDayOfWeek: dayOfWeek,
                                        dayName: dayOfWeek !== undefined ? dayNames[dayOfWeek] : 'Unknown',
                                        note: 'Using service-specific recurringDayOfWeek as source of truth (not affected by one-time changes)'
                                      });
                                    }
                                  }
                                  
                                  // PRIORITY 3: For bid-accepted customers, if Bid is missing recurringDayOfWeek, this indicates a data issue
                                  // Backend should ALWAYS set recurringDayOfWeek on first-time scheduling
                                  // If it's missing, log a warning but don't use scheduledDate as fallback (Bid is source of truth)
                                  if (dayOfWeek === undefined && (actualServiceFrequency === 'weekly' || actualServiceFrequency === 'bi-weekly') && mowingServiceRequest?.acceptedBid) {
                                    console.warn('[CUSTOMER-DETAILS] ⚠️ Bid-accepted customer missing recurringDayOfWeek - Schedule cannot be displayed:', {
                                      service: 'mowing',
                                      hasAcceptedBid: !!mowingServiceRequest?.acceptedBid,
                                      hasScheduledDate: !!mowingServiceRequest?.scheduledDate,
                                      note: 'Backend should have set recurringDayOfWeek on first-time scheduling - Bid is source of truth'
                                    });
                                  }
                                  
                                  // For monthly services: Get recurringDayOfMonth from Bid (PRIORITY 1)
                                  if (actualServiceFrequency === 'monthly' && serviceRequest?.acceptedBid?.servicePricing && Array.isArray(serviceRequest.acceptedBid.servicePricing)) {
                                    const matchingServicePricing = serviceRequest.acceptedBid.servicePricing.find((sp: any) => 
                                      sp.service === 'lawnMowingTrimming' || sp.serviceName?.toLowerCase().includes('mowing')
                                    );
                                    
                                      if (matchingServicePricing?.recurringDayOfMonth !== undefined && matchingServicePricing?.recurringDayOfMonth !== null) {
                                        dayOfMonth = matchingServicePricing.recurringDayOfMonth;
                                      
                                      // CRITICAL: Check if Bid's recurringDayOfMonth doesn't match current scheduledDate
                                      // This indicates the scheduledDate is from a one-time change, and we should use the Bid's value
                                      if (mowingServiceRequest?.scheduledDate) {
                                        const scheduledDateDayOfMonth = new Date(mowingServiceRequest.scheduledDate).getUTCDate();
                                        if (dayOfMonth !== scheduledDateDayOfMonth) {
                                          console.log('[CUSTOMER-DETAILS] ✅ Using Bid recurringDayOfMonth (mismatch with scheduledDate indicates one-time change):', {
                                            service: 'mowing',
                                            bidRecurringDayOfMonth: dayOfMonth,
                                            scheduledDateDayOfMonth,
                                            scheduledDate: mowingServiceRequest.scheduledDate,
                                            note: 'Bid recurringDayOfMonth represents the permanent recurring schedule - scheduledDate is from one-time change'
                                          });
                                        } else {
                                          console.log('[CUSTOMER-DETAILS] ✅ Using Bid recurringDayOfMonth as PRIMARY source (recurring pattern):', {
                                          service: 'mowing',
                                          dayOfMonth,
                                            note: 'Bid recurringDayOfMonth represents the permanent recurring schedule - matches scheduledDate'
                                          });
                                        }
                                      } else {
                                        console.log('[CUSTOMER-DETAILS] ✅ Using Bid recurringDayOfMonth as PRIMARY source (recurring pattern):', {
                                          service: 'mowing',
                                          dayOfMonth,
                                          note: 'Bid recurringDayOfMonth represents the permanent recurring schedule - not affected by one-time changes'
                                        });
                                      }
                                    }
                                  }
                                  
                                  // For monthly services: Create reference date using Bid's recurringDayOfMonth
                                  // CRITICAL: For bid-accepted customers, Bid.recurringDayOfMonth is the SINGLE SOURCE OF TRUTH
                                  // It represents the permanent recurring schedule and is NOT affected by one-time changes
                                  // For onboarded customers (no Bid), scheduledDate is used as a fallback
                                  let referenceDate: Date | string | undefined = undefined;
                                  if (actualServiceFrequency === 'monthly') {
                                    // Use Bid's recurringDayOfMonth - this represents the permanent recurring pattern
                                    let dayOfMonthToUse: number | undefined = dayOfMonth;
                                    
                                    // PRIORITY 2: For ServiceRequests without acceptedBid, use service-specific recurringDayOfMonth from servicePricing
                                    // CRITICAL: Each service has its own recurringDayOfMonth, not shared firstServiceDate
                                    // This is the proper source of truth for onboarded customers - it represents the permanent recurring schedule
                                    // and is NOT affected by one-time changes (only updated on first-time scheduling and permanent changes)
                                    if (dayOfMonthToUse === undefined && !mowingServiceRequest?.acceptedBid) {
                                      // PRIORITY 2A: Try to get day of month from service-specific pricing (most accurate)
                                      const mowingPricing = customerDetailsDialog.customer.servicePricing?.lawnMowingTrimming;
                                      if (mowingPricing && typeof mowingPricing === 'object' && 'recurringDayOfMonth' in mowingPricing) {
                                        dayOfMonthToUse = (mowingPricing as any).recurringDayOfMonth;
                                        console.log('[CUSTOMER-DETAILS] ✅ Using service-specific recurringDayOfMonth from servicePricing.lawnMowingTrimming (ServiceRequest has no acceptedBid):', {
                                      service: 'mowing',
                                          recurringDayOfMonth: dayOfMonthToUse,
                                          note: 'Using service-specific recurringDayOfMonth as source of truth (not affected by one-time changes)'
                                        });
                                      } else if (mowingServiceRequest?.scheduledDate) {
                                        // PRIORITY 2C: Final fallback to scheduledDate only if neither is available
                                    const scheduledDate = new Date(mowingServiceRequest.scheduledDate);
                                        dayOfMonthToUse = scheduledDate.getUTCDate();
                                        console.log('[CUSTOMER-DETAILS] ⚠️ Using scheduledDate as final fallback (service-specific recurringDayOfMonth and firstServiceDate not available):', {
                                      service: 'mowing',
                                      scheduledDate: mowingServiceRequest.scheduledDate,
                                          extractedDayOfMonth: dayOfMonthToUse,
                                          note: 'Both service-specific recurringDayOfMonth and firstServiceDate missing - using scheduledDate (may include one-time changes)'
                                        });
                                      }
                                    }
                                    
                                    // PRIORITY 3: For bid-accepted customers, if Bid is missing recurringDayOfMonth, this indicates a data issue
                                    // Backend should ALWAYS set recurringDayOfMonth on first-time scheduling
                                    // CRITICAL: We do NOT use scheduledDate as a fallback for bid-accepted customers
                                    // The Bid is the source of truth - if it's missing, that's a data corruption issue that needs to be fixed
                                    if (dayOfMonthToUse === undefined && mowingServiceRequest?.acceptedBid) {
                                      console.warn('[CUSTOMER-DETAILS] ⚠️ Bid-accepted customer missing recurringDayOfMonth - Schedule cannot be displayed:', {
                                        service: 'mowing',
                                        hasAcceptedBid: !!mowingServiceRequest?.acceptedBid,
                                        hasScheduledDate: !!mowingServiceRequest?.scheduledDate,
                                        note: 'Backend should have set recurringDayOfMonth on first-time scheduling - Bid is source of truth. This is a data issue that needs to be fixed.'
                                      });
                                    }
                                    
                                    if (dayOfMonthToUse === undefined) {
                                      // For monthly services, we need recurringDayOfMonth from Bid or scheduledDate
                                      console.warn('[CUSTOMER-DETAILS] ⚠️ Monthly service missing both recurringDayOfMonth and scheduledDate - Schedule cannot be displayed:', {
                                        service: 'mowing',
                                        hasAcceptedBid: !!mowingServiceRequest?.acceptedBid,
                                        hasScheduledDate: !!mowingServiceRequest?.scheduledDate,
                                        note: 'Schedule display requires recurringDayOfMonth from Bid or scheduledDate'
                                      });
                                    } else {
                                      console.log('[CUSTOMER-DETAILS] ✅ Using dayOfMonth for monthly Schedule display:', {
                                        service: 'mowing',
                                        dayOfMonth: dayOfMonthToUse,
                                        source: dayOfMonth !== undefined ? 'Bid recurringDayOfMonth' : 'scheduledDate (fallback)',
                                        note: 'Bid recurringDayOfMonth represents the permanent recurring schedule - not affected by one-time changes'
                                      });
                                    }
                                    
                                    if (dayOfMonthToUse !== undefined) {
                                      // Create a date with the day of month (use current month/year, day doesn't matter for display)
                                      const today = new Date();
                                      const year = today.getUTCFullYear();
                                      const month = today.getUTCMonth();
                                      // Use the dayOfMonthToUse, but ensure it's valid (1-31)
                                      const validDayOfMonth = Math.max(1, Math.min(31, dayOfMonthToUse));
                                      referenceDate = new Date(Date.UTC(year, month, validDayOfMonth, 0, 0, 0, 0));
                                      console.log('[CUSTOMER-DETAILS] ✅ Created reference date for monthly Schedule display:', {
                                        service: 'mowing',
                                        dayOfMonth: dayOfMonthToUse,
                                        referenceDate: referenceDate.toISOString(),
                                        source: dayOfMonth !== undefined && dayOfMonth === dayOfMonthToUse ? 'Bid recurringDayOfMonth' : 
                                                mowingServiceRequest?.scheduledDate ? 'scheduledDate' : 'completedDate'
                                      });
                                    }
                                    
                                    if (!referenceDate) {
                                      // If neither recurringDayOfMonth nor scheduledDate is available, don't show a schedule (data issue)
                                      console.warn('[CUSTOMER-DETAILS] ⚠️ Monthly service missing both recurringDayOfMonth and scheduledDate:', {
                                        service: 'mowing',
                                        note: 'Schedule display requires recurringDayOfMonth from Bid or scheduledDate - cannot display schedule'
                                      });
                                    }
                                  } else if (actualServiceFrequency === 'weekly' || actualServiceFrequency === 'bi-weekly') {
                                    // For weekly/bi-weekly, use nextServiceDate or lastServiceDate as reference
                                    // CRITICAL: Only use customer-level fields if service has been scheduled (has scheduledDate)
                                    // This prevents showing schedule for unscheduled services
                                    if (mowingServiceRequest?.scheduledDate) {
                                    referenceDate = customerDetailsDialog.customer.nextServiceDate 
                                      || customerDetailsDialog.customer.lastServiceDate
                                      || mowingServiceRequest?.scheduledDate;
                                  }
                                  }
                                  // For monthly services, referenceDate is only set if dayOfMonthToUse is defined (above)
                                  // If dayOfMonthToUse is undefined, referenceDate remains undefined and we show "Not scheduled"
                                  
                                  if (actualServiceFrequency && actualServiceFrequency !== 'seasonal-contract') {
                                    // Only show schedule if we have the required data
                                    // For monthly: need referenceDate (set from dayOfMonthToUse)
                                    // For weekly/bi-weekly: need dayOfWeek
                                    if (actualServiceFrequency === 'monthly' && referenceDate) {
                                      return (
                                        <Typography variant="body2" fontWeight="medium">
                                          {getScheduleDisplay(actualServiceFrequency, dayOfWeek, referenceDate)}
                                        </Typography>
                                      );
                                    } else if ((actualServiceFrequency === 'weekly' || actualServiceFrequency === 'bi-weekly') && dayOfWeek !== undefined) {
                                    return (
                                      <Typography variant="body2" fontWeight="medium">
                                        {getScheduleDisplay(actualServiceFrequency, dayOfWeek, referenceDate)}
                                      </Typography>
                                    );
                                  }
                                    // If required data is missing, show "Not scheduled"
                                    return <Typography variant="body2" color="text.secondary">Not scheduled</Typography>;
                                  }
                                  // If actualServiceFrequency is still 'seasonal-contract' (couldn't determine serviceFrequency), show "Not scheduled"
                                  return <Typography variant="body2" color="text.secondary">Not scheduled</Typography>;
                                })()}
                              </Grid>
                              {(() => {
                                // Show contract dates only for seasonal contracts (Mowing)
                                const serviceFrequency = customerDetailsDialog.customer.servicePricing?.lawnMowingTrimming?.frequency;
                                const serviceRequest = customerServiceRequests.find((sr: any) => sr.serviceType?.category === 'mowing');
                                let isSeasonal = serviceFrequency === 'seasonal-contract' || customerDetailsDialog.customer.paymentStructure?.type === 'seasonal-contract';
                                
                                // Also check service request's accepted bid
                                if (!isSeasonal && serviceRequest?.acceptedBid?.servicePricing) {
                                  const matchingServicePricing = serviceRequest.acceptedBid.servicePricing.find((sp: any) => 
                                    sp.service === 'lawnMowingTrimming' || sp.serviceName?.toLowerCase().includes('mowing')
                                  );
                                  if (matchingServicePricing?.isSeasonalContract || matchingServicePricing?.frequency === 'seasonal-contract') {
                                    isSeasonal = true;
                                  }
                                }
                                
                                if (!isSeasonal) return null;
                                
                                // Extract contract dates from the same sources we use for other seasonal contract data
                                // SINGLE SOURCE OF TRUTH: For bid-accepted customers, prioritize acceptedBid.servicePricing
                                // This ensures consistency with customer's My Services view and avoids stale data
                                let contractStartDate: string | undefined = undefined;
                                let contractEndDate: string | undefined = undefined;
                                
                                // PRIORITY 1: Check accepted bid first (for bid-accepted customers - this is the source of truth)
                                if (serviceRequest?.acceptedBid?.servicePricing) {
                                  const matchingServicePricing = serviceRequest.acceptedBid.servicePricing.find((sp: any) => 
                                    sp.service === 'lawnMowingTrimming' || sp.serviceName?.toLowerCase().includes('mowing')
                                  );
                                  if (matchingServicePricing?.seasonalContractDetails) {
                                    contractStartDate = matchingServicePricing.seasonalContractDetails.contractStartDate;
                                    contractEndDate = matchingServicePricing.seasonalContractDetails.contractEndDate;
                                  }
                                }
                                
                                // PRIORITY 2: Fallback to customer.servicePricing (for onboarded customers without acceptedBid)
                                if ((!contractStartDate || !contractEndDate)) {
                                  const lawnMowingPricing = customerDetailsDialog.customer.servicePricing?.lawnMowingTrimming as any;
                                  if (lawnMowingPricing?.seasonalContractDetails) {
                                    if (!contractStartDate) contractStartDate = lawnMowingPricing.seasonalContractDetails.contractStartDate;
                                    if (!contractEndDate) contractEndDate = lawnMowingPricing.seasonalContractDetails.contractEndDate;
                                  }
                                }
                                
                                if (!contractStartDate && !contractEndDate) return null;
                                
                                const formatDate = (dateStr: string | undefined) => {
                                  if (!dateStr) return 'N/A';
                                  try {
                                    const date = new Date(dateStr);
                                    // CRITICAL: Extract UTC date components directly (dates are stored as UTC midnight representing calendar dates)
                                    // This prevents timezone conversion issues where end dates appear 1 day early
                                    const year = date.getUTCFullYear();
                                    const month = date.getUTCMonth();
                                    const day = date.getUTCDate();
                                    // Format using UTC components to avoid timezone shift
                                    const formattedDate = new Date(Date.UTC(year, month, day, 12, 0, 0, 0));
                                    return formattedDate.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric', timeZone: 'UTC' });
                                  } catch {
                                    return dateStr;
                                  }
                                };
                                
                                // Extract payment schedule, total contract price, and monthly payment amount
                                // SINGLE SOURCE OF TRUTH: Use stored monthlyPaymentAmount from subscription creation, not recalculate
                                let paymentSchedule: 'single-payment' | 'monthly' | undefined = undefined;
                                let totalContractPrice: number | undefined = undefined;
                                let monthlyPaymentAmount: number | undefined = undefined;
                                
                                // PRIORITY 1: Check accepted bid first (for bid-accepted customers - this is the source of truth for contract details)
                                if (serviceRequest?.acceptedBid?.servicePricing) {
                                  const matchingServicePricing = serviceRequest.acceptedBid.servicePricing.find((sp: any) => 
                                    sp.service === 'lawnMowingTrimming' || sp.serviceName?.toLowerCase().includes('mowing')
                                  );
                                  if (matchingServicePricing?.seasonalContractDetails) {
                                    paymentSchedule = matchingServicePricing.seasonalContractDetails.paymentSchedule;
                                    totalContractPrice = matchingServicePricing.seasonalContractDetails.totalContractPrice;
                                    // Note: Bids don't store monthlyPaymentAmount - it's stored in ExistingCustomer when subscription is created
                                  }
                                }
                                
                                // PRIORITY 2: Fallback to customer.servicePricing (for onboarded customers without acceptedBid)
                                // This is also where monthlyPaymentAmount is stored (single source of truth from subscription creation)
                                if (!paymentSchedule || !totalContractPrice || !monthlyPaymentAmount) {
                                  const lawnMowingPricing = customerDetailsDialog.customer.servicePricing?.lawnMowingTrimming as any;
                                  if (lawnMowingPricing?.seasonalContractDetails) {
                                    if (!paymentSchedule) paymentSchedule = lawnMowingPricing.seasonalContractDetails.paymentSchedule;
                                    if (!totalContractPrice) totalContractPrice = lawnMowingPricing.seasonalContractDetails.totalContractPrice;
                                    // Use stored monthlyPaymentAmount (single source of truth from subscription creation)
                                    if (!monthlyPaymentAmount && paymentSchedule === 'monthly') {
                                      monthlyPaymentAmount = lawnMowingPricing.seasonalContractDetails.monthlyPaymentAmount;
                                      console.log('[CUSTOMER-DETAILS] Retrieved monthlyPaymentAmount from customer.servicePricing:', {
                                        service: 'mowing',
                                        storedMonthlyPaymentAmount: monthlyPaymentAmount,
                                        totalContractPrice,
                                        contractStartDate,
                                        contractEndDate,
                                        paymentSchedule,
                                        note: 'This is the stored value from subscription creation'
                                      });
                                    }
                                  }
                                }
                                
                                // FALLBACK: Only recalculate if stored value is missing (shouldn't happen after subscription is created)
                                if (paymentSchedule === 'monthly' && !monthlyPaymentAmount && totalContractPrice && contractStartDate && contractEndDate) {
                                  try {
                                    const start = new Date(contractStartDate);
                                    const end = new Date(contractEndDate);
                                    const startsOnFirst = start.getUTCDate() === 1;
                                    
                                    let billingMonths: number;
                                    if (startsOnFirst) {
                                      // Contract starts on 1st: Include that month in count - use UTC components
                                      billingMonths = (end.getUTCFullYear() - start.getUTCFullYear()) * 12 + 
                                                      (end.getUTCMonth() - start.getUTCMonth()) + 1; // +1 to include both start and end months
                                    } else {
                                      // Contract starts mid-month: Exclude partial first month - use UTC components
                                      const nextMonthYear = start.getUTCMonth() === 11 ? start.getUTCFullYear() + 1 : start.getUTCFullYear();
                                      const nextMonthMonth = (start.getUTCMonth() + 1) % 12;
                                      billingMonths = (end.getUTCFullYear() - nextMonthYear) * 12 + 
                                                      (end.getUTCMonth() - nextMonthMonth) + 1; // +1 to include end month
                                    }
                                    
                                    if (billingMonths > 0) {
                                      monthlyPaymentAmount = Math.round((totalContractPrice / billingMonths) * 100) / 100;
                                      console.warn('[CUSTOMER-DETAILS] ⚠️ Recalculated monthlyPaymentAmount (stored value missing):', {
                                        service: 'mowing',
                                        recalculated: monthlyPaymentAmount,
                                        totalContractPrice,
                                        billingMonths,
                                        contractStartDate,
                                        contractEndDate,
                                        storedValue: customerDetailsDialog.customer.servicePricing?.lawnMowingTrimming?.seasonalContractDetails?.monthlyPaymentAmount,
                                        note: 'Should use stored value from subscription creation - this is a fallback'
                                      });
                                    }
                                  } catch (e) {
                                    console.error('[CUSTOMER-DETAILS] Error calculating monthly payment:', e);
                                  }
                                }
                                
                                return (
                                  <>
                                    <Grid item xs={12} sm={6}>
                                      <Typography variant="caption" color="text.secondary">Contract Period</Typography>
                                      <Typography variant="body2" fontWeight="medium">
                                        {formatDate(contractStartDate)} - {formatDate(contractEndDate)}
                                      </Typography>
                                    </Grid>
                                    {paymentSchedule && (
                                      <Grid item xs={12} sm={6}>
                                        <Typography variant="caption" color="text.secondary">Payment Schedule</Typography>
                                        <Typography variant="body2" fontWeight="medium">
                                          {paymentSchedule === 'monthly' ? 'Monthly Payments' : 'One-Time Payment'}
                                        </Typography>
                                      </Grid>
                                    )}
                                    {paymentSchedule === 'monthly' && monthlyPaymentAmount && (
                                      <Grid item xs={12} sm={6}>
                                        <Typography variant="caption" color="text.secondary">Monthly Payment</Typography>
                                        <Typography variant="body2" fontWeight="medium" color="primary">
                                          ${monthlyPaymentAmount.toFixed(2)}
                                        </Typography>
                                      </Grid>
                                    )}
                                  </>
                                );
                              })()}
                              <Grid item xs={12} sm={6}>
                                <Typography variant="caption" color="text.secondary">Last Service</Typography>
                                {(() => {
                                  if (!customerDetailsDialog.customer) return <Typography variant="body2" color="text.secondary">N/A</Typography>;
                                  
                                  // Check if this is a per-instance service
                                  const serviceFrequency = customerDetailsDialog.customer.servicePricing?.lawnMowingTrimming?.frequency;
                                  
                                  // For per-instance services, only show Last Service Date if there's an actual completed service request
                                  // Don't use customer.lastServiceDate as fallback for per-instance services
                                  if (serviceFrequency === 'per-instance') {
                                    // Get last service date for this specific service category
                                    // CRITICAL: Include both 'completed' status AND 'scheduled' status with completedDate
                                    // After auto-rescheduling, status becomes 'scheduled' but completedDate is preserved
                                    const completedRequests = customerServiceRequests.filter((sr: any) => 
                                      sr.serviceType?.category === 'mowing' && 
                                      sr.completedDate &&
                                      (sr.status === 'completed' || sr.status === 'scheduled')
                                    );
                                    
                                    if (completedRequests.length > 0) {
                                      const mostRecent = completedRequests.reduce((latest: any, current: any) => {
                                        if (!latest.completedDate) return current;
                                        if (!current.completedDate) return latest;
                                        return new Date(current.completedDate) > new Date(latest.completedDate) ? current : latest;
                                      });
                                      
                                      if (mostRecent?.completedDate) {
                                        // Extract UTC date components directly (same logic as All Customers chart)
                                        const completedDate = new Date(mostRecent.completedDate);
                                        const year = completedDate.getUTCFullYear();
                                        const month = completedDate.getUTCMonth();
                                        const day = completedDate.getUTCDate();
                                        const normalizedDate = new Date(Date.UTC(year, month, day, 0, 0, 0, 0));
                                        
                                        return (
                                          <Typography variant="body2" fontWeight="medium">
                                            {formatDateMMDDYYYY(normalizedDate.toString())}
                                          </Typography>
                                        );
                                      }
                                    }
                                    
                                    // For per-instance services with no completed requests, show N/A
                                    return <Typography variant="body2" color="text.secondary">N/A</Typography>;
                                  }
                                  
                                  // For recurring services, use the same logic as before
                                  // Get last service date for this specific service category
                                  // CRITICAL: Include both 'completed' status AND 'scheduled' status with completedDate
                                  // After auto-rescheduling, status becomes 'scheduled' but completedDate is preserved
                                  const completedRequests = customerServiceRequests.filter((sr: any) => 
                                    sr.serviceType?.category === 'mowing' && 
                                    sr.completedDate &&
                                    (sr.status === 'completed' || sr.status === 'scheduled')
                                  );
                                  
                                  if (completedRequests.length > 0) {
                                    const mostRecent = completedRequests.reduce((latest: any, current: any) => {
                                      if (!latest.completedDate) return current;
                                      if (!current.completedDate) return latest;
                                      return new Date(current.completedDate) > new Date(latest.completedDate) ? current : latest;
                                    });
                                    
                                    if (mostRecent?.completedDate) {
                                      // Extract UTC date components directly (same logic as All Customers chart)
                                      const completedDate = new Date(mostRecent.completedDate);
                                      const year = completedDate.getUTCFullYear();
                                      const month = completedDate.getUTCMonth();
                                      const day = completedDate.getUTCDate();
                                      const normalizedDate = new Date(Date.UTC(year, month, day, 0, 0, 0, 0));
                                      
                                      return (
                                        <Typography variant="body2" fontWeight="medium">
                                          {formatDateMMDDYYYY(normalizedDate.toString())}
                                        </Typography>
                                      );
                                    }
                                  }
                                  
                                  // IMPORTANT: Don't use customer.lastServiceDate as fallback
                                  // It's a single field that can't represent multiple services correctly
                                  // For customers with multiple services, customer.lastServiceDate might be for a different service
                                  // Return N/A if no completed service request found for this specific category
                                  return <Typography variant="body2" color="text.secondary">N/A</Typography>;
                                })()}
                              </Grid>
                              <Grid item xs={12} sm={6}>
                                <Typography variant="caption" color="text.secondary">Next Service</Typography>
                                {(() => {
                                  if (!customerDetailsDialog.customer) return <Typography variant="body2" color="text.secondary">N/A</Typography>;
                                  
                                  // Use customerServiceRequests (fetched specifically for this dialog) instead of allCustomersServiceRequests
                                  // This ensures we have the most up-to-date data including completedDate
                                  const customerRequests = customerServiceRequests || [];
                                  
                                  const getServiceRequest = (category: string) => {
                                    // Match by category and address (for multi-property customers)
                                    const customerAddress = customerDetailsDialog.customer?.address;
                                    const matching = customerRequests.filter((sr: any) => {
                                      const matchesCategory = sr.serviceType?.category === category;
                                      if (!matchesCategory) return false;
                                      
                                      // Match by address for multi-property customers
                                      const srAddress = sr.address;
                                      if (srAddress && customerAddress) {
                                        const sameStreet = (srAddress.street || '').trim().toLowerCase() === (customerAddress.street || '').trim().toLowerCase();
                                        const sameZip = (srAddress.zipCode || '').trim() === (customerAddress.zipCode || '').trim();
                                        return sameStreet && sameZip;
                                      }
                                      
                                      return true; // Fallback if no address match
                                    });
                                    return selectBestServiceRequestForCategory(
                                      matching,
                                      category as 'mowing' | 'fertilizing' | 'snow_removal'
                                    ) || matching[0] || null;
                                  };
                                  
                                  // Use shared utility function for calculating next service dates (SINGLE SOURCE OF TRUTH)
                                  const getNextServiceDate = (category: string) => {
                                    if (!customerDetailsDialog.customer) return null;
                                    const serviceRequest = getServiceRequest(category);
                                    return getNextServiceDateUtil(
                                      customerDetailsDialog.customer,
                                      category as 'mowing' | 'fertilizing' | 'snow_removal',
                                      serviceRequest,
                                      customerRequests
                                    );
                                  };
                                  
                                  const nextDate = getNextServiceDate('mowing');
                                  
                                  if (nextDate) {
                                    return (
                                      <Typography variant="body2" fontWeight="medium">
                                        {formatDateMMDDYYYY(nextDate.toString())}
                                      </Typography>
                                    );
                                  }
                                  
                                  return <Typography variant="body2" color="text.secondary">N/A</Typography>;
                                })()}
                              </Grid>
                              {(() => {
                                // Show "Mark Season Ended" button for recurring services (not seasonal contracts, not per-instance)
                                const serviceRequest = customerServiceRequests.find((sr: any) => sr.serviceType?.category === 'mowing');
                                const serviceFrequency = getServiceFrequency(customerDetailsDialog.customer, 'mowing', serviceRequest);
                                
                                // Determine if this is a seasonal contract
                                let isSeasonalContract = serviceFrequency === 'seasonal-contract';
                                if (!isSeasonalContract && serviceRequest?.acceptedBid?.servicePricing) {
                                  const matchingServicePricing = serviceRequest.acceptedBid.servicePricing.find((sp: any) => 
                                    sp.service === 'lawnMowingTrimming' || sp.serviceName?.toLowerCase().includes('mowing')
                                  );
                                  if (matchingServicePricing?.isSeasonalContract || matchingServicePricing?.frequency === 'seasonal-contract') {
                                    isSeasonalContract = true;
                                  }
                                }
                                
                                // Show for recurring, per-instance, or seasonal-contract + per-instance. Hide for seasonal + weekly/bi-weekly/monthly.
                                const isRecurring = serviceFrequency === 'weekly' || serviceFrequency === 'bi-weekly' || serviceFrequency === 'monthly';
                                const isPerInstance = serviceFrequency === 'per-instance';
                                const allowSeasonalPerInstance = isSeasonalContract && isPerInstance;
                                const canMarkSeasonEnded = (isRecurring || isPerInstance) && (!isSeasonalContract || allowSeasonalPerInstance) && serviceRequest && 
                                                          (serviceRequest.status === 'completed' || serviceRequest.status === 'scheduled' || serviceRequest.status === 'season_ended');
                                
                                if (!canMarkSeasonEnded) return null;
                                
                                return (
                                  <Grid item xs={12}>
                                    <Box display="flex" justifyContent="flex-end" mt={1}>
                                      <Button
                                        variant="outlined"
                                        size="small"
                                        onClick={() => {
                                          if (serviceRequest?._id || serviceRequest?.id) {
                                            setSeasonEndConfirmDialog({ 
                                              open: true, 
                                              serviceRequestId: serviceRequest._id || serviceRequest.id, 
                                              serviceCategory: 'mowing' 
                                            });
                                          }
                                        }}
                                        disabled={markingSeasonEnded === (serviceRequest?._id || serviceRequest?.id)}
                                        sx={{
                                          borderColor: serviceRequest?.status === 'season_ended' ? 'primary.main' : 'error.main',
                                          color: serviceRequest?.status === 'season_ended' ? 'primary.main' : 'error.main',
                                          fontWeight: 'bold',
                                          '&:hover': {
                                            borderColor: serviceRequest?.status === 'season_ended' ? 'primary.dark' : 'error.dark',
                                            backgroundColor: serviceRequest?.status === 'season_ended' ? 'primary.light' : 'error.light',
                                            color: serviceRequest?.status === 'season_ended' ? 'primary.dark' : 'error.dark'
                                          }
                                        }}
                                        startIcon={serviceRequest?.status === 'season_ended' ? undefined : <CheckCircle />}
                                      >
                                        {serviceRequest?.status === 'season_ended'
                                          ? 'Send notification & add to Renewals'
                                          : 'Mark Season Ended'}
                                      </Button>
                                    </Box>
                                  </Grid>
                                );
                              })()}
                            </Grid>
                          </CardContent>
                        </Card>
                      )}

                      {/* Weed Control Service */}
                      {/* SINGLE SOURCE OF TRUTH: Only show service if there's a ServiceRequest for THIS property address */}
                      {/* ServiceRequest is filtered by providerId (backend) and address (frontend), so it's the ultimate source of truth */}
                      {/* CRITICAL: Do NOT check servicePricing - it may contain services from other providers */}
                      {customerServiceRequests.some((sr: any) => sr.serviceType?.category === 'fertilizing') && (
                        <Card variant="outlined">
                          <CardContent>
                            <Box display="flex" justifyContent="space-between" alignItems="start" mb={1}>
                              <Typography variant="h6" fontSize="1rem">
                                Weed Control & Fertilizing
                              </Typography>
                              <Chip label="Active" size="small" color="success" />
                            </Box>
                            <Grid container spacing={2}>
                              <Grid item xs={12} sm={6}>
                                <Typography variant="caption" color="text.secondary">Price</Typography>
                                <Box display="flex" alignItems="center" gap={1}>
                                  <Typography variant="body2" fontWeight="medium">
                                    {(() => {
                                      // Check servicePricing first
                                      let price = customerDetailsDialog.customer.servicePricing?.weedControlFertilizing?.price;
                                      
                                      // If price is missing or 0, check service request's accepted bid
                                      if (!price || Number(price) === 0) {
                                        const serviceRequest = customerServiceRequests.find((sr: any) => sr.serviceType?.category === 'fertilizing');
                                        if (serviceRequest?.acceptedBid) {
                                          // Check if bid has servicePricing array
                                          if (serviceRequest.acceptedBid.servicePricing && Array.isArray(serviceRequest.acceptedBid.servicePricing)) {
                                            const matchingServicePricing = serviceRequest.acceptedBid.servicePricing.find((sp: any) => 
                                              sp.service === 'weedControlFertilizing' || sp.serviceName?.toLowerCase().includes('fertiliz') || sp.serviceName?.toLowerCase().includes('weed')
                                            );
                                            if (matchingServicePricing?.price) {
                                              price = matchingServicePricing.price;
                                            }
                                          }
                                          // Fallback to bid.price
                                          if (!price && serviceRequest.acceptedBid.price) {
                                            price = serviceRequest.acceptedBid.price;
                                          }
                                        }
                                      }
                                      
                                      return price && Number(price) > 0 
                                        ? `$${Number(price).toFixed(2)}`
                                        : 'N/A';
                                    })()}
                                  </Typography>
                                  {(() => {
                                    const serviceFrequency = customerDetailsDialog.customer.servicePricing?.weedControlFertilizing?.frequency;
                                    const serviceRequest = customerServiceRequests.find((sr: any) => sr.serviceType?.category === 'fertilizing');
                                    // CRITICAL: Only check service-specific seasonal contract status, NOT customer-level paymentStructure
                                    // paymentStructure.type is customer-level and may be 'seasonal-contract' if ANY service is seasonal
                                    // We need to check each service individually
                                    let isSeasonal = serviceFrequency === 'seasonal-contract';
                                    
                                    // Also check service request's accepted bid for service-specific seasonal contract status
                                    if (!isSeasonal && serviceRequest?.acceptedBid?.servicePricing) {
                                      const matchingServicePricing = serviceRequest.acceptedBid.servicePricing.find((sp: any) => 
                                        sp.service === 'weedControlFertilizing' || sp.serviceName?.toLowerCase().includes('fertiliz') || sp.serviceName?.toLowerCase().includes('weed')
                                      );
                                      if (matchingServicePricing?.isSeasonalContract || matchingServicePricing?.frequency === 'seasonal-contract') {
                                        isSeasonal = true;
                                      }
                                    }
                                    
                                    return isSeasonal ? (
                                      <Chip 
                                        label="Seasonal Contract" 
                                        size="small" 
                                        color="secondary"
                                        variant="outlined"
                                        sx={{ height: 20, fontSize: '0.65rem' }}
                                      />
                                    ) : null;
                                  })()}
                                </Box>
                              </Grid>
                              <Grid item xs={12} sm={6}>
                                <Typography variant="caption" color="text.secondary">Frequency</Typography>
                                <Typography variant="body2" fontWeight="medium">
                                  {(() => {
                                    const serviceRequest = customerServiceRequests.find((sr: any) => sr.serviceType?.category === 'fertilizing');
                                    const frequency = getServiceFrequency(customerDetailsDialog.customer, 'fertilizing', serviceRequest);
                                    
                                    if (!frequency) return 'N/A';
                                    
                                    // Format frequency for display
                                    if (frequency === 'per-instance') return 'Per Instance';
                                    if (frequency === 'weekly') return 'Weekly';
                                    if (frequency === 'bi-weekly') return 'Bi-Weekly';
                                    if (frequency === 'monthly') return 'Monthly';
                                    if (frequency === 'seasonal-contract') return 'Seasonal Contract';
                                    return frequency.charAt(0).toUpperCase() + frequency.slice(1).replace('-', '-');
                                  })()}
                                </Typography>
                              </Grid>
                              <Grid item xs={12} sm={6}>
                                <Typography variant="caption" color="text.secondary">Schedule</Typography>
                                {(() => {
                                  // Use the SAME logic as All Customers chart - use getServiceFrequency helper
                                  const serviceRequest = customerServiceRequests.find((sr: any) => sr.serviceType?.category === 'fertilizing');
                                  const serviceFrequency = getServiceFrequency(customerDetailsDialog.customer, 'fertilizing', serviceRequest);
                                  
                                  // For seasonal contracts, check the actual service frequency
                                  let actualServiceFrequency = serviceFrequency;
                                  if (serviceFrequency === 'seasonal-contract' && serviceRequest?.acceptedBid?.servicePricing) {
                                    const matchingServicePricing = serviceRequest.acceptedBid.servicePricing.find((sp: any) => 
                                      sp.service === 'weedControlFertilizing' || sp.serviceName?.toLowerCase().includes('fertiliz') || sp.serviceName?.toLowerCase().includes('weed')
                                    );
                                    if (matchingServicePricing?.seasonalContractDetails?.serviceFrequency) {
                                      actualServiceFrequency = matchingServicePricing.seasonalContractDetails.serviceFrequency;
                                    }
                                  }
                                  
                                  // For per-instance services (including seasonal contracts with per-instance service frequency), show "Per Instance"
                                  if (actualServiceFrequency === 'per-instance') {
                                    return <Typography variant="body2" fontWeight="medium">Per Instance</Typography>;
                                  }
                                  
                                  // For weekly, bi-weekly, monthly (and seasonal contracts with recurring serviceFrequency), show the schedule
                                  // IMPORTANT: Get service-specific schedule data from Bid's servicePricing, not the shared serviceSchedule
                                  // This ensures each service shows its correct schedule when customers have multiple services
                                  let dayOfWeek: number | undefined = undefined;
                                  let dayOfMonth: number | undefined = undefined;
                                  
                                  // Get the fertilizing service request first (needed for fallback)
                                  const fertilizingServiceRequest = customerServiceRequests.find((sr: any) => sr.serviceType?.category === 'fertilizing');
                                  
                                  // CRITICAL: Schedule display shows the RECURRING schedule pattern
                                  // PRIORITY 1: Use scheduledDate as PRIMARY source of truth (it's always correct)
                                  // scheduledDate is the actual scheduled date - it's the ground truth for what day/date the service is scheduled
                                  // This ensures Schedule matches Next Service Date (which also uses scheduledDate)
                                  
                                  // CRITICAL: Schedule display shows the RECURRING schedule pattern, NOT one-time changes
                                  // PRIORITY 1: Use Bid's recurringDayOfWeek as PRIMARY source (represents the recurring pattern)
                                  // This ensures Schedule shows the permanent recurring day, not temporary one-time changes
                                  if ((actualServiceFrequency === 'weekly' || actualServiceFrequency === 'bi-weekly') && serviceRequest?.acceptedBid?.servicePricing && Array.isArray(serviceRequest.acceptedBid.servicePricing)) {
                                    const matchingServicePricing = serviceRequest.acceptedBid.servicePricing.find((sp: any) => 
                                      sp.service === 'weedControlFertilizing' || sp.serviceName?.toLowerCase().includes('fertiliz') || sp.serviceName?.toLowerCase().includes('weed')
                                    );
                                    
                                    // Check if recurringDayOfWeek is set (including 0 for Sunday)
                                    if (matchingServicePricing && typeof matchingServicePricing.recurringDayOfWeek === 'number') {
                                        dayOfWeek = matchingServicePricing.recurringDayOfWeek;
                                        const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
                                      const bidDayOfWeek = matchingServicePricing.recurringDayOfWeek;
                                      console.log('[CUSTOMER-DETAILS] ✅ Using Bid recurringDayOfWeek as PRIMARY source (recurring pattern):', {
                                          service: 'fertilizing',
                                          dayOfWeek,
                                        dayName: dayNames[bidDayOfWeek],
                                        frequency: actualServiceFrequency,
                                        bidRecurringDayOfWeek: matchingServicePricing.recurringDayOfWeek,
                                        note: 'Bid recurringDayOfWeek represents the permanent recurring schedule - not affected by one-time changes'
                                        });
                                      } else if (actualServiceFrequency === 'bi-weekly' && matchingServicePricing?.biWeeklyStartDate) {
                                        const biWeeklyStartDate = new Date(matchingServicePricing.biWeeklyStartDate);
                                        dayOfWeek = biWeeklyStartDate.getUTCDay();
                                        const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
                                      const calculatedDayOfWeek = biWeeklyStartDate.getUTCDay();
                                      console.log('[CUSTOMER-DETAILS] ✅ Using Bid biWeeklyStartDate as PRIMARY source (recurring pattern):', {
                                          service: 'fertilizing',
                                          biWeeklyStartDate: matchingServicePricing.biWeeklyStartDate,
                                          dayOfWeek,
                                        dayName: dayNames[calculatedDayOfWeek],
                                        note: 'Bid biWeeklyStartDate represents the permanent recurring schedule - not affected by one-time changes'
                                      });
                                    } else {
                                      console.log('[CUSTOMER-DETAILS] ⚠️ Bid recurringDayOfWeek not available for fertilizing service:', {
                                        service: 'fertilizing',
                                        hasMatchingServicePricing: !!matchingServicePricing,
                                        recurringDayOfWeek: matchingServicePricing?.recurringDayOfWeek,
                                        biWeeklyStartDate: matchingServicePricing?.biWeeklyStartDate,
                                        frequency: actualServiceFrequency,
                                        note: 'Will fallback to scheduledDate or other sources'
                                        });
                                      }
                                    }
                                    
                                  // PRIORITY 2: For ServiceRequests without acceptedBid, use service-specific recurringDayOfWeek from servicePricing
                                  // CRITICAL: Each service has its own recurringDayOfWeek, not shared dayOfWeek
                                  // This is the proper source of truth for onboarded customers - it represents the permanent recurring schedule
                                  // and is NOT affected by one-time changes (only updated on first-time scheduling and permanent changes)
                                  if (dayOfWeek === undefined && (actualServiceFrequency === 'weekly' || actualServiceFrequency === 'bi-weekly') && !fertilizingServiceRequest?.acceptedBid) {
                                    // PRIORITY 2A: Try to get day of week from service-specific pricing (most accurate)
                                    const fertilizingPricing = customerDetailsDialog.customer.servicePricing?.weedControlFertilizing;
                                    if (fertilizingPricing && typeof fertilizingPricing === 'object' && 'recurringDayOfWeek' in fertilizingPricing) {
                                      dayOfWeek = (fertilizingPricing as any).recurringDayOfWeek;
                                        const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
                                        console.log('[CUSTOMER-DETAILS] ✅ Using service-specific recurringDayOfWeek from servicePricing.weedControlFertilizing (ServiceRequest has no acceptedBid):', {
                                          service: 'fertilizing',
                                          recurringDayOfWeek: dayOfWeek,
                                          dayName: dayOfWeek !== undefined ? dayNames[dayOfWeek] : 'Unknown',
                                          note: 'Using service-specific recurringDayOfWeek as source of truth (not affected by one-time changes)'
                                        });
                                    } else if (fertilizingServiceRequest?.scheduledDate && customerDetailsDialog.customer.serviceSchedule?.dayOfWeek !== undefined) {
                                      // PRIORITY 2B: Fallback to shared dayOfWeek ONLY if service has been scheduled (for backward compatibility with old data)
                                      // CRITICAL: Only use fallback if serviceRequest.scheduledDate exists - this ensures we don't show schedule for unscheduled services
                                      dayOfWeek = customerDetailsDialog.customer.serviceSchedule.dayOfWeek;
                                        const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
                                        console.log('[CUSTOMER-DETAILS] ⚠️ Using shared serviceSchedule.dayOfWeek as fallback (service has been scheduled but service-specific recurringDayOfWeek missing):', {
                                          service: 'fertilizing',
                                          dayOfWeek,
                                          dayName: dayOfWeek !== undefined ? dayNames[dayOfWeek] : 'Unknown',
                                          hasScheduledDate: !!fertilizingServiceRequest?.scheduledDate,
                                          note: 'Service has been scheduled - using shared dayOfWeek for backward compatibility (old data may be missing service-specific field)'
                                        });
                                    }
                                  }
                                  
                                  // PRIORITY 3: For bid-accepted customers, if Bid is missing recurringDayOfWeek, this indicates a data issue
                                  // Backend should ALWAYS set recurringDayOfWeek on first-time scheduling
                                  // If it's missing, log a warning but don't use scheduledDate as fallback (Bid is source of truth)
                                  if (dayOfWeek === undefined && (actualServiceFrequency === 'weekly' || actualServiceFrequency === 'bi-weekly') && fertilizingServiceRequest?.acceptedBid) {
                                    console.warn('[CUSTOMER-DETAILS] ⚠️ Bid-accepted customer missing recurringDayOfWeek - Schedule cannot be displayed:', {
                                      service: 'fertilizing',
                                      hasAcceptedBid: !!fertilizingServiceRequest?.acceptedBid,
                                      hasScheduledDate: !!fertilizingServiceRequest?.scheduledDate,
                                      note: 'Backend should have set recurringDayOfWeek on first-time scheduling - Bid is source of truth'
                                    });
                                  }
                                  
                                  // For monthly services: Get recurringDayOfMonth from Bid (PRIORITY 1)
                                  if (actualServiceFrequency === 'monthly' && fertilizingServiceRequest?.acceptedBid?.servicePricing && Array.isArray(fertilizingServiceRequest.acceptedBid.servicePricing)) {
                                    const matchingServicePricing = fertilizingServiceRequest.acceptedBid.servicePricing.find((sp: any) => 
                                      sp.service === 'weedControlFertilizing' || sp.serviceName?.toLowerCase().includes('fertiliz') || sp.serviceName?.toLowerCase().includes('weed')
                                    );
                                    
                                      if (matchingServicePricing?.recurringDayOfMonth !== undefined && matchingServicePricing?.recurringDayOfMonth !== null) {
                                        dayOfMonth = matchingServicePricing.recurringDayOfMonth;
                                      
                                      // CRITICAL: Check if Bid's recurringDayOfMonth doesn't match current scheduledDate
                                      // This indicates the scheduledDate is from a one-time change, and we should use the Bid's value
                                      if (fertilizingServiceRequest?.scheduledDate) {
                                        const scheduledDateDayOfMonth = new Date(fertilizingServiceRequest.scheduledDate).getUTCDate();
                                        if (dayOfMonth !== scheduledDateDayOfMonth) {
                                          console.log('[CUSTOMER-DETAILS] ✅ Using Bid recurringDayOfMonth (mismatch with scheduledDate indicates one-time change):', {
                                            service: 'fertilizing',
                                            bidRecurringDayOfMonth: dayOfMonth,
                                            scheduledDateDayOfMonth,
                                            scheduledDate: fertilizingServiceRequest.scheduledDate,
                                            note: 'Bid recurringDayOfMonth represents the permanent recurring schedule - scheduledDate is from one-time change'
                                          });
                                        } else {
                                          console.log('[CUSTOMER-DETAILS] ✅ Using Bid recurringDayOfMonth as PRIMARY source (recurring pattern):', {
                                          service: 'fertilizing',
                                          dayOfMonth,
                                            note: 'Bid recurringDayOfMonth represents the permanent recurring schedule - matches scheduledDate'
                                          });
                                        }
                                      } else {
                                        console.log('[CUSTOMER-DETAILS] ✅ Using Bid recurringDayOfMonth as PRIMARY source (recurring pattern):', {
                                          service: 'fertilizing',
                                          dayOfMonth,
                                          note: 'Bid recurringDayOfMonth represents the permanent recurring schedule - not affected by one-time changes'
                                        });
                                      }
                                    } else {
                                      console.log('[CUSTOMER-DETAILS] ⚠️ Bid recurringDayOfMonth not available for fertilizing service:', {
                                      service: 'fertilizing',
                                        hasMatchingServicePricing: !!matchingServicePricing,
                                        recurringDayOfMonth: matchingServicePricing?.recurringDayOfMonth,
                                        frequency: actualServiceFrequency,
                                        note: 'Will fallback to scheduledDate for first-time scheduling'
                                      });
                                    }
                                  }
                                  
                                  // For monthly services: Create reference date using Bid's recurringDayOfMonth
                                  // CRITICAL: For bid-accepted customers, Bid.recurringDayOfMonth is the SINGLE SOURCE OF TRUTH
                                  // It represents the permanent recurring schedule and is NOT affected by one-time changes
                                  // For onboarded customers (no Bid), scheduledDate is used as a fallback
                                  let referenceDate: Date | string | undefined = undefined;
                                  if (actualServiceFrequency === 'monthly') {
                                    // Use Bid's recurringDayOfMonth - this represents the permanent recurring pattern
                                    let dayOfMonthToUse: number | undefined = dayOfMonth;
                                    
                                    // PRIORITY 2: For ServiceRequests without acceptedBid, use service-specific recurringDayOfMonth from servicePricing
                                    // CRITICAL: Each service has its own recurringDayOfMonth, not shared firstServiceDate
                                    // This is the proper source of truth for onboarded customers - it represents the permanent recurring schedule
                                    // and is NOT affected by one-time changes (only updated on first-time scheduling and permanent changes)
                                    if (dayOfMonthToUse === undefined && !fertilizingServiceRequest?.acceptedBid) {
                                      // PRIORITY 2A: Try to get day of month from service-specific pricing (most accurate)
                                      const fertilizingPricing = customerDetailsDialog.customer.servicePricing?.weedControlFertilizing;
                                      console.log('[CUSTOMER-DETAILS] 🔍 Checking servicePricing.weedControlFertilizing for recurringDayOfMonth:', {
                                        service: 'fertilizing',
                                        hasFertilizingPricing: !!fertilizingPricing,
                                        fertilizingPricingType: typeof fertilizingPricing,
                                        fertilizingPricingKeys: fertilizingPricing && typeof fertilizingPricing === 'object' ? Object.keys(fertilizingPricing) : [],
                                        hasRecurringDayOfMonth: fertilizingPricing && typeof fertilizingPricing === 'object' ? 'recurringDayOfMonth' in fertilizingPricing : false,
                                        recurringDayOfMonth: fertilizingPricing && typeof fertilizingPricing === 'object' ? (fertilizingPricing as any).recurringDayOfMonth : undefined,
                                        fullFertilizingPricing: fertilizingPricing
                                      });
                                      if (fertilizingPricing && typeof fertilizingPricing === 'object' && 'recurringDayOfMonth' in fertilizingPricing) {
                                        dayOfMonthToUse = (fertilizingPricing as any).recurringDayOfMonth;
                                        console.log('[CUSTOMER-DETAILS] ✅ Using service-specific recurringDayOfMonth from servicePricing.weedControlFertilizing (ServiceRequest has no acceptedBid):', {
                                          service: 'fertilizing',
                                          recurringDayOfMonth: dayOfMonthToUse,
                                          note: 'Using service-specific recurringDayOfMonth as source of truth (not affected by one-time changes)'
                                        });
                                      } else if (fertilizingServiceRequest?.scheduledDate) {
                                        // PRIORITY 2C: Final fallback to scheduledDate only if neither is available
                                    const scheduledDate = new Date(fertilizingServiceRequest.scheduledDate);
                                        dayOfMonthToUse = scheduledDate.getUTCDate();
                                        console.log('[CUSTOMER-DETAILS] ⚠️ Using scheduledDate as final fallback (service-specific recurringDayOfMonth and firstServiceDate not available):', {
                                      service: 'fertilizing',
                                      scheduledDate: fertilizingServiceRequest.scheduledDate,
                                          extractedDayOfMonth: dayOfMonthToUse,
                                          note: 'Both service-specific recurringDayOfMonth and firstServiceDate missing - using scheduledDate (may include one-time changes)'
                                        });
                                      }
                                    }
                                    
                                    // PRIORITY 3: For bid-accepted customers, if Bid is missing recurringDayOfMonth, this indicates a data issue
                                    // Backend should ALWAYS set recurringDayOfMonth on first-time scheduling
                                    // CRITICAL: We do NOT use scheduledDate as a fallback for bid-accepted customers
                                    // The Bid is the source of truth - if it's missing, that's a data corruption issue that needs to be fixed
                                    if (dayOfMonthToUse === undefined && fertilizingServiceRequest?.acceptedBid) {
                                      console.warn('[CUSTOMER-DETAILS] ⚠️ Bid-accepted customer missing recurringDayOfMonth - Schedule cannot be displayed:', {
                                        service: 'fertilizing',
                                        hasAcceptedBid: !!fertilizingServiceRequest?.acceptedBid,
                                        hasScheduledDate: !!fertilizingServiceRequest?.scheduledDate,
                                        note: 'Backend should have set recurringDayOfMonth on first-time scheduling - Bid is source of truth. This is a data issue that needs to be fixed.'
                                      });
                                    }
                                    
                                    if (dayOfMonthToUse === undefined) {
                                      // For monthly services, we need recurringDayOfMonth from Bid or scheduledDate
                                      console.warn('[CUSTOMER-DETAILS] ⚠️ Monthly service missing both recurringDayOfMonth and scheduledDate - Schedule cannot be displayed:', {
                                        service: 'fertilizing',
                                        hasAcceptedBid: !!fertilizingServiceRequest?.acceptedBid,
                                        hasScheduledDate: !!fertilizingServiceRequest?.scheduledDate,
                                        note: 'Schedule display requires recurringDayOfMonth from Bid or scheduledDate'
                                      });
                                    } else {
                                      console.log('[CUSTOMER-DETAILS] ✅ Using dayOfMonth for monthly Schedule display:', {
                                        service: 'fertilizing',
                                        dayOfMonth: dayOfMonthToUse,
                                        source: dayOfMonth !== undefined ? 'Bid recurringDayOfMonth' : 'scheduledDate (fallback)',
                                        note: 'Bid recurringDayOfMonth represents the permanent recurring schedule - not affected by one-time changes'
                                      });
                                    }
                                    
                                    if (dayOfMonthToUse !== undefined) {
                                      // Create a date with the day of month (use current month/year, day doesn't matter for display)
                                      const today = new Date();
                                      const year = today.getUTCFullYear();
                                      const month = today.getUTCMonth();
                                      // Use the dayOfMonthToUse, but ensure it's valid (1-31)
                                      const validDayOfMonth = Math.max(1, Math.min(31, dayOfMonthToUse));
                                      referenceDate = new Date(Date.UTC(year, month, validDayOfMonth, 0, 0, 0, 0));
                                      console.log('[CUSTOMER-DETAILS] ✅ Created reference date for monthly Schedule display:', {
                                        service: 'fertilizing',
                                        dayOfMonth: dayOfMonthToUse,
                                        referenceDate: referenceDate.toISOString(),
                                        source: 'Bid recurringDayOfMonth (primary)'
                                      });
                                    }
                                    
                                    if (!referenceDate) {
                                      // If recurringDayOfMonth is not available, don't show a schedule (data issue)
                                      console.warn('[CUSTOMER-DETAILS] ⚠️ Monthly service missing recurringDayOfMonth - Schedule cannot be displayed:', {
                                        service: 'fertilizing',
                                        hasAcceptedBid: !!fertilizingServiceRequest?.acceptedBid,
                                        note: 'Schedule display requires recurringDayOfMonth from Bid'
                                      });
                                    }
                                  } else if (actualServiceFrequency === 'weekly' || actualServiceFrequency === 'bi-weekly') {
                                    // For weekly/bi-weekly, use nextServiceDate or lastServiceDate as reference
                                    // CRITICAL: Only use customer-level fields if service has been scheduled (has scheduledDate)
                                    // This prevents showing schedule for unscheduled services
                                    if (fertilizingServiceRequest?.scheduledDate) {
                                    referenceDate = customerDetailsDialog.customer.nextServiceDate 
                                      || customerDetailsDialog.customer.lastServiceDate
                                      || fertilizingServiceRequest?.scheduledDate;
                                  }
                                  }
                                  // For monthly services, referenceDate is only set if dayOfMonthToUse is defined (above)
                                  // If dayOfMonthToUse is undefined, referenceDate remains undefined and we show "Not scheduled"
                                  
                                  if (actualServiceFrequency && actualServiceFrequency !== 'seasonal-contract') {
                                    // Only show schedule if we have the required data
                                    // For monthly: need referenceDate (set from dayOfMonthToUse)
                                    // For weekly/bi-weekly: need dayOfWeek
                                    if (actualServiceFrequency === 'monthly' && referenceDate) {
                                      return (
                                        <Typography variant="body2" fontWeight="medium">
                                          {getScheduleDisplay(actualServiceFrequency, dayOfWeek, referenceDate)}
                                        </Typography>
                                      );
                                    } else if ((actualServiceFrequency === 'weekly' || actualServiceFrequency === 'bi-weekly') && dayOfWeek !== undefined) {
                                    return (
                                      <Typography variant="body2" fontWeight="medium">
                                        {getScheduleDisplay(actualServiceFrequency, dayOfWeek, referenceDate)}
                                      </Typography>
                                    );
                                    }
                                    // If required data is missing, show "Not scheduled"
                                    return <Typography variant="body2" color="text.secondary">Not scheduled</Typography>;
                                  }
                                  return <Typography variant="body2" color="text.secondary">Not scheduled</Typography>;
                                })()}
                              </Grid>
                              {(() => {
                                // Show contract dates only for seasonal contracts (Weed Control)
                                const serviceFrequency = customerDetailsDialog.customer.servicePricing?.weedControlFertilizing?.frequency;
                                const serviceRequest = customerServiceRequests.find((sr: any) => sr.serviceType?.category === 'fertilizing');
                                let isSeasonal = serviceFrequency === 'seasonal-contract' || customerDetailsDialog.customer.paymentStructure?.type === 'seasonal-contract';
                                
                                // Also check service request's accepted bid
                                if (!isSeasonal && serviceRequest?.acceptedBid?.servicePricing) {
                                  const matchingServicePricing = serviceRequest.acceptedBid.servicePricing.find((sp: any) => 
                                    sp.service === 'weedControlFertilizing' || sp.serviceName?.toLowerCase().includes('fertiliz') || sp.serviceName?.toLowerCase().includes('weed')
                                  );
                                  if (matchingServicePricing?.isSeasonalContract || matchingServicePricing?.frequency === 'seasonal-contract') {
                                    isSeasonal = true;
                                  }
                                }
                                
                                if (!isSeasonal) return null;
                                
                                // Extract contract dates from the same sources we use for other seasonal contract data
                                // SINGLE SOURCE OF TRUTH: For bid-accepted customers, prioritize acceptedBid.servicePricing
                                // This ensures consistency with customer's My Services view and avoids stale data
                                let contractStartDate: string | undefined = undefined;
                                let contractEndDate: string | undefined = undefined;
                                
                                // PRIORITY 1: Check accepted bid first (for bid-accepted customers - this is the source of truth)
                                if (serviceRequest?.acceptedBid?.servicePricing) {
                                  const matchingServicePricing = serviceRequest.acceptedBid.servicePricing.find((sp: any) => 
                                    sp.service === 'weedControlFertilizing' || sp.serviceName?.toLowerCase().includes('fertiliz') || sp.serviceName?.toLowerCase().includes('weed')
                                  );
                                  if (matchingServicePricing?.seasonalContractDetails) {
                                    contractStartDate = matchingServicePricing.seasonalContractDetails.contractStartDate;
                                    contractEndDate = matchingServicePricing.seasonalContractDetails.contractEndDate;
                                  }
                                }
                                
                                // PRIORITY 2: Fallback to customer.servicePricing (for onboarded customers without acceptedBid)
                                if ((!contractStartDate || !contractEndDate)) {
                                  const weedControlPricing = customerDetailsDialog.customer.servicePricing?.weedControlFertilizing as any;
                                  if (weedControlPricing?.seasonalContractDetails) {
                                    if (!contractStartDate) contractStartDate = weedControlPricing.seasonalContractDetails.contractStartDate;
                                    if (!contractEndDate) contractEndDate = weedControlPricing.seasonalContractDetails.contractEndDate;
                                  }
                                }
                                
                                if (!contractStartDate && !contractEndDate) return null;
                                
                                const formatDate = (dateStr: string | undefined) => {
                                  if (!dateStr) return 'N/A';
                                  try {
                                    const date = new Date(dateStr);
                                    // CRITICAL: Extract UTC date components directly (dates are stored as UTC midnight representing calendar dates)
                                    // This prevents timezone conversion issues where end dates appear 1 day early
                                    const year = date.getUTCFullYear();
                                    const month = date.getUTCMonth();
                                    const day = date.getUTCDate();
                                    // Format using UTC components to avoid timezone shift
                                    const formattedDate = new Date(Date.UTC(year, month, day, 12, 0, 0, 0));
                                    return formattedDate.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric', timeZone: 'UTC' });
                                  } catch {
                                    return dateStr;
                                  }
                                };
                                
                                // Extract payment schedule, total contract price, and monthly payment amount
                                // SINGLE SOURCE OF TRUTH: Use stored monthlyPaymentAmount from subscription creation, not recalculate
                                let paymentSchedule: 'single-payment' | 'monthly' | undefined = undefined;
                                let totalContractPrice: number | undefined = undefined;
                                let monthlyPaymentAmount: number | undefined = undefined;
                                
                                // PRIORITY 1: Check accepted bid first (for bid-accepted customers - this is the source of truth for contract details)
                                if (serviceRequest?.acceptedBid?.servicePricing) {
                                  const matchingServicePricing = serviceRequest.acceptedBid.servicePricing.find((sp: any) => 
                                    sp.service === 'weedControlFertilizing' || sp.serviceName?.toLowerCase().includes('fertiliz') || sp.serviceName?.toLowerCase().includes('weed')
                                  );
                                  if (matchingServicePricing?.seasonalContractDetails) {
                                    paymentSchedule = matchingServicePricing.seasonalContractDetails.paymentSchedule;
                                    totalContractPrice = matchingServicePricing.seasonalContractDetails.totalContractPrice;
                                    // Note: Bids don't store monthlyPaymentAmount - it's stored in ExistingCustomer when subscription is created
                                  }
                                }
                                
                                // PRIORITY 2: Fallback to customer.servicePricing (for onboarded customers without acceptedBid)
                                // This is also where monthlyPaymentAmount is stored (single source of truth from subscription creation)
                                if (!paymentSchedule || !totalContractPrice || !monthlyPaymentAmount) {
                                  const weedControlPricing = customerDetailsDialog.customer.servicePricing?.weedControlFertilizing as any;
                                  if (weedControlPricing?.seasonalContractDetails) {
                                    if (!paymentSchedule) paymentSchedule = weedControlPricing.seasonalContractDetails.paymentSchedule;
                                    if (!totalContractPrice) totalContractPrice = weedControlPricing.seasonalContractDetails.totalContractPrice;
                                    // Use stored monthlyPaymentAmount (single source of truth from subscription creation)
                                    if (!monthlyPaymentAmount && paymentSchedule === 'monthly') {
                                      monthlyPaymentAmount = weedControlPricing.seasonalContractDetails.monthlyPaymentAmount;
                                    }
                                  }
                                }
                                
                                // FALLBACK: Only recalculate if stored value is missing (shouldn't happen after subscription is created)
                                if (paymentSchedule === 'monthly' && !monthlyPaymentAmount && totalContractPrice && contractStartDate && contractEndDate) {
                                  try {
                                    const start = new Date(contractStartDate);
                                    const end = new Date(contractEndDate);
                                    const startsOnFirst = start.getUTCDate() === 1;
                                    
                                    let billingMonths: number;
                                    if (startsOnFirst) {
                                      // Contract starts on 1st: Include that month in count - use UTC components
                                      billingMonths = (end.getUTCFullYear() - start.getUTCFullYear()) * 12 + 
                                                      (end.getUTCMonth() - start.getUTCMonth()) + 1; // +1 to include both start and end months
                                    } else {
                                      // Contract starts mid-month: Exclude partial first month - use UTC components
                                      const nextMonthYear = start.getUTCMonth() === 11 ? start.getUTCFullYear() + 1 : start.getUTCFullYear();
                                      const nextMonthMonth = (start.getUTCMonth() + 1) % 12;
                                      billingMonths = (end.getUTCFullYear() - nextMonthYear) * 12 + 
                                                      (end.getUTCMonth() - nextMonthMonth) + 1; // +1 to include end month
                                    }
                                    
                                    if (billingMonths > 0) {
                                      monthlyPaymentAmount = Math.round((totalContractPrice / billingMonths) * 100) / 100;
                                      console.warn('[CUSTOMER-DETAILS] ⚠️ Recalculated monthlyPaymentAmount (stored value missing):', {
                                        service: 'fertilizing',
                                        recalculated: monthlyPaymentAmount,
                                        note: 'Should use stored value from subscription creation - this is a fallback'
                                      });
                                    }
                                  } catch (e) {
                                    console.error('[CUSTOMER-DETAILS] Error calculating monthly payment:', e);
                                  }
                                }
                                
                                return (
                                  <>
                                    <Grid item xs={12} sm={6}>
                                      <Typography variant="caption" color="text.secondary">Contract Period</Typography>
                                      <Typography variant="body2" fontWeight="medium">
                                        {formatDate(contractStartDate)} - {formatDate(contractEndDate)}
                                      </Typography>
                                    </Grid>
                                    {paymentSchedule && (
                                      <Grid item xs={12} sm={6}>
                                        <Typography variant="caption" color="text.secondary">Payment Schedule</Typography>
                                        <Typography variant="body2" fontWeight="medium">
                                          {paymentSchedule === 'monthly' ? 'Monthly Payments' : 'One-Time Payment'}
                                        </Typography>
                                      </Grid>
                                    )}
                                    {paymentSchedule === 'monthly' && monthlyPaymentAmount && (
                                      <Grid item xs={12} sm={6}>
                                        <Typography variant="caption" color="text.secondary">Monthly Payment</Typography>
                                        <Typography variant="body2" fontWeight="medium" color="primary">
                                          ${monthlyPaymentAmount.toFixed(2)}
                                        </Typography>
                                      </Grid>
                                    )}
                                  </>
                                );
                              })()}
                              <Grid item xs={12} sm={6}>
                                <Typography variant="caption" color="text.secondary">Last Service</Typography>
                                {(() => {
                                  if (!customerDetailsDialog.customer) return <Typography variant="body2" color="text.secondary">N/A</Typography>;
                                  
                                  // Check if this is a per-instance service
                                  const serviceFrequency = customerDetailsDialog.customer.servicePricing?.weedControlFertilizing?.frequency;
                                  
                                  // For per-instance services, only show Last Service Date if there's an actual completed service request
                                  // Don't use customer.lastServiceDate as fallback for per-instance services
                                  if (serviceFrequency === 'per-instance') {
                                    // Get last service date for this specific service category
                                    // CRITICAL: Include both 'completed' status AND 'scheduled' status with completedDate
                                    // After auto-rescheduling, status becomes 'scheduled' but completedDate is preserved
                                    const completedRequests = customerServiceRequests.filter((sr: any) => 
                                      sr.serviceType?.category === 'fertilizing' && 
                                      sr.completedDate &&
                                      (sr.status === 'completed' || sr.status === 'scheduled')
                                    );
                                    
                                    if (completedRequests.length > 0) {
                                      const mostRecent = completedRequests.reduce((latest: any, current: any) => {
                                        if (!latest.completedDate) return current;
                                        if (!current.completedDate) return latest;
                                        return new Date(current.completedDate) > new Date(latest.completedDate) ? current : latest;
                                      });
                                      
                                      if (mostRecent?.completedDate) {
                                        // Extract UTC date components directly (same logic as All Customers chart)
                                        const completedDate = new Date(mostRecent.completedDate);
                                        const year = completedDate.getUTCFullYear();
                                        const month = completedDate.getUTCMonth();
                                        const day = completedDate.getUTCDate();
                                        const normalizedDate = new Date(Date.UTC(year, month, day, 0, 0, 0, 0));
                                        
                                        return (
                                          <Typography variant="body2" fontWeight="medium">
                                            {formatDateMMDDYYYY(normalizedDate.toString())}
                                          </Typography>
                                        );
                                      }
                                    }
                                    
                                    // For per-instance services with no completed requests, show N/A
                                    return <Typography variant="body2" color="text.secondary">N/A</Typography>;
                                  }
                                  
                                  // For recurring services, use the same logic as before
                                  // Get last service date for this specific service category
                                  // CRITICAL: Include both 'completed' status AND 'scheduled' status with completedDate
                                  // After auto-rescheduling, status becomes 'scheduled' but completedDate is preserved
                                  const completedRequests = customerServiceRequests.filter((sr: any) => 
                                    sr.serviceType?.category === 'fertilizing' && 
                                    sr.completedDate &&
                                    (sr.status === 'completed' || sr.status === 'scheduled')
                                  );
                                  
                                  if (completedRequests.length > 0) {
                                    const mostRecent = completedRequests.reduce((latest: any, current: any) => {
                                      if (!latest.completedDate) return current;
                                      if (!current.completedDate) return latest;
                                      return new Date(current.completedDate) > new Date(latest.completedDate) ? current : latest;
                                    });
                                    
                                    if (mostRecent?.completedDate) {
                                      // Extract UTC date components directly (same logic as All Customers chart)
                                      const completedDate = new Date(mostRecent.completedDate);
                                      const year = completedDate.getUTCFullYear();
                                      const month = completedDate.getUTCMonth();
                                      const day = completedDate.getUTCDate();
                                      const normalizedDate = new Date(Date.UTC(year, month, day, 0, 0, 0, 0));
                                      
                                      return (
                                        <Typography variant="body2" fontWeight="medium">
                                          {formatDateMMDDYYYY(normalizedDate.toString())}
                                        </Typography>
                                      );
                                    }
                                  }
                                  
                                  // IMPORTANT: Don't use customer.lastServiceDate as fallback
                                  // It's a single field that can't represent multiple services correctly
                                  // For customers with multiple services, customer.lastServiceDate might be for a different service
                                  // Return N/A if no completed service request found for this specific category
                                  return <Typography variant="body2" color="text.secondary">N/A</Typography>;
                                })()}
                              </Grid>
                              <Grid item xs={12} sm={6}>
                                <Typography variant="caption" color="text.secondary">Next Service</Typography>
                                {(() => {
                                  if (!customerDetailsDialog.customer) return <Typography variant="body2" color="text.secondary">N/A</Typography>;
                                  
                                  // Use customerServiceRequests (dialog fetch) — same as mowing — so we don't
                                  // pick a stale first-match SR from the broader allCustomersServiceRequests map.
                                  const customerRequests = customerServiceRequests || [];
                                  
                                  const getServiceRequest = (category: string) => {
                                    const customerAddress = customerDetailsDialog.customer?.address;
                                    const matching = customerRequests.filter((sr: any) => {
                                      if (sr.serviceType?.category !== category) return false;
                                      const srAddress = sr.address;
                                      if (srAddress && customerAddress) {
                                        const sameStreet = (srAddress.street || '').trim().toLowerCase() === (customerAddress.street || '').trim().toLowerCase();
                                        const sameZip = (srAddress.zipCode || '').trim() === (customerAddress.zipCode || '').trim();
                                        return sameStreet && sameZip;
                                      }
                                      return true;
                                    });
                                    // Prefer upcoming scheduled; util also re-selects best from the list
                                    return matching.find((sr: any) => sr.status === 'scheduled' || sr.status === 'in-progress')
                                      || matching.find((sr: any) => sr.status === 'accepted')
                                      || matching[0];
                                  };
                                  
                                  // Use shared utility function for calculating next service dates (SINGLE SOURCE OF TRUTH)
                                  const getNextServiceDate = (category: string) => {
                                    if (!customerDetailsDialog.customer) return null;
                                    const serviceRequest = getServiceRequest(category);
                                    return getNextServiceDateUtil(
                                      customerDetailsDialog.customer,
                                      category as 'mowing' | 'fertilizing' | 'snow_removal',
                                      serviceRequest,
                                      customerRequests
                                    );
                                  };
                                  
                                  const nextDate = getNextServiceDate('fertilizing');
                                  
                                  if (nextDate) {
                                    return (
                                      <Typography variant="body2" fontWeight="medium">
                                        {formatDateMMDDYYYY(nextDate.toString())}
                                      </Typography>
                                    );
                                  }
                                  
                                  return <Typography variant="body2" color="text.secondary">N/A</Typography>;
                                })()}
                              </Grid>
                              {(() => {
                                // Show "Mark Season Ended" button for recurring services (not seasonal contracts, not per-instance)
                                const serviceRequest = customerServiceRequests.find((sr: any) => sr.serviceType?.category === 'fertilizing');
                                const serviceFrequency = getServiceFrequency(customerDetailsDialog.customer, 'fertilizing', serviceRequest);
                                
                                // Determine if this is a seasonal contract
                                let isSeasonalContract = serviceFrequency === 'seasonal-contract';
                                if (!isSeasonalContract && serviceRequest?.acceptedBid?.servicePricing) {
                                  const matchingServicePricing = serviceRequest.acceptedBid.servicePricing.find((sp: any) => 
                                    sp.service === 'weedControlFertilizing' || sp.serviceName?.toLowerCase().includes('fertiliz') || sp.serviceName?.toLowerCase().includes('weed')
                                  );
                                  if (matchingServicePricing?.isSeasonalContract || matchingServicePricing?.frequency === 'seasonal-contract') {
                                    isSeasonalContract = true;
                                  }
                                }
                                
                                // Show for recurring, per-instance, or seasonal-contract + per-instance. Hide for seasonal + weekly/bi-weekly/monthly.
                                const isRecurring = serviceFrequency === 'weekly' || serviceFrequency === 'bi-weekly' || serviceFrequency === 'monthly';
                                const isPerInstance = serviceFrequency === 'per-instance';
                                const allowSeasonalPerInstance = isSeasonalContract && isPerInstance;
                                const canMarkSeasonEnded = (isRecurring || isPerInstance) && (!isSeasonalContract || allowSeasonalPerInstance) && serviceRequest && 
                                                          (serviceRequest.status === 'completed' || serviceRequest.status === 'scheduled' || serviceRequest.status === 'season_ended');
                                
                                if (!canMarkSeasonEnded) return null;
                                
                                return (
                                  <Grid item xs={12}>
                                    <Box display="flex" justifyContent="flex-end" mt={1}>
                                      <Button
                                        variant="outlined"
                                        size="small"
                                        onClick={() => {
                                          if (serviceRequest?._id || serviceRequest?.id) {
                                            setSeasonEndConfirmDialog({ 
                                              open: true, 
                                              serviceRequestId: serviceRequest._id || serviceRequest.id, 
                                              serviceCategory: 'fertilizing' 
                                            });
                                          }
                                        }}
                                        disabled={markingSeasonEnded === (serviceRequest?._id || serviceRequest?.id)}
                                        sx={{
                                          borderColor: serviceRequest?.status === 'season_ended' ? 'primary.main' : 'error.main',
                                          color: serviceRequest?.status === 'season_ended' ? 'primary.main' : 'error.main',
                                          fontWeight: 'bold',
                                          '&:hover': {
                                            borderColor: serviceRequest?.status === 'season_ended' ? 'primary.dark' : 'error.dark',
                                            backgroundColor: serviceRequest?.status === 'season_ended' ? 'primary.light' : 'error.light',
                                            color: serviceRequest?.status === 'season_ended' ? 'primary.dark' : 'error.dark'
                                          }
                                        }}
                                        startIcon={serviceRequest?.status === 'season_ended' ? undefined : <CheckCircle />}
                                      >
                                        {serviceRequest?.status === 'season_ended'
                                          ? 'Send notification & add to Renewals'
                                          : 'Mark Season Ended'}
                                      </Button>
                                    </Box>
                                  </Grid>
                                );
                              })()}
                            </Grid>
                          </CardContent>
                        </Card>
                      )}

                      {/* Snow Removal Service */}
                      {/* SINGLE SOURCE OF TRUTH: Only show service if there's a ServiceRequest for THIS property address */}
                      {/* ServiceRequest is filtered by address, so it's the ultimate source of truth */}
                      {/* Also check servicePricing to ensure the service is properly configured */}
                      {/* Snow Removal Service */}
                      {/* SINGLE SOURCE OF TRUTH: Only show service if there's a ServiceRequest for THIS property address */}
                      {/* ServiceRequest is filtered by providerId (backend) and address (frontend), so it's the ultimate source of truth */}
                      {/* CRITICAL: Do NOT check servicePricing - it may contain services from other providers */}
                      {customerServiceRequests.some((sr: any) => sr.serviceType?.category === 'snow_removal') && (
                        <Card variant="outlined">
                          <CardContent>
                            <Box display="flex" justifyContent="space-between" alignItems="start" mb={1}>
                              <Typography variant="h6" fontSize="1rem">
                                Snow Removal
                              </Typography>
                              <Chip label="Active" size="small" color="success" />
                            </Box>
                            <Grid container spacing={2}>
                              <Grid item xs={12} sm={6}>
                                <Typography variant="caption" color="text.secondary">Price</Typography>
                                <Box display="flex" alignItems="center" gap={1}>
                                  <Typography variant="body2" fontWeight="medium">
                                    {(() => {
                                      // Check servicePricing first
                                      let price = customerDetailsDialog.customer.servicePricing?.snowRemoval?.price;
                                      
                                      // If price is missing or 0, check service request's accepted bid
                                      if (!price || Number(price) === 0) {
                                        const serviceRequest = customerServiceRequests.find((sr: any) => sr.serviceType?.category === 'snow_removal');
                                        if (serviceRequest?.acceptedBid) {
                                          // Check if bid has servicePricing array
                                          if (serviceRequest.acceptedBid.servicePricing && Array.isArray(serviceRequest.acceptedBid.servicePricing)) {
                                            const matchingServicePricing = serviceRequest.acceptedBid.servicePricing.find((sp: any) => 
                                              sp.service === 'snowRemoval' || sp.serviceName?.toLowerCase().includes('snow')
                                            );
                                            if (matchingServicePricing?.price) {
                                              price = matchingServicePricing.price;
                                            }
                                          }
                                          // Fallback to bid.price
                                          if (!price && serviceRequest.acceptedBid.price) {
                                            price = serviceRequest.acceptedBid.price;
                                          }
                                        }
                                      }
                                      
                                      return price && Number(price) > 0 
                                        ? `$${Number(price).toFixed(2)}`
                                        : 'N/A';
                                    })()}
                                  </Typography>
                                  {(() => {
                                    const serviceFrequency = customerDetailsDialog.customer.servicePricing?.snowRemoval?.frequency;
                                    const serviceRequest = customerServiceRequests.find((sr: any) => sr.serviceType?.category === 'snow_removal');
                                    // CRITICAL: Only check service-specific seasonal contract status, NOT customer-level paymentStructure
                                    // paymentStructure.type is customer-level and may be 'seasonal-contract' if ANY service is seasonal
                                    // We need to check each service individually
                                    let isSeasonal = serviceFrequency === 'seasonal-contract';
                                    
                                    // Also check service request's accepted bid for service-specific seasonal contract status
                                    if (!isSeasonal && serviceRequest?.acceptedBid?.servicePricing) {
                                      const matchingServicePricing = serviceRequest.acceptedBid.servicePricing.find((sp: any) => 
                                        sp.service === 'snowRemoval' || sp.serviceName?.toLowerCase().includes('snow')
                                      );
                                      if (matchingServicePricing?.isSeasonalContract || matchingServicePricing?.frequency === 'seasonal-contract') {
                                        isSeasonal = true;
                                      }
                                    }
                                    
                                    return isSeasonal ? (
                                      <Chip 
                                        label="Seasonal Contract" 
                                        size="small" 
                                        color="secondary"
                                        variant="outlined"
                                        sx={{ height: 20, fontSize: '0.65rem' }}
                                      />
                                    ) : null;
                                  })()}
                                </Box>
                              </Grid>
                              <Grid item xs={12} sm={6}>
                                <Typography variant="caption" color="text.secondary">Frequency</Typography>
                                <Typography variant="body2" fontWeight="medium">
                                  {(() => {
                                    // SINGLE SOURCE OF TRUTH: Use getServiceFrequency helper which checks all sources correctly
                                    // getServiceFrequency already checks seasonalContractDetails.serviceFrequency FIRST, so it should return 'per-instance' if that's what's in the Bid
                                    const serviceRequest = customerServiceRequests.find((sr: any) => sr.serviceType?.category === 'snow_removal');
                                    const frequency = getServiceFrequency(customerDetailsDialog.customer, 'snow_removal', serviceRequest);
                                    
                                    // getServiceFrequency should already return the correct frequency (including 'per-instance' for seasonal contracts)
                                    // But if it returns 'seasonal-contract', we need to check the Bid for the actual serviceFrequency
                                    if (frequency === 'seasonal-contract' && serviceRequest?.acceptedBid?.servicePricing) {
                                        const matchingServicePricing = serviceRequest.acceptedBid.servicePricing.find((sp: any) => 
                                          sp.service === 'snowRemoval' || sp.serviceName?.toLowerCase().includes('snow')
                                        );
                                        if (matchingServicePricing?.seasonalContractDetails?.serviceFrequency) {
                                          const actualFrequency = matchingServicePricing.seasonalContractDetails.serviceFrequency;
                                        // Format the frequency for display - this is what should be shown
                                          if (actualFrequency === 'per-instance') return 'Per Instance';
                                          if (actualFrequency === 'weekly') return 'Weekly';
                                          if (actualFrequency === 'bi-weekly') return 'Bi-Weekly';
                                          if (actualFrequency === 'monthly') return 'Monthly';
                                          return actualFrequency.charAt(0).toUpperCase() + actualFrequency.slice(1).replace('-', '-');
                                        }
                                      // If seasonal contract but no serviceFrequency found, check servicePricing as fallback
                                      const fallbackFrequency = customerDetailsDialog.customer.servicePricing?.snowRemoval?.frequency;
                                      if (fallbackFrequency && fallbackFrequency !== 'seasonal-contract') {
                                        if (fallbackFrequency === 'per-instance') return 'Per Instance';
                                        if (fallbackFrequency === 'weekly') return 'Weekly';
                                        if (fallbackFrequency === 'bi-weekly') return 'Bi-Weekly';
                                        if (fallbackFrequency === 'monthly') return 'Monthly';
                                        return fallbackFrequency.charAt(0).toUpperCase() + fallbackFrequency.slice(1).replace('-', '-');
                                    }
                                      // Last resort: return 'Seasonal Contract' only if we truly can't determine the serviceFrequency
                                      return 'Seasonal Contract';
                                    }
                                    
                                    // Format frequency for display - getServiceFrequency should already return the correct value
                                      if (frequency === 'per-instance') return 'Per Instance';
                                      if (frequency === 'weekly') return 'Weekly';
                                      if (frequency === 'bi-weekly') return 'Bi-Weekly';
                                      if (frequency === 'monthly') return 'Monthly';
                                    if (frequency === 'seasonal-contract') return 'Seasonal Contract';
                                    
                                    return 'N/A';
                                  })()}
                                </Typography>
                              </Grid>
                              <Grid item xs={12} sm={6}>
                                <Typography variant="caption" color="text.secondary">Schedule</Typography>
                                {(() => {
                                  // Use the SAME logic as All Customers chart - use getServiceFrequency helper
                                  const serviceRequest = customerServiceRequests.find((sr: any) => sr.serviceType?.category === 'snow_removal');
                                  const serviceFrequency = getServiceFrequency(customerDetailsDialog.customer, 'snow_removal', serviceRequest);
                                  
                                  // For seasonal contracts, check the actual service frequency
                                  let actualServiceFrequency = serviceFrequency;
                                  if (serviceFrequency === 'seasonal-contract' && serviceRequest?.acceptedBid?.servicePricing) {
                                    const matchingServicePricing = serviceRequest.acceptedBid.servicePricing.find((sp: any) => 
                                      sp.service === 'snowRemoval' || sp.serviceName?.toLowerCase().includes('snow')
                                    );
                                    if (matchingServicePricing?.seasonalContractDetails?.serviceFrequency) {
                                      actualServiceFrequency = matchingServicePricing.seasonalContractDetails.serviceFrequency;
                                    }
                                  }
                                  
                                  // For per-instance services (including seasonal contracts with per-instance service frequency), show "Per Instance"
                                  if (actualServiceFrequency === 'per-instance') {
                                    return <Typography variant="body2" fontWeight="medium">Per Instance</Typography>;
                                  }
                                  
                                  // For weekly, bi-weekly, monthly (and seasonal contracts with recurring serviceFrequency), show the schedule
                                  // CRITICAL: Schedule display shows the RECURRING schedule pattern, NOT one-time changes
                                  // PRIORITY 1: Use Bid's recurringDayOfWeek/recurringDayOfMonth as PRIMARY source (represents the recurring pattern)
                                  // This ensures Schedule shows the permanent recurring day, not temporary one-time changes
                                  let dayOfWeek: number | undefined = undefined;
                                  let dayOfMonth: number | undefined = undefined;
                                  
                                  // Get the snow removal service request first (needed for fallback)
                                  const snowRemovalServiceRequest = customerServiceRequests.find((sr: any) => sr.serviceType?.category === 'snow_removal');
                                  
                                  // PRIORITY 1: Use Bid's recurringDayOfWeek as PRIMARY source (represents the recurring pattern)
                                  if ((actualServiceFrequency === 'weekly' || actualServiceFrequency === 'bi-weekly') && serviceRequest?.acceptedBid?.servicePricing && Array.isArray(serviceRequest.acceptedBid.servicePricing)) {
                                    const matchingServicePricing = serviceRequest.acceptedBid.servicePricing.find((sp: any) => 
                                      sp.service === 'snowRemoval' || sp.serviceName?.toLowerCase().includes('snow')
                                    );
                                    
                                    if (matchingServicePricing && typeof matchingServicePricing.recurringDayOfWeek === 'number') {
                                      dayOfWeek = matchingServicePricing.recurringDayOfWeek;
                                      const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
                                      const bidDayOfWeek = matchingServicePricing.recurringDayOfWeek;
                                      console.log('[CUSTOMER-DETAILS] ✅ Using Bid recurringDayOfWeek as PRIMARY source (recurring pattern):', {
                                        service: 'snow_removal',
                                        dayOfWeek,
                                        dayName: dayNames[bidDayOfWeek],
                                        frequency: actualServiceFrequency,
                                        bidRecurringDayOfWeek: matchingServicePricing.recurringDayOfWeek,
                                        note: 'Bid recurringDayOfWeek represents the permanent recurring schedule - not affected by one-time changes'
                                      });
                                    } else if (actualServiceFrequency === 'bi-weekly' && matchingServicePricing?.biWeeklyStartDate) {
                                      const biWeeklyStartDate = new Date(matchingServicePricing.biWeeklyStartDate);
                                      dayOfWeek = biWeeklyStartDate.getUTCDay();
                                      const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
                                      const calculatedDayOfWeek = biWeeklyStartDate.getUTCDay();
                                      console.log('[CUSTOMER-DETAILS] ✅ Using Bid biWeeklyStartDate as PRIMARY source (recurring pattern):', {
                                        service: 'snow_removal',
                                        biWeeklyStartDate: matchingServicePricing.biWeeklyStartDate,
                                        dayOfWeek,
                                        dayName: dayNames[calculatedDayOfWeek],
                                        note: 'Bid biWeeklyStartDate represents the permanent recurring schedule - not affected by one-time changes'
                                      });
                                    }
                                  }
                                  
                                  // For monthly services, get recurringDayOfMonth from Bid
                                  if (actualServiceFrequency === 'monthly' && serviceRequest?.acceptedBid?.servicePricing && Array.isArray(serviceRequest.acceptedBid.servicePricing)) {
                                    const matchingServicePricing = serviceRequest.acceptedBid.servicePricing.find((sp: any) => 
                                      sp.service === 'snowRemoval' || sp.serviceName?.toLowerCase().includes('snow')
                                    );
                                    
                                    if (matchingServicePricing?.recurringDayOfMonth !== undefined && matchingServicePricing?.recurringDayOfMonth !== null) {
                                      dayOfMonth = matchingServicePricing.recurringDayOfMonth;
                                      console.log('[CUSTOMER-DETAILS] ✅ Using Bid recurringDayOfMonth as PRIMARY source (recurring pattern):', {
                                        service: 'snow_removal',
                                        dayOfMonth,
                                        note: 'Bid recurringDayOfMonth represents the permanent recurring schedule - not affected by one-time changes'
                                      });
                                    }
                                  }
                                  
                                  // PRIORITY 2: Fallback to scheduledDate if Bid data is missing (e.g., service not yet scheduled or Bid not updated)
                                  // Only use scheduledDate when Bid's recurringDayOfWeek is not available
                                  if (dayOfWeek === undefined && (actualServiceFrequency === 'weekly' || actualServiceFrequency === 'bi-weekly') && snowRemovalServiceRequest?.scheduledDate) {
                                    const scheduledDate = new Date(snowRemovalServiceRequest.scheduledDate);
                                    dayOfWeek = scheduledDate.getUTCDay();
                                    const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
                                    console.log('[CUSTOMER-DETAILS] ⚠️ Using scheduledDate as fallback (Bid recurringDayOfWeek not available):', {
                                      service: 'snow_removal',
                                      scheduledDate: snowRemovalServiceRequest.scheduledDate,
                                      dayOfWeek,
                                      dayName: dayOfWeek !== undefined ? dayNames[dayOfWeek] : 'Unknown',
                                      frequency: actualServiceFrequency,
                                      note: 'Bid recurringDayOfWeek missing - using scheduledDate as fallback (may include one-time changes)'
                                    });
                                  }
                                  
                                  // PRIORITY 3: Fallback to completedDate for bi-weekly if Bid data and scheduledDate not available
                                  if (dayOfWeek === undefined && actualServiceFrequency === 'bi-weekly' && snowRemovalServiceRequest?.completedDate) {
                                    const completedDate = new Date(snowRemovalServiceRequest.completedDate);
                                    dayOfWeek = completedDate.getUTCDay();
                                    const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
                                    console.log('[CUSTOMER-DETAILS] ⚠️ Using completedDate as fallback (Bid data and scheduledDate not available):', {
                                      service: 'snow_removal',
                                      completedDate: snowRemovalServiceRequest.completedDate,
                                      dayOfWeek,
                                      dayName: dayOfWeek !== undefined ? dayNames[dayOfWeek] : 'Unknown',
                                      note: 'Bid data and scheduledDate not available - using completedDate as last resort'
                                    });
                                  }
                                  
                                  // NOTE: We no longer use shared serviceSchedule.dayOfWeek as fallback because it's shared across services and can show wrong data
                                  // Service-specific recurringDayOfWeek should always be set on first scheduling
                                  
                                  // For monthly services: Create reference date using Bid's recurringDayOfMonth as PRIMARY source
                                  // CRITICAL: Schedule shows the RECURRING pattern, NOT one-time changes
                                  let referenceDate: Date | string | undefined = undefined;
                                  if (actualServiceFrequency === 'monthly') {
                                    let dayOfMonthToUse: number | undefined = undefined;
                                    
                                    // PRIORITY 1: Use Bid's recurringDayOfMonth as PRIMARY source (represents the recurring pattern)
                                    if (dayOfMonth !== undefined) {
                                      dayOfMonthToUse = dayOfMonth;
                                      console.log('[CUSTOMER-DETAILS] ✅ Using Bid recurringDayOfMonth as PRIMARY source (recurring pattern):', {
                                        service: 'snow_removal',
                                        dayOfMonth: dayOfMonthToUse,
                                        note: 'Bid recurringDayOfMonth represents the permanent recurring schedule - not affected by one-time changes'
                                      });
                                    }
                                    
                                    // PRIORITY 2: Fallback to scheduledDate if Bid data is missing (e.g., service not yet scheduled or Bid not updated)
                                    if (dayOfMonthToUse === undefined && snowRemovalServiceRequest?.scheduledDate) {
                                      const scheduledDate = new Date(snowRemovalServiceRequest.scheduledDate);
                                      dayOfMonthToUse = scheduledDate.getUTCDate();
                                      console.log('[CUSTOMER-DETAILS] ⚠️ Using scheduledDate as fallback (Bid recurringDayOfMonth not available):', {
                                        service: 'snow_removal',
                                        scheduledDate: snowRemovalServiceRequest.scheduledDate,
                                        dayOfMonth: dayOfMonthToUse,
                                        note: 'Bid recurringDayOfMonth missing - using scheduledDate as fallback (may include one-time changes)'
                                      });
                                    }
                                    
                                    // PRIORITY 3: Fallback to completedDate if Bid data and scheduledDate not available
                                    if (dayOfMonthToUse === undefined && snowRemovalServiceRequest?.completedDate) {
                                      const completedDate = new Date(snowRemovalServiceRequest.completedDate);
                                      dayOfMonthToUse = completedDate.getUTCDate();
                                      console.log('[CUSTOMER-DETAILS] ⚠️ Using completedDate as fallback (Bid data and scheduledDate not available):', {
                                        service: 'snow_removal',
                                        completedDate: snowRemovalServiceRequest.completedDate,
                                        dayOfMonth: dayOfMonthToUse,
                                        note: 'Bid data and scheduledDate not available - using completedDate as last resort'
                                      });
                                    }
                                    
                                    if (dayOfMonthToUse !== undefined) {
                                      // Create a date with the day of month (use current month/year, day doesn't matter for display)
                                      const today = new Date();
                                      const year = today.getUTCFullYear();
                                      const month = today.getUTCMonth();
                                      // Use the dayOfMonthToUse, but ensure it's valid (1-31)
                                      const validDayOfMonth = Math.max(1, Math.min(31, dayOfMonthToUse));
                                      referenceDate = new Date(Date.UTC(year, month, validDayOfMonth, 0, 0, 0, 0));
                                      console.log('[CUSTOMER-DETAILS] ✅ Created reference date for monthly Schedule display:', {
                                        service: 'snow_removal',
                                        dayOfMonth: dayOfMonthToUse,
                                        referenceDate: referenceDate.toISOString(),
                                        source: dayOfMonth !== undefined ? 'Bid recurringDayOfMonth (primary)' : 
                                                snowRemovalServiceRequest?.scheduledDate ? 'scheduledDate (fallback)' : 'completedDate (last resort)'
                                      });
                                    }
                                    
                                    if (!referenceDate) {
                                      // If neither recurringDayOfMonth nor scheduledDate is available, don't show a schedule (data issue)
                                      console.warn('[CUSTOMER-DETAILS] ⚠️ Monthly service missing both recurringDayOfMonth and scheduledDate:', {
                                        service: 'snow_removal',
                                        note: 'Schedule display requires recurringDayOfMonth from Bid or scheduledDate - cannot display schedule'
                                      });
                                    }
                                  } else if (actualServiceFrequency === 'weekly' || actualServiceFrequency === 'bi-weekly') {
                                    // For weekly/bi-weekly, use nextServiceDate or lastServiceDate as reference
                                    // CRITICAL: Only use customer-level fields if service has been scheduled (has scheduledDate)
                                    // This prevents showing schedule for unscheduled services
                                    if (snowRemovalServiceRequest?.scheduledDate) {
                                      referenceDate = customerDetailsDialog.customer.nextServiceDate 
                                    || customerDetailsDialog.customer.lastServiceDate
                                        || snowRemovalServiceRequest?.scheduledDate;
                                    }
                                  }
                                  // For monthly services, referenceDate is only set if dayOfMonthToUse is defined (above)
                                  // If dayOfMonthToUse is undefined, referenceDate remains undefined and we show "Not scheduled"
                                  
                                  if (actualServiceFrequency && actualServiceFrequency !== 'seasonal-contract') {
                                    // Only show schedule if we have the required data
                                    // For monthly: need referenceDate (set from dayOfMonthToUse)
                                    // For weekly/bi-weekly: need dayOfWeek
                                    if (actualServiceFrequency === 'monthly' && referenceDate) {
                                      return (
                                        <Typography variant="body2" fontWeight="medium">
                                          {getScheduleDisplay(actualServiceFrequency, dayOfWeek, referenceDate)}
                                        </Typography>
                                      );
                                    } else if ((actualServiceFrequency === 'weekly' || actualServiceFrequency === 'bi-weekly') && dayOfWeek !== undefined) {
                                    return (
                                      <Typography variant="body2" fontWeight="medium">
                                        {getScheduleDisplay(actualServiceFrequency, dayOfWeek, referenceDate)}
                                      </Typography>
                                    );
                                    }
                                    // If required data is missing, show "Not scheduled"
                                    return <Typography variant="body2" color="text.secondary">Not scheduled</Typography>;
                                  }
                                  return <Typography variant="body2" color="text.secondary">Not scheduled</Typography>;
                                })()}
                              </Grid>
                              {(() => {
                                // Show contract dates only for seasonal contracts (Snow Removal)
                                const serviceFrequency = customerDetailsDialog.customer.servicePricing?.snowRemoval?.frequency;
                                const serviceRequest = customerServiceRequests.find((sr: any) => sr.serviceType?.category === 'snow_removal');
                                let isSeasonal = serviceFrequency === 'seasonal-contract' || customerDetailsDialog.customer.paymentStructure?.type === 'seasonal-contract';
                                
                                // Also check service request's accepted bid
                                if (!isSeasonal && serviceRequest?.acceptedBid?.servicePricing) {
                                  const matchingServicePricing = serviceRequest.acceptedBid.servicePricing.find((sp: any) => 
                                    sp.service === 'snowRemoval' || sp.serviceName?.toLowerCase().includes('snow')
                                  );
                                  if (matchingServicePricing?.isSeasonalContract || matchingServicePricing?.frequency === 'seasonal-contract') {
                                    isSeasonal = true;
                                  }
                                }
                                
                                if (!isSeasonal) return null;
                                
                                // Extract contract dates from the same sources we use for other seasonal contract data
                                // SINGLE SOURCE OF TRUTH: For bid-accepted customers, prioritize acceptedBid.servicePricing
                                // This ensures consistency with customer's My Services view and avoids stale data
                                let contractStartDate: string | undefined = undefined;
                                let contractEndDate: string | undefined = undefined;
                                
                                // PRIORITY 1: Check accepted bid first (for bid-accepted customers - this is the source of truth)
                                if (serviceRequest?.acceptedBid?.servicePricing) {
                                  const matchingServicePricing = serviceRequest.acceptedBid.servicePricing.find((sp: any) => 
                                    sp.service === 'snowRemoval' || sp.serviceName?.toLowerCase().includes('snow')
                                  );
                                  if (matchingServicePricing?.seasonalContractDetails) {
                                    contractStartDate = matchingServicePricing.seasonalContractDetails.contractStartDate;
                                    contractEndDate = matchingServicePricing.seasonalContractDetails.contractEndDate;
                                  }
                                }
                                
                                // PRIORITY 2: Fallback to customer.servicePricing (for onboarded customers without acceptedBid)
                                if ((!contractStartDate || !contractEndDate)) {
                                  const snowRemovalPricing = customerDetailsDialog.customer.servicePricing?.snowRemoval as any;
                                  if (snowRemovalPricing?.seasonalContractDetails) {
                                    if (!contractStartDate) contractStartDate = snowRemovalPricing.seasonalContractDetails.contractStartDate;
                                    if (!contractEndDate) contractEndDate = snowRemovalPricing.seasonalContractDetails.contractEndDate;
                                  }
                                }
                                
                                if (!contractStartDate && !contractEndDate) return null;
                                
                                const formatDate = (dateStr: string | undefined) => {
                                  if (!dateStr) return 'N/A';
                                  try {
                                    const date = new Date(dateStr);
                                    // CRITICAL: Extract UTC date components directly (dates are stored as UTC midnight representing calendar dates)
                                    // This prevents timezone conversion issues where end dates appear 1 day early
                                    const year = date.getUTCFullYear();
                                    const month = date.getUTCMonth();
                                    const day = date.getUTCDate();
                                    // Format using UTC components to avoid timezone shift
                                    const formattedDate = new Date(Date.UTC(year, month, day, 12, 0, 0, 0));
                                    return formattedDate.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric', timeZone: 'UTC' });
                                  } catch {
                                    return dateStr;
                                  }
                                };
                                
                                // Extract payment schedule, total contract price, and monthly payment amount
                                // SINGLE SOURCE OF TRUTH: Use stored monthlyPaymentAmount from subscription creation, not recalculate
                                let paymentSchedule: 'single-payment' | 'monthly' | undefined = undefined;
                                let totalContractPrice: number | undefined = undefined;
                                let monthlyPaymentAmount: number | undefined = undefined;
                                
                                // PRIORITY 1: Check accepted bid first (for bid-accepted customers - this is the source of truth for contract details)
                                if (serviceRequest?.acceptedBid?.servicePricing) {
                                  const matchingServicePricing = serviceRequest.acceptedBid.servicePricing.find((sp: any) => 
                                    sp.service === 'snowRemoval' || sp.serviceName?.toLowerCase().includes('snow')
                                  );
                                  if (matchingServicePricing?.seasonalContractDetails) {
                                    paymentSchedule = matchingServicePricing.seasonalContractDetails.paymentSchedule;
                                    totalContractPrice = matchingServicePricing.seasonalContractDetails.totalContractPrice;
                                    // Note: Bids don't store monthlyPaymentAmount - it's stored in ExistingCustomer when subscription is created
                                  }
                                }
                                
                                // PRIORITY 2: Fallback to customer.servicePricing (for onboarded customers without acceptedBid)
                                // This is also where monthlyPaymentAmount is stored (single source of truth from subscription creation)
                                if (!paymentSchedule || !totalContractPrice || !monthlyPaymentAmount) {
                                  const snowRemovalPricing = customerDetailsDialog.customer.servicePricing?.snowRemoval as any;
                                  if (snowRemovalPricing?.seasonalContractDetails) {
                                    if (!paymentSchedule) paymentSchedule = snowRemovalPricing.seasonalContractDetails.paymentSchedule;
                                    if (!totalContractPrice) totalContractPrice = snowRemovalPricing.seasonalContractDetails.totalContractPrice;
                                    // Use stored monthlyPaymentAmount (single source of truth from subscription creation)
                                    if (!monthlyPaymentAmount && paymentSchedule === 'monthly') {
                                      monthlyPaymentAmount = snowRemovalPricing.seasonalContractDetails.monthlyPaymentAmount;
                                    }
                                  }
                                }
                                
                                // FALLBACK: Only recalculate if stored value is missing (shouldn't happen after subscription is created)
                                if (paymentSchedule === 'monthly' && !monthlyPaymentAmount && totalContractPrice && contractStartDate && contractEndDate) {
                                  try {
                                    const start = new Date(contractStartDate);
                                    const end = new Date(contractEndDate);
                                    const startsOnFirst = start.getUTCDate() === 1;
                                    
                                    let billingMonths: number;
                                    if (startsOnFirst) {
                                      // Contract starts on 1st: Include that month in count - use UTC components
                                      billingMonths = (end.getUTCFullYear() - start.getUTCFullYear()) * 12 + 
                                                      (end.getUTCMonth() - start.getUTCMonth()) + 1; // +1 to include both start and end months
                                    } else {
                                      // Contract starts mid-month: Exclude partial first month - use UTC components
                                      const nextMonthYear = start.getUTCMonth() === 11 ? start.getUTCFullYear() + 1 : start.getUTCFullYear();
                                      const nextMonthMonth = (start.getUTCMonth() + 1) % 12;
                                      billingMonths = (end.getUTCFullYear() - nextMonthYear) * 12 + 
                                                      (end.getUTCMonth() - nextMonthMonth) + 1; // +1 to include end month
                                    }
                                    
                                    if (billingMonths > 0) {
                                      monthlyPaymentAmount = Math.round((totalContractPrice / billingMonths) * 100) / 100;
                                      console.warn('[CUSTOMER-DETAILS] ⚠️ Recalculated monthlyPaymentAmount (stored value missing):', {
                                        service: 'snow_removal',
                                        recalculated: monthlyPaymentAmount,
                                        note: 'Should use stored value from subscription creation - this is a fallback'
                                      });
                                    }
                                  } catch (e) {
                                    console.error('[CUSTOMER-DETAILS] Error calculating monthly payment:', e);
                                  }
                                }
                                
                                return (
                                  <>
                                    <Grid item xs={12} sm={6}>
                                      <Typography variant="caption" color="text.secondary">Contract Period</Typography>
                                      <Typography variant="body2" fontWeight="medium">
                                        {formatDate(contractStartDate)} - {formatDate(contractEndDate)}
                                      </Typography>
                                    </Grid>
                                    {paymentSchedule && (
                                      <Grid item xs={12} sm={6}>
                                        <Typography variant="caption" color="text.secondary">Payment Schedule</Typography>
                                        <Typography variant="body2" fontWeight="medium">
                                          {paymentSchedule === 'monthly' ? 'Monthly Payments' : 'One-Time Payment'}
                                        </Typography>
                                      </Grid>
                                    )}
                                    {paymentSchedule === 'monthly' && monthlyPaymentAmount && (
                                      <Grid item xs={12} sm={6}>
                                        <Typography variant="caption" color="text.secondary">Monthly Payment</Typography>
                                        <Typography variant="body2" fontWeight="medium" color="primary">
                                          ${monthlyPaymentAmount.toFixed(2)}
                                        </Typography>
                                      </Grid>
                                    )}
                                  </>
                                );
                              })()}
                              <Grid item xs={12} sm={6}>
                                <Typography variant="caption" color="text.secondary">Last Service</Typography>
                                {(() => {
                                  if (!customerDetailsDialog.customer) return <Typography variant="body2" color="text.secondary">N/A</Typography>;
                                  
                                  // CRITICAL: Use customerServiceRequests which is filtered by THIS property's address
                                  // Do NOT use allCustomersServiceRequests[customerId] - it includes all properties
                                  const getServiceRequest = (category: string) => {
                                    return customerServiceRequests.find((sr: any) => sr.serviceType?.category === category);
                                  };
                                  
                                  const getLastServiceDate = (category: string) => {
                                    // First, look for ALL completed service requests for this specific service category
                                    // CRITICAL: Include both 'completed' status AND 'scheduled' status with completedDate
                                    // After auto-rescheduling, status becomes 'scheduled' but completedDate is preserved
                                    // SINGLE SOURCE OF TRUTH: Same logic as All Customers chart and other services
                                    const completedRequests = customerServiceRequests.filter((sr: any) => {
                                      const srCategory = sr.serviceType?.category;
                                      const matches = srCategory === category;
                                      const hasCompletedDate = !!sr.completedDate;
                                      return matches && hasCompletedDate && (sr.status === 'completed' || sr.status === 'scheduled');
                                    });
                                    
                                    // If we have completed requests, get the most recent one (by completedDate)
                                    if (completedRequests.length > 0) {
                                      const mostRecent = completedRequests.reduce((latest: any, current: any) => {
                                        if (!latest.completedDate) return current;
                                        if (!current.completedDate) return latest;
                                        return new Date(current.completedDate) > new Date(latest.completedDate) ? current : latest;
                                      });
                                      
                                      if (mostRecent?.completedDate) {
                                        // Normalize completedDate to UTC midnight (date-only) to avoid timezone display issues
                                        const completedDate = new Date(mostRecent.completedDate);
                                        const year = completedDate.getUTCFullYear();
                                        const month = completedDate.getUTCMonth();
                                        const day = completedDate.getUTCDate();
                                        const normalizedDate = new Date(Date.UTC(year, month, day, 0, 0, 0, 0));
                                        return normalizedDate;
                                      }
                                    }
                                    
                                    // Fallback: check any service request (not just completed) for this category
                                    const serviceRequest = getServiceRequest(category);
                                    if (serviceRequest?.completedDate) {
                                      const completedDate = new Date(serviceRequest.completedDate);
                                      const normalizedDate = new Date(Date.UTC(
                                        completedDate.getUTCFullYear(),
                                        completedDate.getUTCMonth(),
                                        completedDate.getUTCDate(),
                                        0, 0, 0, 0
                                      ));
                                      return normalizedDate;
                                    }
                                    
                                    // IMPORTANT: Don't use customer.lastServiceDate as fallback
                                    // Return null if no completed service request found for this specific category
                                    return null;
                                  };
                                  
                                  // Get service frequency using the same helper as All Customers chart
                                  const serviceRequest = getServiceRequest('snow_removal');
                                  const serviceFrequency = getServiceFrequency(customerDetailsDialog.customer, 'snow_removal', serviceRequest);
                                  
                                  // Use the SAME getLastServiceDate function as All Customers chart
                                  const lastServiceDate = getLastServiceDate('snow_removal');
                                  
                                  if (lastServiceDate) {
                                    return (
                                      <Typography variant="body2" fontWeight="medium">
                                        {formatDateMMDDYYYY(lastServiceDate.toString())}
                                      </Typography>
                                    );
                                  }
                                  
                                  return <Typography variant="body2" color="text.secondary">N/A</Typography>;
                                })()}
                              </Grid>
                              <Grid item xs={12} sm={6}>
                                <Typography variant="caption" color="text.secondary">Next Service</Typography>
                                {(() => {
                                  if (!customerDetailsDialog.customer) return <Typography variant="body2" color="text.secondary">N/A</Typography>;
                                  
                                  // CRITICAL: Use customerServiceRequests which is filtered by THIS property's address
                                  // Do NOT use allCustomersServiceRequests[customerId] - it includes all properties
                                  const getServiceRequest = (category: string) => {
                                    return customerServiceRequests.find((sr: any) => sr.serviceType?.category === category);
                                  };
                                  
                                  // Use shared utility function for calculating next service dates (SINGLE SOURCE OF TRUTH)
                                  const getNextServiceDate = (category: string) => {
                                    if (!customerDetailsDialog.customer) return null;
                                    const serviceRequest = getServiceRequest(category);
                                    return getNextServiceDateUtil(
                                      customerDetailsDialog.customer,
                                      category as 'mowing' | 'fertilizing' | 'snow_removal',
                                      serviceRequest,
                                      customerServiceRequests
                                    );
                                  };
                                  
                                  const nextDate = getNextServiceDate('snow_removal');
                                  
                                  if (nextDate) {
                                    return (
                                      <Typography variant="body2" fontWeight="medium">
                                        {formatDateMMDDYYYY(nextDate.toString())}
                                      </Typography>
                                    );
                                  }
                                  
                                  // For per-instance services, show "Per Instance" if no date
                                  const serviceRequestForDisplay = customerServiceRequests.find((sr: any) => 
                                    sr.serviceType?.category === 'snow_removal'
                                  );
                                  const displayFrequency = getServiceFrequency(customerDetailsDialog.customer, 'snow_removal', serviceRequestForDisplay);
                                  if (displayFrequency === 'per-instance') {
                                    return <Typography variant="body2" color="text.secondary">Per Instance</Typography>;
                                  }
                                  
                                  return <Typography variant="body2" color="text.secondary">N/A</Typography>;
                                })()}
                              </Grid>
                              {(() => {
                                // Show "Mark Season Ended" button for recurring services (not seasonal contracts, not per-instance)
                                const serviceRequest = customerServiceRequests.find((sr: any) => sr.serviceType?.category === 'snow_removal');
                                const serviceFrequency = getServiceFrequency(customerDetailsDialog.customer, 'snow_removal', serviceRequest);
                                
                                // Determine if this is a seasonal contract
                                let isSeasonalContract = serviceFrequency === 'seasonal-contract';
                                if (!isSeasonalContract && serviceRequest?.acceptedBid?.servicePricing) {
                                  const matchingServicePricing = serviceRequest.acceptedBid.servicePricing.find((sp: any) => 
                                    sp.service === 'snowRemoval' || sp.serviceName?.toLowerCase().includes('snow')
                                  );
                                  if (matchingServicePricing?.isSeasonalContract || matchingServicePricing?.frequency === 'seasonal-contract') {
                                    isSeasonalContract = true;
                                  }
                                }
                                
                                // Show for recurring, per-instance, or seasonal-contract + per-instance. Hide for seasonal + weekly/bi-weekly/monthly.
                                const isRecurring = serviceFrequency === 'weekly' || serviceFrequency === 'bi-weekly' || serviceFrequency === 'monthly';
                                const isPerInstance = serviceFrequency === 'per-instance';
                                const allowSeasonalPerInstance = isSeasonalContract && isPerInstance;
                                // Per-instance: allow 'accepted' (Schedule Later as Needed, no date yet) so provider can mark season ended e.g. no snow all winter
                                const allowedStatuses = isPerInstance
                                  ? ['completed', 'scheduled', 'season_ended', 'accepted']
                                  : ['completed', 'scheduled', 'season_ended'];
                                const canMarkSeasonEnded = (isRecurring || isPerInstance) && (!isSeasonalContract || allowSeasonalPerInstance) && serviceRequest && 
                                                          allowedStatuses.includes(serviceRequest.status);
                                
                                if (!canMarkSeasonEnded) return null;
                                
                                return (
                                  <Grid item xs={12}>
                                    <Box display="flex" justifyContent="flex-end" mt={1}>
                                      <Button
                                        variant="outlined"
                                        size="small"
                                        onClick={() => {
                                          if (serviceRequest?._id || serviceRequest?.id) {
                                            setSeasonEndConfirmDialog({ 
                                              open: true, 
                                              serviceRequestId: serviceRequest._id || serviceRequest.id, 
                                              serviceCategory: 'snow_removal' 
                                            });
                                          }
                                        }}
                                        disabled={markingSeasonEnded === (serviceRequest?._id || serviceRequest?.id)}
                                        sx={{
                                          borderColor: serviceRequest?.status === 'season_ended' ? 'primary.main' : 'error.main',
                                          color: serviceRequest?.status === 'season_ended' ? 'primary.main' : 'error.main',
                                          fontWeight: 'bold',
                                          '&:hover': {
                                            borderColor: serviceRequest?.status === 'season_ended' ? 'primary.dark' : 'error.dark',
                                            backgroundColor: serviceRequest?.status === 'season_ended' ? 'primary.light' : 'error.light',
                                            color: serviceRequest?.status === 'season_ended' ? 'primary.dark' : 'error.dark'
                                          }
                                        }}
                                        startIcon={serviceRequest?.status === 'season_ended' ? undefined : <CheckCircle />}
                                      >
                                        {serviceRequest?.status === 'season_ended'
                                          ? 'Send notification & add to Renewals'
                                          : 'Mark Season Ended'}
                                      </Button>
                                    </Box>
                                  </Grid>
                                );
                              })()}
                            </Grid>
                          </CardContent>
                        </Card>
                      )}
                    </Box>
                  )}
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Payment Status
                  </Typography>
                  <Tooltip title={getPaymentStatus(customerDetailsDialog.customer).tooltip}>
                    <Chip 
                      label={getPaymentStatus(customerDetailsDialog.customer).label}
                      color={getPaymentStatus(customerDetailsDialog.customer).color}
                      size="small"
                      icon={getPaymentStatus(customerDetailsDialog.customer).icon}
                    />
                  </Tooltip>
                </Grid>
                {customerDetailsDialog.customer.serviceSchedule.notes && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Notes
                    </Typography>
                    <Typography variant="body2">
                      {customerDetailsDialog.customer.serviceSchedule.notes}
                    </Typography>
                  </Grid>
                )}
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCustomerDetailsDialog({ open: false, customer: null })}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Change Type Dialog - One-time vs Permanent */}
      <Dialog
        open={changeTypeDialog.open}
        onClose={() => setChangeTypeDialog({ open: false, customerId: null })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Change Type</DialogTitle>
        <DialogContent>
          <Typography variant="body1" gutterBottom sx={{ mb: 2 }}>
            How would you like to update the next service date?
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Button
              type="button"
              variant="outlined"
              fullWidth
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (changeTypeDialog.customerId) {
                  handleUpdateNextServiceDate(changeTypeDialog.customerId, 'one-time');
                }
              }}
              disabled={loading}
              sx={{ p: 2, textAlign: 'left', justifyContent: 'flex-start' }}
            >
              <Box>
                <Typography variant="subtitle1" fontWeight="bold">
                  One-time Change
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Only change this specific service date. The recurring schedule will remain unchanged.
                </Typography>
              </Box>
            </Button>
            <Button
              type="button"
              variant="outlined"
              fullWidth
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (changeTypeDialog.customerId) {
                  handleUpdateNextServiceDate(changeTypeDialog.customerId, 'permanent');
                }
              }}
              disabled={loading}
              sx={{ p: 2, textAlign: 'left', justifyContent: 'flex-start' }}
            >
              <Box>
                <Typography variant="subtitle1" fontWeight="bold">
                  Permanent Change
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Update the recurring schedule. All future service dates will be based on this new date.
                </Typography>
              </Box>
            </Button>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button 
            type="button"
            onClick={(e) => {
              e.preventDefault();
              setChangeTypeDialog({ open: false, customerId: null });
            }}
          >
            Cancel
          </Button>
        </DialogActions>
      </Dialog>

      {/* Mark Season Ended Confirmation Dialog */}
      <Dialog
        open={seasonEndConfirmDialog.open}
        onClose={() => setSeasonEndConfirmDialog({ open: false, serviceRequestId: null, serviceCategory: null })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Mark Season Ended</DialogTitle>
        <DialogContent>
          <Typography variant="body1" paragraph>
            Are you sure you want to mark this service as season ended? This will stop auto-rescheduling. The service will be moved to your Renewals page, where you can set a renewal price and extend a renewal offer to the customer before the next season.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setSeasonEndConfirmDialog({ open: false, serviceRequestId: null, serviceCategory: null })}
          >
            Cancel
          </Button>
          <Button 
            variant="contained"
            color="error"
            onClick={() => {
              if (seasonEndConfirmDialog.serviceRequestId && seasonEndConfirmDialog.serviceCategory) {
                handleMarkSeasonEnded(seasonEndConfirmDialog.serviceRequestId, seasonEndConfirmDialog.serviceCategory);
              }
            }}
            disabled={!seasonEndConfirmDialog.serviceRequestId || !seasonEndConfirmDialog.serviceCategory}
          >
            Mark Season Ended
          </Button>
        </DialogActions>
      </Dialog>

    </Box>
  );
};

export default CustomerManagement;
